/**
 * Message catalog — AR (default) + EN (§11.4, §14.2).
 *
 * Rules (binding):
 *  - ZERO hard-coded strings in screens — every user-visible string lives here.
 *  - `en` is the reference shape; `ar` must satisfy it, so a missing key in
 *    either language is a compile error, not a runtime fallback.
 *  - API error codes map 1:1 to the identity.yaml `x-error-codes` registry —
 *    the server localizes too, but the client renders these when offline/mocked.
 *  - §14.2 bidi: LTR runs embedded in Arabic copy (phone examples, machine
 *    keys, IDs) MUST be isolated. Where the value is rendered as its own
 *    element the app uses a `dir="ltr"` island; where it is baked INTO a
 *    sentence - as in the phone hints - the catalog string wraps it in the
 *    Unicode isolate pair U+2066 (LRI) / U+2069 (PDI), written as the
 *    escapes `\u2066`/`\u2069` so the source stays readable. Never paste a
 *    bare `+964...` into AR copy: bidi reorders it to `9647701234567+`
 *    (M6-14/D2). Platform-wide version of this rule (all four clients):
 *    docs/i18n-bidi-guidance.md (M7-08).
 */

export const en = {
  "app.name": "Admin Panel",
  "app.skipToContent": "Skip to main content",

  "nav.group.overview": "Overview",
  "nav.group.operations": "Operations",
  "nav.group.directory": "Directory",
  "nav.group.finance": "Finance",
  "nav.group.growth": "Growth",
  "nav.group.system": "System",

  "nav.dashboard": "Dashboard",
  "nav.orders": "Orders",
  "nav.dispatchMap": "Dispatch Map",
  "nav.unassignedOrders": "No Driver",
  "nav.assignments": "Assignments",
  "nav.dispatchRules": "Dispatch Rules",
  "nav.merchants": "Merchants",
  "nav.merchantApplications": "Applications",
  "nav.storeCategories": "Store Categories",
  "nav.drivers": "Drivers",
  "nav.vehicleTypes": "Vehicle Types",
  "nav.customers": "Customers",
  "nav.zones": "Zones",
  "nav.wallets": "Wallets",
  "nav.settlement": "Settlement",
  "nav.promotions": "Promotions",
  "nav.subscriptions": "Subscriptions",
  "nav.notifications": "Notifications",
  "nav.settings": "Settings",
  "nav.featureFlags": "Feature Flags",
  "nav.audit": "Audit Log",
  "nav.engagement": "Engagement",
  "nav.ads": "Ads",
  "nav.homeSections": "Home sections",
  "nav.collections": "Collections",
  "nav.categoryLayout": "Category layout",
  "nav.homeLayout": "Home layout",
  "nav.popups": "Pop-up ads",
  "nav.privacyPolicy": "Privacy policy",

  "nav.collapse": "Collapse navigation",
  "nav.expand": "Expand navigation",

  "breadcrumbs.label": "Breadcrumb",

  "topbar.language": "العربية",
  "topbar.languageSwitchTo": "Switch language to Arabic",
  "topbar.theme.light": "Light theme",
  "topbar.theme.dark": "Dark theme",
  "topbar.theme.system": "Follow system theme",
  "topbar.userMenu": "Account menu",
  "topbar.signedInAs": "Signed in as",
  "topbar.logout": "Sign out",
  "topbar.loggingOut": "Signing out…",

  "login.title": "Sign in",
  "login.phone.label": "Phone number",
  "login.phone.invalid": "Enter a valid international phone number (+ and 8–15 digits).",
  "login.password.label": "Password",
  "login.password.tooShort": "Password must be at least 8 characters.",
  "login.password.show": "Show password",
  "login.password.hide": "Hide password",
  "login.submit": "Sign in",
  "login.submitting": "Signing in…",
  "login.lockout": "Too many attempts — sign-in is locked. Try again in {seconds} seconds.",

  "session.restoring": "Restoring your session…",
  "session.expired": "Your session expired — please sign in again.",

  "screen.dashboard.title": "Dashboard",
  "screen.orders.title": "Orders",
  "screen.dispatchMap.title": "Dispatch Map",
  "screen.merchants.title": "Merchants",
  "screen.storeCategories.title": "Store Categories",
  "screen.drivers.title": "Drivers",
  "screen.vehicleTypes.title": "Vehicle Types",
  "screen.customers.title": "Customers",
  "screen.zones.title": "Zones",
  "screen.wallets.title": "Wallets",
  "screen.settlement.title": "Settlement",
  "screen.notifications.title": "Notifications",
  "screen.promotions.title": "Promotions",
  "screen.ads.title": "Ads",
  "screen.homeSections.title": "Home sections",
  "screen.collections.title": "Collections",
  "screen.categoryLayout.title": "Category layout",
  "screen.homeLayout.title": "Home layout",
  "screen.popups.title": "Pop-up ads",
  "screen.privacyPolicy.title": "Privacy policy",
  "screen.subscriptions.title": "Subscriptions",
  "screen.settings.title": "Settings",
  "screen.featureFlags.title": "Feature Flags",
  "screen.audit.title": "Audit Log",

  "notFound.title": "Page not found",
  "notFound.backHome": "Back to dashboard",

  "error.offline": "Cannot reach the server — check your connection and try again.",
  "common.retry": "Retry",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.edit": "Edit",
  "common.actions": "Actions",
  "common.saveChanges": "Save changes",
  "common.saveFailed": "Saving failed — please try again.",
  "common.backToList": "Back to list",
  "common.clearFilters": "Clear filters",
  "common.filterAll": "All",

  "form.required": "This field is required.",
  "form.wholeMoneyOnly": "Amounts are in whole dinars — no fractions.",
  "merchants.form.vehicleType": "Delivery vehicle type",
  "merchants.form.vehicleTypePlaceholder": "Select a vehicle type",
  "merchants.table.vehicleType": "Vehicle",
  "orders.table.vehicleType": "Vehicle",
  "orders.detail.vehicleType": "Required vehicle type",
  "merchants.detail.vehicleType": "Delivery vehicle type",
  "applications.review.vehicleType": "Delivery vehicle type",
  "form.invalidPhone": "Enter a valid international phone number (+ and 8–15 digits).",

  "pager.range": "{from}–{to} of {total}",
  "pager.perPage": "Rows per page",
  "pager.previous": "Previous page",
  "pager.next": "Next page",
  "pager.pageOf": "{page} / {pages}",

  "unsaved.title": "Discard unsaved changes?",
  "unsaved.description": "Unsaved edits will be lost.",
  "unsaved.leave": "Discard and leave",
  "unsaved.stay": "Keep editing",

  "week.sat": "Saturday",
  "week.sun": "Sunday",
  "week.mon": "Monday",
  "week.tue": "Tuesday",
  "week.wed": "Wednesday",
  "week.thu": "Thursday",
  "week.fri": "Friday",

  // ---- Zones (M2-14, §6.1, §6.4) ----
  "zones.title": "Zones",
  "zones.count": "{count} zones",
  "zones.count.one": "{count} zone",
  "zones.new": "New zone",
  "zones.search.label": "Search zones",
  "zones.search.placeholder": "Search by Arabic or English name…",
  "zones.filter.status": "Status",
  "zones.filter.all": "All",
  "zones.status.active": "Active",
  "zones.status.disabled": "Disabled",
  "zones.table.label": "Zones",
  "zones.table.name": "Zone",
  "zones.table.center": "Center point",
  "zones.table.pricing": "Delivery pricing",
  "zones.table.status": "Status",
  "zones.table.updated": "Updated",
  "zones.pricing.custom": "Custom pricing",
  "zones.pricing.global": "Global pricing",
  "zones.empty.title": "No zones yet",
  "zones.emptyFiltered.title": "No zones match",
  "zones.editor.createTitle": "New zone",
  "zones.editor.editTitle": "Edit zone — {name}",
  "zones.editor.detailsTitle": "Zone details",
  "zones.editor.createSubmit": "Create zone",
  "zones.form.nameAr": "Name (Arabic)",
  "zones.form.nameEn": "Name (English)",
  "zones.form.nameRequired": "Enter a name of at least 2 characters.",
  "zones.form.active": "Zone is active",
  "zones.form.pinRequired": "Drop the zone center pin on the map before saving.",
  "zones.pin.title": "Center point",
  "zones.pin.mapLabel": "Zone center map",
  "zones.pin.instructions": "Click the map to drop the center pin, drag it to adjust, or use the arrow keys for fine tuning. Hold Shift for larger steps.",
  "zones.pricing.title": "Delivery pricing overrides",
  "zones.pricing.baseCost": "Base cost",
  "zones.pricing.costPerKm": "Cost per km",
  "zones.pricing.minFee": "Minimum delivery fee",
  "zones.pricing.maxFee": "Maximum delivery fee",
  "zones.pricing.amount": "{label} amount",
  "zones.pricing.inherits": "Inherits the global setting.",
  "zones.pricing.inheritsValue": "Inherits the global setting — currently {value}.",
  "zones.pricing.invalidAmount": "Enter a valid amount (e.g. 2500 or 2500.50).",
  "zones.pricing.minAboveMax": "The minimum fee cannot exceed the maximum fee.",
  "zones.toast.created": "Zone created.",
  "zones.toast.updated": "Zone saved.",
  "zones.toast.deleted": "Zone deleted.",
  "zones.toast.createdPricingFailed": "Zone created, but saving the pricing override failed — try again below.",
  "zones.disable.title": "Disable this zone?",
  "zones.disable.description": "The zone disappears from order forms and store assignment immediately.",
  "zones.disable.confirm": "Disable zone",
  "zones.delete.title": "Danger zone",
  "zones.delete.action": "Delete zone",
  "zones.delete.confirmTitle": "Delete this zone?",
  "zones.delete.confirmDescription": "This cannot be undone.",
  "zones.export.nameAr": "Name (Arabic)",
  "zones.export.nameEn": "Name (English)",
  "zones.export.lat": "Center latitude",
  "zones.export.lng": "Center longitude",

  // ---- Dispatch live map (M6-05, §14.4, §6.5) ----
  "dispatch.title": "Dispatch Map",
  "dispatch.counts": "{drivers} drivers online ({onOrder} on an order) · {orders} active orders",
  "dispatch.updatedAt": "Updated {time}",
  "dispatch.layers.label": "Map layers",
  "dispatch.layers.drivers": "Drivers",
  "dispatch.truncated": "Showing the first 100 orders per status — refine in the Orders list.",
  "dispatch.unplotted": "{count} orders are not on the map yet (pickup location still loading).",
  "dispatch.unplotted.one": "{count} order is not on the map yet (pickup location still loading).",
  "dispatch.map.label": "Live dispatch map",
  "dispatch.empty.title": "All quiet",
  "dispatch.denied.title": "No access to the live map",
  "dispatch.marker.order": "Order {orderNo} — {status}",
  "dispatch.marker.driver": "{name} — {status}",
  "dispatch.panel.orderLabel": "Order {orderNo} details",
  "dispatch.panel.driverLabel": "Driver {name} details",
  "dispatch.panel.route": "Route",
  "dispatch.panel.routeValue": "{from} → {to}",
  "dispatch.panel.noDropoff": "No drop-off yet",
  "dispatch.panel.driver": "Driver",
  "dispatch.panel.noDriver": "Not assigned yet",
  "dispatch.panel.created": "Created",
  "dispatch.panel.viewOrder": "Open order",
  "dispatch.panel.viewDriver": "Open driver",
  "dispatch.panel.currentOrder": "Current order",
  "dispatch.panel.noCurrentOrder": "No active order",
  "dispatch.panel.lastSeen": "Position is stale — last seen {time}.",
  "dispatch.assign.action": "Assign driver",
  "dispatch.reassign.action": "Reassign driver",
  "dispatch.assign.title": "Assign a driver to {orderNo}",
  "dispatch.reassign.title": "Reassign {orderNo} to another driver",
  "dispatch.assign.driver": "Driver",
  "dispatch.assign.driverPlaceholder": "Choose a driver…",
  "dispatch.assign.driverRequired": "Choose a driver first.",
  "dispatch.assign.driverBusyOption": "{name} — on an order",
  "dispatch.assign.driverOfflineOption": "{name} — offline",
  "dispatch.reassign.reason": "Reason",
  "dispatch.assign.reasonRequired": "A reason of at least 3 characters is required.",
  "dispatch.assign.force": "Assign anyway (override the busy-driver guard)",
  "dispatch.assign.submit": "Assign driver",
  "dispatch.reassign.submit": "Reassign driver",
  "dispatch.assign.done": "Driver assigned — {orderNo} is now confirmed.",
  "dispatch.reassign.done": "{orderNo} reassigned.",
  "dispatch.assign.confirmTitle": "Assign this driver?",
  "dispatch.reassign.confirmTitle": "Reassign this order?",
  "dispatch.assign.driverVehicleOption": "{name} · {vehicle}",
  "dispatch.assign.mismatchNotice": "This order needs a {required}; the driver you picked drives a {driverVehicle}.",
  "dispatch.assign.mismatchConfirmTitle": "Assign a driver with the wrong vehicle?",

  // ---- Merchants (M2-15, §6.2) ----
  "merchants.title": "Merchants",
  "merchants.count": "{count} merchants",
  "merchants.count.one": "{count} merchant",
  "merchants.new": "New merchant",
  "merchants.search.label": "Search merchants",
  "merchants.search.placeholder": "Search by name, number or phone…",
  "merchants.filter.status": "Status",
  "merchants.filter.zone": "Zone",
  "merchants.table.label": "Merchants",
  "merchants.table.name": "Merchant",
  "merchants.table.category": "Category",
  "merchants.table.phone": "Phone",
  "merchants.table.branches": "Branches",
  "merchants.table.status": "Status",
  "merchants.table.readiness": "Orders",
  "merchants.status.DRAFT": "Draft",
  "merchants.status.ACTIVE": "Active",
  "merchants.status.SUSPENDED": "Suspended",
  "merchants.status.CLOSED": "Closed",
  "merchants.readiness.ready": "Ready",
  "merchants.readiness.notReady": "Not ready",
  "merchants.readiness.readyLong": "This merchant can create orders — active status and a pinned, active branch are in place.",
  "merchants.readiness.notReadyLong": "This merchant cannot create orders yet:",
  "merchants.readiness.gap.notActive": "The merchant account is not active.",
  "merchants.readiness.gap.noBranch": "No active branch with a pinned store location exists.",
  "merchants.empty.title": "No merchants yet",
  "merchants.emptyFiltered.title": "No merchants match",
  "merchants.create.title": "New merchant",
  "merchants.create.ownerTitle": "Owner account",
  "merchants.create.profileTitle": "Store profile",
  "merchants.create.branchTitle": "First branch",
  "merchants.create.branchToggle": "Create the first branch now",
  "merchants.create.ownerConflictTitle": "Owner already has a merchant",
  "merchants.create.phoneTakenTitle": "Phone already registered",
  "merchants.create.phoneTakenAction": "Link the existing account",
  "merchants.create.submit": "Create merchant",
  // M7-43a — inline owner-account provisioning (Wave 8).
  "merchants.owner.linkToggle": "Link an existing account",
  "merchants.owner.name": "Owner name",
  "merchants.owner.phone": "Owner phone",
  "merchants.owner.password": "Password",
  "merchants.owner.generate": "Generate",
  "merchants.owner.copy": "Copy",
  "merchants.owner.passwordCopied": "Password copied",
  "merchants.owner.passwordCopyFailed": "Could not copy the password",
  "merchants.owner.nameRequired": "The owner's name is required.",
  "merchants.owner.phoneInvalid": "Enter a valid international phone number (+ and 8–15 digits).",
  "merchants.owner.passwordTooShort": "Password must be at least 8 characters.",
  "merchants.owner.linkRequired": "Search and select the owner's account.",
  "merchants.owner.searchLabel": "Search the user directory",
  "merchants.owner.passwordTooLong": "Password must be at most 128 characters.",
  "merchants.owner.title": "Owner account",
  "merchants.owner.resetPassword": "Reset password",
  "merchants.owner.resetTitle": "Reset the owner's password",
  "merchants.owner.resetDescription": "The owner is signed out of every device.",
  "merchants.owner.newPassword": "New password",
  "merchants.owner.regenerate": "Regenerate",
  "merchants.owner.resetConfirm": "Set",
  "merchants.owner.resetDone": "Password set",
  "merchants.form.owner": "Owner account",
  "merchants.form.ownerInvalid": "Enter a valid 26-character user ID.",
  "merchants.form.nameAr": "Name (Arabic)",
  "merchants.form.nameEn": "Name (English)",
  "merchants.form.category": "Category",
  "merchants.form.categoryPlaceholder": "Choose a category…",
  "merchants.form.categoryInactive": "(inactive)",
  "merchants.form.contactPhone": "Contact phone",
  "merchants.form.whatsappPhone": "WhatsApp phone (optional)",
  "merchants.form.descriptionAr": "Description (Arabic)",
  "merchants.form.descriptionEn": "Description (English)",
  "merchants.form.status": "Status",
  "merchants.form.suspendedReason": "Suspension reason",
  "merchants.form.reasonRequired": "A suspension reason is required.",
  "merchants.social.title": "Social links",
  "merchants.social.instagram": "Instagram",
  "merchants.social.facebook": "Facebook",
  "merchants.social.show": "Show in app",
  "merchants.social.invalid.instagram": "Enter an https link on instagram.com.",
  "merchants.social.invalid.facebook": "Enter an https link on facebook.com or fb.com.",
  "merchants.social.urlRequired": "Enter the link to show it in the app.",
  "merchants.prep.title": "Preparation time",
  "merchants.prep.from": "From",
  "merchants.prep.to": "To",
  "merchants.prep.unit": "min",
  "merchants.prep.range": "Enter a whole number from {min} to {max}.",
  "merchants.prep.lte": "Must not exceed «To».",
  "merchants.prep.value": "\u2066{min}–{max}\u2069 min",
  "merchants.statusConfirm.suspendTitle": "Suspend this merchant?",
  "merchants.statusConfirm.suspendDescription": "The merchant loses panel access and cannot create orders until reactivated.",
  "merchants.statusConfirm.closeTitle": "Close this merchant?",
  "merchants.statusConfirm.closeDescription": "Closing is permanent.",
  "merchants.statusConfirm.confirm": "Confirm change",
  "merchants.toast.created": "Merchant created.",
  "merchants.toast.updated": "Merchant saved.",
  "merchants.action.openWallet": "Wallet",
  "merchants.tabs.label": "Merchant sections",
  "merchants.tabs.profile": "Profile",
  "merchants.tabs.branches": "Branches",
  "merchants.tabs.staff": "Staff",
  "merchants.tabs.media": "Media",

  // ---- Store location (Wave 16, `multi_branch` off — one location per store) ----
  "storeLocation.title": "Store location",
  "storeLocation.empty.title": "No store location yet",
  "storeLocation.pinNow": "Pin the store location",
  "storeLocation.save": "Save location",
  "storeLocation.toast.pinned": "Store location pinned.",
  "storeLocation.toast.saved": "Store location saved.",
  "storeLocation.form.pinRequired": "The store location must be pinned on the map before saving.",
  "storeLocation.form.phone": "Store phone (optional)",
  "storeLocation.form.active": "Location is active",
  "merchants.create.locationToggle": "Pin the store location now",
  "merchants.readiness.readyLongSingle": "This merchant can create orders — active status and a pinned store location are in place.",
  "merchants.readiness.gap.noLocation": "No store location is pinned on the map.",

  // ---- Store media (Wave 16, M7-119) ----
  "media.logo.title": "Logo",
  "media.cover.title": "Cover image",
  "media.logo.preview": "Logo preview",
  "media.cover.preview": "Cover preview",
  "media.empty": "No image",
  "media.choose": "Choose an image",
  "media.replace": "Replace the image",
  "media.upload": "Upload",
  "media.remove": "Remove",
  "media.remove.logoTitle": "Remove the logo?",
  "media.remove.coverTitle": "Remove the cover image?",
  "media.remove.description": "The image disappears from the customer app immediately.",
  "media.toast.saved": "Image saved.",
  "media.toast.removed": "Image removed.",
  "media.error.type": "Only JPEG, PNG or WebP images are accepted.",
  "media.error.size": "The image must be {mb} MB or smaller.",
  "media.error.tooSmall": "The image must be at least {px} pixels on its shorter side.",
  "media.error.tooNarrow": "The image must be at least {px} pixels wide.",
  "media.ratio.logo": "This image is {ratio} — it will be cropped to a square.",
  "media.ratio.cover": "This image is {ratio} — it will be cropped to fit the 2:1 frame.",
  "media.menu.title": "Menu images",
  "media.menu.counter": "{count} image(s)",
  "media.menu.add": "Add images",
  "media.menu.uploading": "Uploading {done} of {total}…",
  "media.menu.uploaded": "{count} image(s) added.",
  "media.menu.empty.title": "No menu images yet",
  "media.menu.image": "Menu image {index}",
  "media.menu.moveEarlier": "Move image {index} earlier",
  "media.menu.moveLater": "Move image {index} later",
  "media.menu.delete": "Delete image {index}",
  "media.menu.deleteConfirm.title": "Delete this menu image?",
  "media.menu.deleteConfirm.description": "This cannot be undone.",
  "media.menu.deleteConfirm.confirm": "Delete image",
  "media.menu.deleted": "Menu image deleted.",
  "media.menu.reorderFailed": "Reordering failed — the list was refreshed from the server.",

  // ---- Branches (§6.2 store pinning) ----
  "branches.new": "Add branch",
  "branches.active": "Active",
  "branches.inactive": "Inactive",
  "branches.inZone": "Zone: {zone}",
  "branches.zoneUnknown": "Unknown zone",
  "branches.empty.title": "No branches yet",
  "branches.modal.createTitle": "Add branch",
  "branches.modal.editTitle": "Edit branch",
  "branches.modal.createSubmit": "Add branch",
  "branches.form.nameAr": "Branch name (Arabic)",
  "branches.form.nameEn": "Branch name (English)",
  "branches.form.zone": "Zone",
  "branches.form.zonePlaceholder": "Choose a zone…",
  // M7-43d — zone directory states said out loud (owner bug report).
  "branches.form.zonesErrorTitle": "Zones could not be loaded",
  "branches.form.zonesErrorBody": "The active-zone directory did not load, so a zone cannot be assigned right now.",
  "branches.form.zonesEmptyTitle": "No active zones",
  "branches.form.zonesEmptyAction": "Open Zones",
  "branches.form.pin": "Store location",
  "branches.form.pinMapLabel": "Store location map",
  "branches.form.pinInstructions": "Click the map to pin the exact store location, drag to adjust, or use the arrow keys. Hold Shift for larger steps.",
  "branches.form.pinRequired": "The store location must be pinned on the map — the branch cannot be saved without it.",
  "branches.form.phone": "Branch phone (optional)",
  "branches.form.landmark": "Landmark (optional)",
  "branches.form.address": "Address (optional)",
  "branches.form.hours": "Working hours",
  "branches.form.hoursInvalid": "Set opening and closing times for every open day.",
  "branches.form.opensAt": "{day} opening time",
  "branches.form.closesAt": "{day} closing time",
  "branches.form.closed": "Closed",
  "branches.form.active": "Branch is active",
  "branches.deactivate": "Deactivate",
  "branches.deactivateConfirm.title": "Deactivate this branch?",
  "branches.deactivateConfirm.description": "If this is the last active branch, the merchant can no longer create orders.",
  "branches.toast.created": "Branch added.",
  "branches.toast.updated": "Branch saved.",
  "branches.toast.deactivated": "Branch deactivated.",
  "branches.map.label": "Branch locations map",
  "branches.map.moved": "Location of {branch} updated.",
  "branches.map.confirmTitle": "Move {branch}?",
  "branches.map.confirmAction": "Move store pin",
  "branches.map.saving": "Saving the new location…",

  // ---- Merchant applications (§14.5 join flow — M7-43b) ----
  "applications.title": "Merchant Applications",
  "applications.pendingCount": "{count} pending applications awaiting review",
  "applications.pendingCount.one": "{count} pending application awaiting review",
  "applications.filter.status": "Status",
  "applications.status.PENDING": "Pending",
  "applications.status.APPROVED": "Approved",
  "applications.status.REJECTED": "Rejected",
  "applications.table.label": "Merchant applications",
  "applications.table.store": "Store",
  "applications.table.applicant": "Applicant",
  "applications.table.category": "Category",
  "applications.table.zone": "Zone",
  "applications.table.submitted": "Submitted",
  "applications.table.status": "Status",
  "applications.empty.title": "No pending applications",
  "applications.emptyFiltered.title": "No applications match",
  "applications.review.title": "Application — {store}",
  "applications.review.applicant": "Applicant",
  "applications.review.store": "Store information",
  "applications.review.decision": "Decision",
  "applications.review.rejectionReason": "Rejection reason: {reason}",
  "applications.review.decidedAt": "Decided {date}",
  "applications.action.approve": "Approve",
  "applications.action.reject": "Reject…",
  "applications.approveConfirm.title": "Approve “{store}”?",
  "applications.reject.title": "Reject application",
  "applications.reject.reason": "Rejection reason",
  "applications.reject.reasonRequired": "A rejection reason is required.",
  "applications.reject.confirm": "Reject “{store}”",
  "applications.toast.approved": "“{store}” approved — the merchant account is active.",
  "applications.toast.rejected": "“{store}” rejected.",

  // ---- Staff (employment + Identity scoped roles) ----
  "staff.add": "Add staff",
  "staff.table.label": "Staff",
  "staff.table.user": "User",
  "staff.table.scope": "Scope",
  "staff.table.title": "Job title",
  "staff.table.status": "Status",
  "staff.table.since": "Added",
  "staff.scope.merchant": "Whole merchant",
  "staff.scope.branch": "Branch: {branch}",
  "staff.scope.branchUnknown": "Branch (removed)",
  "staff.status.active": "Active",
  "staff.status.revoked": "Revoked",
  "staff.empty.title": "No staff yet",
  "staff.modal.title": "Add staff member",
  "staff.modal.submit": "Add staff",
  "staff.form.phone": "User phone",
  "staff.form.scope": "Access scope",
  "staff.form.title": "Job title (optional)",
  "staff.revoke": "Revoke",
  "staff.revokeConfirm.title": "Revoke this staff member?",
  "staff.revokeConfirm.description": "Their merchant access is revoked immediately.",
  "staff.toast.added": "Staff member added.",
  "staff.toast.revoked": "Staff access revoked.",

  // ---- Dashboard (M6-03, §14.4 dashboard-first) ----
  "dashboard.refreshedAt": "Updated {time}",
  "dashboard.kpi.ordersToday": "Orders today",
  "dashboard.kpi.activeDrivers": "Active drivers",
  "dashboard.kpi.revenue": "Revenue today",
  "dashboard.kpi.cancellations": "Cancellations today",
  "dashboard.delta.up": "Up {percent} percent versus yesterday",
  "dashboard.delta.down": "Down {percent} percent versus yesterday",
  "dashboard.delta.flat": "Unchanged versus yesterday",
  "dashboard.chart.byHour.title": "Orders by hour",
  "dashboard.chart.byStatus.title": "Status distribution",
  "dashboard.chart.hourColumn": "Hour",
  "dashboard.chart.ordersColumn": "Orders",
  "dashboard.chart.statusColumn": "Status",
  "dashboard.chart.unknownStatus": "{status} (not in this build)",
  "dashboard.empty.title": "No orders yet today",

  // ---- Settings hub entry (M6-03) ----
  "settings.themeEntry.open": "Manage theme",

  // ---- Theme management (M6-03, §14.1.1 / ADR-006) ----
  "themeScreen.title": "Theme & branding",
  "themeScreen.version": "Version {version} — updated {time}",
  "themeScreen.brandSection": "Brand",
  "themeScreen.appNameAr": "App name (Arabic)",
  "themeScreen.appNameEn": "App name (English)",
  "themeScreen.logoLight": "Logo — light surfaces",
  "themeScreen.logoDark": "Logo — dark surfaces",
  "themeScreen.logoMark": "Mark (square icon)",
  "themeScreen.tabs.label": "Theme mode",
  "themeScreen.tab.light": "Light mode",
  "themeScreen.tab.dark": "Dark mode",
  "themeScreen.group.core": "Brand core",
  "themeScreen.group.promo": "Promo accent",
  "themeScreen.group.surfaces": "Surfaces & borders",
  "themeScreen.group.text": "Text & focus",
  "themeScreen.group.feedback": "Feedback colors",
  "themeScreen.colorInvalid": "Enter an uppercase hex color in the #RRGGBB form.",
  "themeScreen.nameRequired": "The app name is required in both languages.",
  "themeScreen.logoRequired": "A logo URL is required.",
  "themeScreen.preview.title": "Preview",
  "themeScreen.preview.description": "Rendered from your draft before saving — switch the mode tab to preview light/dark.",
  "themeScreen.preview.body": "Body text on the surface color, with secondary ink for captions.",
  "themeScreen.preview.primaryAction": "Primary action",
  "themeScreen.preview.secondaryAction": "Secondary",
  "themeScreen.preview.dangerAction": "Destructive",
  "themeScreen.preview.badgeSuccess": "Success",
  "themeScreen.preview.badgeWarning": "Warning",
  "themeScreen.preview.badgeDanger": "Danger",
  "themeScreen.preview.badgeInfo": "Info",
  "themeScreen.preview.badgePromo": "Promo",
  "themeScreen.save": "Save & apply…",
  "themeScreen.reset": "Discard changes",
  "themeScreen.confirm.title": "Apply this theme to the whole platform?",
  "themeScreen.confirm.apply": "Apply theme",
  "themeScreen.toast.saved": "Theme saved — clients re-theme on their next revalidation.",
  "themeScreen.conflict.title": "Someone else saved a newer theme",
  "themeScreen.conflict.description": "Your edits are based on an outdated version — reload the current theme and reapply them.",
  "themeScreen.conflict.reload": "Reload current theme",
  "themeScreen.denied.title": "No theme permission",

  // ---- Table platform (M6-04, §14.4) ----
  "table.columns": "Columns",
  "table.export.csv": "Export CSV",
  "table.export.excel": "Export Excel",
  "table.savedFilters.label": "Saved filters",
  "table.savedFilters.placeholder": "Saved filters…",
  "table.savedFilters.save": "Save filters",
  "table.savedFilters.modalTitle": "Save current filters",
  "table.savedFilters.nameLabel": "Filter set name",
  "table.savedFilters.nameRequired": "Enter a name for the filter set.",
  "table.savedFilters.saveConfirm": "Save",
  "table.savedFilters.delete": "Delete saved filter",
  "table.savedFilters.deleteTitle": "Delete this saved filter?",
  "table.savedFilters.deleteConfirm": "Delete",
  "table.bulk.label": "Bulk actions",
  "table.bulk.selected": "{count} selected",
  "table.bulk.clear": "Clear selection",
  "table.selectAll": "Select all rows on this page",
  "table.selectRow": "Select {name}",

  // ---- Orders (M6-04, §3, §14.4, §17.4) ----
  "orders.title": "Orders",
  "orders.count": "{count} orders",
  "orders.count.one": "{count} order",
  "orders.search.label": "Find by order number",
  "orders.search.placeholder": "Exact order number, e.g. ORD-000042…",
  "orders.filter.status": "Status",
  "orders.filter.merchant": "Merchant",
  "orders.filter.driver": "Driver",
  "orders.filter.zone": "Zone",
  "orders.filter.category": "Category",
  "orders.filter.from": "From date",
  "orders.filter.to": "To date",
  "orders.table.label": "Orders",
  "orders.table.orderNo": "Order",
  "orders.table.created": "Created",
  "orders.table.status": "Status",
  "orders.table.merchant": "Store / sender",
  "orders.table.driver": "Driver",
  "orders.table.zone": "Zone",
  "orders.table.category": "Category",
  "orders.table.source": "Source",
  "orders.table.type": "Type",
  "orders.table.total": "Total",
  "orders.export.currency": "Currency",
  "orders.empty.title": "No orders yet",
  "orders.emptyFiltered.title": "No orders match",
  "orders.source.MERCHANT_CREATED": "Merchant",
  "orders.source.CUSTOMER_CREATED": "Customer app",
  "orders.source.ADMIN_CREATED": "Admin",
  "orders.source.API_CREATED": "API",
  "orders.type.DISPATCH": "Dispatch",
  "orders.type.MARKETPLACE": "Marketplace",
  "orders.type.PICKUP": "Pickup",
  "orders.type.SCHEDULED": "Scheduled",
  "orders.bulkCancel.action": "Cancel {count} orders",
  "orders.bulkCancel.action.one": "Cancel {count} order",
  "orders.bulkCancel.title": "Cancel {count} orders?",
  "orders.bulkCancel.title.one": "Cancel {count} order?",
  "orders.bulkCancel.description": "This cannot be undone.",
  "orders.bulkCancel.confirm": "Cancel orders",
  "orders.bulkCancel.done": "{count} orders cancelled.",
  "orders.bulkCancel.done.one": "{count} order cancelled.",
  "orders.bulkCancel.partial": "{done} cancelled, {failed} failed — the list has been refreshed.",
  "orders.transition.reasonLabel": "Reason",
  "orders.transition.reasonRequired": "A reason is required for this action.",
  "orders.detail.title": "Order {orderNo}",
  "orders.tabs.label": "Order sections",
  "orders.tabs.details": "Details",
  "orders.tabs.history": "Change history",
  "orders.detail.parties": "Parties & context",
  "orders.detail.merchant": "Merchant",
  "orders.detail.driver": "Driver",
  "orders.detail.noDriver": "Not assigned yet",
  "orders.detail.customer": "Customer",
  "orders.detail.customerPhone": "Customer phone",
  "orders.detail.customerLink": "Account link",
  "orders.detail.registered": "Registered account",
  "orders.detail.guest": "Guest record",
  "orders.detail.modWindow": "Modification window ends",
  "orders.detail.completedAt": "Completed",
  "orders.detail.landmark": "Landmark",
  "orders.detail.dropoff": "Drop-off pin",
  "orders.detail.breakdown": "Price breakdown",
  "orders.detail.noQuote": "No delivery quote yet — landmark-only order, completable before pickup.",
  "orders.detail.orderValue": "Order value",
  "orders.detail.deliveryFee": "Delivery fee",
  "orders.detail.feeFormula": "Base {base} + {perKm}/km × {km} km",
  "orders.detail.capMin": "Minimum fee applied",
  "orders.detail.capMax": "Maximum fee applied",
  "orders.detail.exactPin": "Measured to the exact pin",
  "orders.detail.zoneCenter": "Measured to the zone center",
  "orders.detail.discount": "Discount",
  "orders.detail.total": "Final amount",
  "orders.funding.PLATFORM": "Platform-funded",
  "orders.funding.MERCHANT": "Merchant-funded",
  "orders.funding.SHARED": "Co-funded",
  "orders.funding.line": "{funder} — merchant {merchant} · platform {platform} · driver compensation {driver}",
  "orders.detail.money": "Payment & settlement",
  "orders.detail.paymentMethod": "Payment method",
  "orders.detail.cashOnDelivery": "Cash on delivery",
  "orders.detail.toCollect": "Driver collects",
  "orders.detail.settlementLink": "Settlement records",
  "orders.detail.walletsLink": "Wallets",
  "orders.timeline.created": "Created by {actor}",
  "orders.timeline.transition": "By {actor} — from {from}",
  "orders.timeline.reason": "Reason: {reason}",
  "orders.actor.MERCHANT": "merchant",
  "orders.actor.DRIVER": "driver",
  "orders.actor.ADMIN": "admin",
  "orders.actor.CUSTOMER": "customer",
  "orders.actor.SYSTEM": "system",
  "orders.actor.API": "API",
  "orders.geofence.PASSED": "Geofence passed",
  "orders.geofence.SKIPPED": "Geofence skipped",
  "orders.geofence.OVERRIDDEN": "Geofence overridden",
  "orders.geofence.distance": "{distance} m from target (allowed {allowed} m)",
  "orders.actions.cancel": "Cancel order…",
  "orders.actions.return": "Mark returned…",
  "orders.cancel.title": "Cancel order {orderNo}?",
  "orders.cancel.description": "This cannot be undone.",
  "orders.cancel.confirm": "Cancel order",
  "orders.return.title": "Mark order {orderNo} as returned?",
  "orders.return.description": "The delivery ends as a return and cannot be undone.",
  "orders.return.confirm": "Mark returned",
  "orders.toast.cancelled": "Order cancelled.",
  "orders.toast.returned": "Order marked as returned.",

  // ---- Customer delivery requests (Wave 21, §3.1/§14.4 — owner ruling 2026-09-15) ----
  "orders.customerRequest.badge": "Customer request",
  "orders.customerRequest.noStore": "No store",
  "orders.detail.pickup": "Pickup",
  "orders.detail.pickupAddress": "Pickup address",
  "orders.detail.pickupLandmark": "Pickup landmark",
  "orders.detail.pickupZone": "Pickup zone",
  "orders.detail.pickupPin": "Pickup pin",
  "orders.detail.pickupMap": "Pickup location on the map",
  "orders.detail.pickupNoPin": "No pin — measured from the zone centre",
  "orders.detail.recipientCard": "Recipient & parcel",
  "orders.detail.recipientName": "Recipient",
  "orders.detail.recipientPhone": "Recipient phone",
  "orders.detail.parcel": "Parcel",
  "orders.detail.payer": "Fee paid by",
  "orders.detail.sender": "Sender",
  "orders.detail.senderPhone": "Sender phone",
  "orders.detail.senderNote": "Note for the driver",
  "orders.payer.SENDER": "Sender",
  "orders.payer.RECIPIENT": "Recipient",

  // ---- Drivers (M6-04, §2.3, §8.2) ----
  "drivers.title": "Drivers",
  "drivers.count": "{count} drivers",
  "drivers.count.one": "{count} driver",
  "drivers.search.label": "Search drivers",
  "drivers.search.placeholder": "Search by number, name or phone…",
  "drivers.filter.status": "Status",
  "drivers.filter.availability": "Availability",
  "drivers.filter.blocked": "Blocked only",
  "drivers.filter.vehicle": "Vehicle",
  "drivers.table.label": "Drivers",
  "drivers.table.driver": "Driver",
  "drivers.table.phone": "Phone",
  "drivers.table.vehicle": "Vehicle",
  "drivers.vehicle.none": "No vehicle",
  "drivers.table.status": "Status",
  "drivers.table.availability": "Availability",
  "drivers.table.blocked": "Blocked",
  "drivers.table.created": "Joined",
  "drivers.export.driverNo": "Driver no.",
  "drivers.status.PENDING_APPROVAL": "Pending approval",
  "drivers.status.ACTIVE": "Active",
  "drivers.status.SUSPENDED": "Suspended",
  "drivers.status.TERMINATED": "Terminated",
  "drivers.availability.ONLINE": "Online",
  "drivers.availability.OFFLINE": "Offline",
  "drivers.availability.ON_ORDER": "On an order",
  "drivers.blocked.WALLET_BELOW_THRESHOLD": "Wallet below threshold",
  "drivers.blocked.DOCUMENTS_EXPIRED": "Documents expired",
  "drivers.blocked.ADMIN": "Blocked by admin",
  "drivers.empty.title": "No drivers yet",
  "drivers.emptyFiltered.title": "No drivers match",
  "drivers.tabs.label": "Driver sections",
  "drivers.tabs.overview": "Overview",
  "drivers.detail.notBlocked": "Not blocked",
  "drivers.detail.nationalId": "National ID",
  "drivers.detail.birthDate": "Birth date",
  "drivers.detail.notes": "Notes",

  // ---- Customers (M6-04, §2.4, §3.4) ----
  "customers.title": "Customers",
  "customers.count": "{count} customers",
  "customers.count.one": "{count} customer",
  "customers.search.label": "Search customers",
  "customers.search.placeholder": "Search by phone or name…",
  "customers.filter.origin": "Origin",
  "customers.filter.claimed": "Account link",
  "customers.table.label": "Customers",
  "customers.table.customer": "Customer",
  "customers.table.origin": "Origin",
  "customers.table.linked": "Account",
  "customers.table.locale": "Language",
  "customers.table.created": "First seen",
  "customers.export.phone": "Phone",
  "customers.origin.REGISTERED": "Registered directly",
  "customers.origin.GUEST_MERCHANT_ORDER": "Guest via merchant order",
  "customers.origin.GUEST_ADMIN": "Guest via admin",
  "customers.linked.registered": "Registered",
  "customers.linked.guest": "Guest",
  "customers.locale.ar": "Arabic",
  "customers.locale.en": "English",
  "customers.unnamed": "Unnamed customer",
  "customers.empty.title": "No customers yet",
  "customers.emptyFiltered.title": "No customers match",
  "customers.tabs.label": "Customer sections",
  "customers.tabs.overview": "Overview",
  "customers.detail.claimedAt": "Account claimed",
  "customers.detail.addresses": "Saved addresses",
  "customers.addresses.default": "Default",
  "customers.addresses.empty.title": "No saved addresses",

  // ---- Audit / Change History (audit.yaml v1.0.0 — M6-18, conformed M6-06) ----
  "audit.tab": "Change history",
  "audit.verb.created": "Created",
  "audit.verb.updated": "Updated",
  "audit.verb.statusChanged": "Status changed",
  "audit.verb.deleted": "Deleted",
  "audit.by": "By {actor}",
  "audit.actor.system": "System",
  "audit.subjectLabel": "Subject",
  "audit.traceLabel": "Trace",
  "audit.diff.was": "was",
  "audit.diff.now": "now",
  "audit.emptyValue": "(empty)",
  "audit.empty.title": "No recorded changes",
  "audit.denied.title": "Change history unavailable",

  // ---- Wallet management (M6-06 §14.4, §8.1/§8.2) ----
  "wallets.title": "Wallets",
  "wallets.count": "{count} wallets",
  "wallets.count.one": "{count} wallet",
  "wallets.tabs.label": "Wallet groups",
  "wallets.tabs.merchants": "Merchant wallets",
  "wallets.tabs.drivers": "Driver wallets",
  "wallets.table.label": "Wallets",
  "wallets.table.owner": "Owner",
  "wallets.table.balance": "Balance",
  "wallets.table.creditLimit": "Credit limit",
  "wallets.table.available": "Available",
  "wallets.table.status": "Status",
  "wallets.table.blocked": "Assignment block",
  "wallets.table.actions": "Actions",
  "wallets.badge.negative": "Negative",
  "wallets.badge.frozen": "Frozen",
  "wallets.badge.active": "Active",
  "wallets.badge.blocked": "Blocked",
  "wallets.badge.notBlocked": "Clear",
  "wallets.filter.negativeOnly": "Negative balances only",
  "wallets.filter.blockedOnly": "Blocked drivers only",
  "wallets.action.fund": "Fund wallet",
  "wallets.action.topup": "Top up",
  "wallets.action.adjust": "Adjust",
  "wallets.action.setCreditLimit": "Set credit limit",
  "wallets.export.ownerNo": "Owner no.",
  "wallets.empty.title": "No wallets yet",
  "wallets.openOwner.action": "Open an owner's wallet",
  "wallets.openOwner.title": "Open an owner's wallet",
  "wallets.openOwner.searchMerchant": "Search merchants",
  "wallets.openOwner.searchDriver": "Search drivers",
  "wallets.openOwner.searchPlaceholder": "Name, number or phone",
  "wallets.openOwner.results": "Matching owners",
  "wallets.openOwner.failed": "The directory could not be loaded.",
  "wallets.openOwner.empty.title": "No matching owner",
  "wallets.emptyFiltered.title": "No wallets match",
  "wallets.owner.MERCHANT": "Merchant",
  "wallets.owner.DRIVER": "Driver",
  "wallets.owner.CUSTOMER": "Customer",
  "wallets.detail.title": "Wallet",
  "wallets.detail.balanceTitle": "Balance",
  "wallets.blockedBanner.title": "Assignments blocked",
  "wallets.blockedBanner.since": "Blocked since {date}.",
  "wallets.plan.title": "Driver plan",
  "wallets.plan.type": "Plan type",
  "wallets.plan.DAILY_SUBSCRIPTION": "Daily subscription",
  "wallets.plan.COMMISSION": "Commission",
  "wallets.plan.dailyFee": "Daily fee",
  "wallets.plan.commissionPercent": "Commission of delivery fee",
  "wallets.plan.effectiveFrom": "Effective from",
  "wallets.plan.none": "No active plan.",
  "wallets.ledger.title": "Ledger",
  "wallets.ledger.filterLabel": "Filter by entry type",
  "wallets.ledger.tableLabel": "Ledger entries",
  "wallets.ledger.date": "Date",
  "wallets.ledger.type": "Type",
  "wallets.ledger.amount": "Amount",
  "wallets.ledger.balanceAfter": "Balance after",
  "wallets.ledger.related": "Reference",
  "wallets.ledger.memo": "Memo",
  "wallets.ledger.promotionRef": "Promotion",
  "wallets.ledger.empty.title": "No ledger entries",
  "wallets.entry.TOPUP": "Top-up",
  "wallets.entry.WITHDRAWAL": "Withdrawal",
  "wallets.entry.COMMISSION": "Commission",
  "wallets.entry.DAILY_FEE": "Daily fee",
  "wallets.entry.SUBSCRIPTION_FEE": "Subscription fee",
  "wallets.entry.COFUND_DEDUCTION": "Co-fund deduction",
  "wallets.entry.COFUND_COMPENSATION": "Co-fund compensation",
  "wallets.entry.SETTLEMENT": "Settlement",
  "wallets.entry.ADJUSTMENT": "Adjustment",
  "wallets.entry.REFUND": "Refund",
  "wallets.entry.REVERSAL": "Reversal",
  "wallets.direction.CREDIT": "Credit (add funds)",
  "wallets.direction.DEBIT": "Debit (deduct funds)",
  "wallets.dialog.fundTitle": "Fund merchant wallet",
  "wallets.dialog.topupTitle": "Top up driver wallet",
  "wallets.dialog.adjustTitle": "Post adjustment",
  "wallets.dialog.confirm": "Post entry",
  "wallets.dialog.amount": "Amount",
  "wallets.dialog.amountInvalid": "Enter a valid amount (e.g. 25000.00).",
  "wallets.dialog.memo": "Reference note",
  "wallets.dialog.direction": "Direction",
  "wallets.dialog.override": "Allow crossing the credit limit",
  "wallets.dialog.limitNoOverride": "This debit would cross the wallet's credit limit; you do not hold the override permission.",
  "wallets.dialog.balanceContext": "Current balance: {balance}.",
  "wallets.dialog.fundConsequence": "This posts a cash deposit to the merchant wallet immediately and appears in the ledger.",
  "wallets.dialog.topupConsequence": "This posts a cash top-up to the driver wallet immediately; if the driver is blocked, a sufficient top-up lifts the block.",
  "wallets.dialog.adjustDebitConsequence": "This deducts funds from the wallet immediately and appears in the ledger.",
  "wallets.dialog.adjustCreditConsequence": "This adds funds to the wallet immediately and appears in the ledger.",
  "wallets.creditLimit.title": "Credit limit override",
  "wallets.creditLimit.label": "Credit limit",
  "wallets.creditLimit.confirmTitle": "Change the credit limit?",
  "wallets.creditLimit.confirmDescription": "Future debits are checked against the new floor.",
  "wallets.toast.posted": "Entry posted.",
  "wallets.toast.limitSaved": "Credit limit updated.",

  // ---- Settlement + reconciliation (M6-06 §14.4, §8.3) ----
  "settlement.title": "Settlement",
  "settlement.tabs.label": "Settlement views",
  "settlement.tabs.records": "Records",
  "settlement.tabs.reconciliation": "Daily reconciliation",
  "settlement.table.label": "Settlement records",
  "settlement.table.record": "Record",
  "settlement.table.merchant": "Merchant",
  "settlement.table.driver": "Driver",
  "settlement.table.outcome": "Outcome",
  "settlement.table.status": "Status",
  "settlement.table.customerAmount": "Customer paid",
  "settlement.table.merchantAmount": "Merchant received",
  "settlement.table.platformRevenue": "Platform revenue",
  "settlement.table.created": "Created",
  "settlement.filter.merchant": "Merchant",
  "settlement.filter.driver": "Driver",
  "settlement.filter.outcome": "Outcome",
  "settlement.filter.status": "Status",
  "settlement.filter.from": "From",
  "settlement.filter.to": "To",
  "settlement.outcome.DELIVERED": "Delivered",
  "settlement.outcome.RETURNED": "Returned",
  "settlement.status.DRAFT": "Draft",
  "settlement.status.PENDING": "Pending",
  "settlement.status.SETTLED": "Settled",
  "settlement.status.VOID": "Void",
  "settlement.status.DISPUTED": "Disputed",
  "settlement.empty.title": "No settlement records",
  "settlement.emptyFiltered.title": "No records match",
  "settlement.scope.DRIVER": "Driver",
  "settlement.scope.MERCHANT": "Merchant",
  "settlement.party.DRIVER": "Driver",
  "settlement.party.MERCHANT": "Merchant",
  "settlement.party.CUSTOMER": "Customer",
  "settlement.party.PLATFORM": "Platform",
  "settlement.party.PROMOTION_FUND": "Promotion fund",
  "settlement.flow.DRIVER_PAYS_MERCHANT": "Driver pays merchant the order value",
  "settlement.flow.CUSTOMER_PAYS_DRIVER": "Customer pays driver on delivery",
  "settlement.flow.MERCHANT_REFUNDS_DRIVER": "Merchant refunds driver (return)",
  "settlement.flow.MERCHANT_PAYS_RETURN_FEE": "Merchant pays driver the delivery fee (return)",
  "settlement.flow.PLATFORM_COMMISSION": "Platform commission",
  "settlement.flow.DAILY_FEE": "Daily subscription fee",
  "settlement.flow.COFUND_MERCHANT_SHARE": "Merchant share of the co-funded discount",
  "settlement.flow.COFUND_DRIVER_COMPENSATION": "Driver compensation for the discount",
  "settlement.flow.REVERSAL": "Reversal",
  "settlement.detail.parties": "Parties",
  "settlement.detail.amounts": "Amounts",
  "settlement.detail.orderValue": "Order value",
  "settlement.detail.deliveryFee": "Delivery fee",
  "settlement.detail.customerDiscount": "Customer discount",
  "settlement.detail.merchantAmount": "Merchant received",
  "settlement.detail.customerAmount": "Customer paid",
  "settlement.detail.driverIncome": "Driver delivery income",
  "settlement.detail.platformRevenue": "Platform revenue",
  "settlement.detail.merchantShare": "Merchant discount share",
  "settlement.detail.driverCompensation": "Driver discount compensation",
  "settlement.detail.driverPlan": "Driver plan",
  "settlement.detail.flowsTitle": "Cash flow legs",
  "settlement.detail.cofundTag": "Co-fund",
  "settlement.recon.status.DRAFT": "Draft",
  "settlement.recon.status.FINALIZED": "Finalized",
  "settlement.recon.table.label": "Daily reconciliations",
  "settlement.recon.table.report": "Report",
  "settlement.recon.table.scope": "Scope",
  "settlement.recon.table.orders": "Orders",
  "settlement.recon.table.ordersSplit": "{total} ({delivered} delivered / {returned} returned)",
  "settlement.recon.scopeType": "Scope type",
  "settlement.recon.scope": "Scope",
  "settlement.recon.periodDate": "Day",
  "settlement.recon.ordersCount": "Orders",
  "settlement.recon.deliveredCount": "Delivered",
  "settlement.recon.returnedCount": "Returned",
  "settlement.recon.grossCollected": "Gross collected",
  "settlement.recon.merchantPaid": "Paid to merchants",
  "settlement.recon.deliveryFees": "Delivery fees",
  "settlement.recon.platformRevenue": "Platform revenue",
  "settlement.recon.cofundDeductions": "Co-fund deductions",
  "settlement.recon.netPosition": "Net position",
  "settlement.recon.generatedAt": "Generated {date}",
  "settlement.recon.viewLedger": "Open wallet ledger",
  "settlement.recon.empty.title": "No reconciliation reports",
  "settlement.recon.generate.open": "Generate report",
  "settlement.recon.generate.title": "Generate daily reconciliation",
  "settlement.recon.generate.action": "Generate",
  "settlement.recon.generate.scopePlaceholder": "Choose…",
  "settlement.recon.generate.scopeRequired": "Choose a driver or merchant.",
  "settlement.recon.generate.dateRequired": "Choose the day to reconcile.",
  "settlement.recon.generate.finalize": "Finalize the report",
  "settlement.recon.confirm.title": "Generate and replace?",
  "settlement.recon.confirm.description": "An existing report for the same scope, day and currency is replaced.",
  "settlement.recon.toast.generated": "Reconciliation generated.",

  // ---- Subscription management (M6-06 §14.4, §7) ----
  "subscriptions.title": "Subscriptions",
  "subscriptions.tabs.label": "Subscription views",
  "subscriptions.tabs.subscriptions": "Subscriptions",
  "subscriptions.tabs.packages": "Packages",
  "subscriptions.table.label": "Merchant subscriptions",
  "subscriptions.table.subscription": "Subscription",
  "subscriptions.table.merchant": "Merchant",
  "subscriptions.table.package": "Package",
  "subscriptions.table.status": "Status",
  "subscriptions.table.starts": "Starts",
  "subscriptions.table.ends": "Ends",
  "subscriptions.table.autoRenew": "Auto-renew",
  "subscriptions.filter.status": "Status",
  "subscriptions.filter.merchant": "Merchant",
  "subscriptions.status.ACTIVE": "Active",
  "subscriptions.status.EXPIRING": "Expiring",
  "subscriptions.status.EXPIRED": "Expired",
  "subscriptions.status.RENEWED": "Renewed",
  "subscriptions.status.CANCELLED": "Cancelled",
  "subscriptions.autoRenew.on": "Auto-renew",
  "subscriptions.autoRenew.off": "Off",
  "subscriptions.empty.title": "No subscriptions yet",
  "subscriptions.emptyFiltered.title": "No subscriptions match",
  "subscriptions.assign.open": "New subscription",
  "subscriptions.assign.title": "Assign a package to a merchant",
  "subscriptions.assign.action": "Subscribe",
  "subscriptions.assign.merchant": "Merchant",
  "subscriptions.assign.merchantPlaceholder": "Choose a merchant…",
  "subscriptions.assign.merchantRequired": "Choose a merchant.",
  "subscriptions.assign.package": "Package",
  "subscriptions.assign.packagePlaceholder": "Choose a package…",
  "subscriptions.assign.packageRequired": "Choose a package.",
  "subscriptions.assign.autoRenew": "Auto-renew at period end",
  "subscriptions.assign.confirmTitle": "Start this subscription?",
  "subscriptions.assign.confirmWallet": "{amount} is deducted from the merchant wallet immediately.",
  "subscriptions.paidVia.label": "Paid via",
  "subscriptions.paidVia.CASH_ADMIN": "Cash (recorded by admin)",
  "subscriptions.paidVia.MERCHANT_WALLET": "Merchant wallet",
  "subscriptions.period.MONTHLY": "Monthly",
  "subscriptions.period.QUARTERLY": "Quarterly",
  "subscriptions.period.YEARLY": "Yearly",
  "subscriptions.feature.customer_app_visibility": "Customer app visibility",
  "subscriptions.feature.featured_listing": "Featured listing",
  "subscriptions.feature.lower_commission": "Lower commission",
  "subscriptions.feature.promotion_cofunding": "Promotion co-funding",
  "subscriptions.feature.advanced_analytics": "Advanced analytics",
  "subscriptions.package.create": "New package",
  "subscriptions.package.createTitle": "Create package",
  "subscriptions.package.editTitle": "Edit package",
  "subscriptions.package.active": "Active",
  "subscriptions.package.inactive": "Inactive",
  "subscriptions.package.table.label": "Subscription packages",
  "subscriptions.package.table.name": "Package",
  "subscriptions.package.table.price": "Price",
  "subscriptions.package.table.features": "Features",
  "subscriptions.package.table.active": "Status",
  "subscriptions.package.empty.title": "No packages yet",
  "subscriptions.package.form.required": "This field is required.",
  "subscriptions.package.form.nameAr": "Name (Arabic)",
  "subscriptions.package.form.nameEn": "Name (English)",
  "subscriptions.package.form.descriptionAr": "Description (Arabic)",
  "subscriptions.package.form.descriptionEn": "Description (English)",
  "subscriptions.package.form.price": "Price",
  "subscriptions.package.form.period": "Period",
  "subscriptions.package.form.sortOrder": "Sort order",
  "subscriptions.package.form.features": "Package features",
  "subscriptions.package.form.isActive": "Available for new subscriptions",
  "subscriptions.package.deactivate.title": "Delist this package?",
  "subscriptions.package.deactivate.description": "New subscriptions can no longer use this package.",
  "subscriptions.detail.periodTitle": "Subscription period",
  "subscriptions.detail.featuresTitle": "Features snapshot",
  "subscriptions.detail.noFeatures": "No features in this period's snapshot.",
  "subscriptions.detail.invoicesTitle": "Invoice history",
  "subscriptions.detail.renewedFrom": "Renewed from",
  "subscriptions.detail.renewedTo": "Renewed to",
  "subscriptions.banner.expiringTitle": "Expiring soon",
  "subscriptions.banner.expiredTitle": "Expired",
  "subscriptions.renew.title": "Renew subscription",
  "subscriptions.renew.action": "Renew",
  "subscriptions.renew.confirmTitle": "Renew this subscription?",
  "subscriptions.renew.toast": "Subscription renewed.",
  "subscriptions.cancel.title": "Cancel subscription",
  "subscriptions.cancel.action": "Cancel subscription",
  "subscriptions.cancel.description": "The store loses its subscription features, including customer-app visibility.",
  "subscriptions.cancel.reason": "Reason",
  "subscriptions.cancel.toast": "Subscription cancelled.",
  "subscriptions.invoice.number": "Invoice",
  "subscriptions.invoice.amount": "Amount",
  "subscriptions.invoice.status": "Status",
  "subscriptions.invoice.paidAt": "Paid at",
  "subscriptions.invoice.actions": "Actions",
  "subscriptions.invoice.empty": "No invoices for this period.",
  "subscriptions.invoice.recordPayment": "Record payment",
  "subscriptions.invoice.toast.paid": "Invoice marked paid.",
  "subscriptions.invoice.payConfirmTitle": "Record this payment?",
  "subscriptions.invoice.payConfirmWallet": "{amount} is deducted from the merchant wallet immediately.",
  "subscriptions.invoice.status.PENDING": "Pending",
  "subscriptions.invoice.status.PAID": "Paid",
  "subscriptions.invoice.status.FAILED": "Failed",
  "subscriptions.invoice.status.VOID": "Void",
  "subscriptions.invoice.status.WAIVED": "Waived",

  // ---- Merchants export (M6-04 platform adoption) ----
  "merchants.export.merchantNo": "Merchant no.",

  // identity.yaml x-error-codes (client-side rendering of stable codes)
  /* M6-07 — promotion management (§4, §14.4) */
  "common.from": "From",
  "common.to": "To",

  "promotions.title": "Promotions",
  "promotions.tabs.label": "Promotion sections",
  "promotions.tabs.promotions": "Promotions",
  "promotions.tabs.applications": "Applications",
  "promotions.tabs.funding": "Funding reports",
  "promotions.tabs.preview": "Preview",
  "promotions.create": "New promotion",
  "promotions.create.title": "New promotion",
  "promotions.create.denied": "You do not have permission to create promotions.",
  "zones.manage.denied": "You do not have permission to manage zones.",
  "merchants.create.denied": "You do not have permission to create merchants.",
  "drivers.create.denied": "You do not have permission to create drivers.",

  "promotions.table.label": "Promotions",
  "promotions.table.no": "Reference",
  "promotions.table.name": "Name",
  "promotions.table.kind": "Type",
  "promotions.table.method": "Discount",
  "promotions.table.funder": "Funded by",
  "promotions.table.status": "Status",
  "promotions.table.priority": "Priority",
  "promotions.table.exclusive": "Exclusive",
  "promotions.table.redemptions": "Redemptions",
  "promotions.table.starts": "Starts",
  "promotions.table.ends": "Ends",
  "promotions.listed": "Listed",
  "promotions.filter.status": "Filter by status",
  "promotions.filter.kind": "Type",
  "promotions.filter.funder": "Funded by",
  "promotions.filter.target": "Target",
  "promotions.filter.search": "Search",
  "promotions.filter.search.placeholder": "Reference, name or coupon code",
  "promotions.empty.title": "No promotions yet",
  "promotions.emptyFiltered.title": "No promotions match",

  "promotions.status.DRAFT": "Draft",
  "promotions.status.ACTIVE": "Active",
  "promotions.status.PAUSED": "Paused",
  "promotions.status.ENDED": "Ended",
  "promotions.status.ARCHIVED": "Archived",

  "promotions.kind.COUPON": "Coupon code",
  "promotions.kind.AUTOMATIC": "Automatic",
  "promotions.kind.CAMPAIGN": "Campaign",
  "promotions.target.DELIVERY_FEE": "Delivery fee",
  "promotions.target.ORDER_VALUE": "Order value",
  "promotions.target.FREE_DELIVERY": "Free delivery",
  "promotions.method.PERCENTAGE": "Percentage",
  "promotions.method.FIXED_AMOUNT": "Fixed amount",
  "promotions.funder.PLATFORM": "Platform",
  "promotions.funder.MERCHANT": "Merchant",
  "promotions.funder.SHARED": "Shared",

  "promotions.form.nameAr": "Name (Arabic)",
  "promotions.form.nameEn": "Name (English)",
  "promotions.form.descriptionAr": "Description (Arabic)",
  "promotions.form.descriptionEn": "Description (English)",
  "promotions.form.listedInApp": "Show in customer app",
  "promotions.form.kind": "Promotion type",
  "promotions.form.target": "Discount target",
  "promotions.form.method": "Calculation method",
  "promotions.form.valuePercent": "Percentage",
  "promotions.form.valueAmount": "Amount",
  "promotions.form.valueInvalid": "Enter a valid positive amount (percentages up to 100).",
  "promotions.form.maxDiscount": "Maximum discount",
  "promotions.form.funding": "Funding (who pays for the discount)",
  "promotions.form.funder": "Funded by",
  "promotions.form.merchantShare": "Merchant share",
  "promotions.form.merchantShareInvalid": "For shared funding the merchant share must be strictly between 0 and 100.",
  "promotions.form.platformShare": "Platform share (derived): {pct}%",
  "promotions.form.sumsTo100": "Split sums to 100%",
  "promotions.form.merchantsRequired": "Merchant/shared funding requires selecting at least one merchant in the merchants condition.",
  "promotions.form.conditions": "Conditions",
  "promotions.form.stackingLimits": "Stacking & usage limits",
  "promotions.form.stackable": "Stackable",
  "promotions.form.priority": "Priority",
  "promotions.form.usageLimitTotal": "Global usage limit",
  "promotions.form.usageLimitPerCustomer": "Per-customer limit",
  "promotions.form.limitInvalid": "Enter a whole number of at least 1.",
  "promotions.form.startsAt": "Starts at",
  "promotions.form.endsAt": "Ends at",
  "promotions.form.required": "This field is required.",
  "promotions.form.couponCode": "Coupon code",
  "promotions.form.orderValueBoundsInvalid": "The minimum must not exceed the maximum.",
  "promotions.form.create": "Create promotion",

  "promotions.condition.first_order": "First order",
  "promotions.condition.customer_segment": "Customer segment",
  "promotions.condition.order_value": "Order value bounds",
  "promotions.condition.order_value.min": "Minimum",
  "promotions.condition.order_value.max": "Maximum",
  "promotions.condition.day_of_week": "Days of week",
  "promotions.condition.time_window": "Time window",
  "promotions.condition.time_window.from": "From",
  "promotions.condition.time_window.to": "To",
  "promotions.condition.time_window.wraps": "Wraps past midnight",
  "promotions.condition.zones": "Recipient zones",
  "promotions.condition.merchant_categories": "Order categories",
  "promotions.condition.merchants": "Merchants",
  "promotions.segment.NEW": "New",
  "promotions.segment.RETURNING": "Returning",
  "promotions.segment.DORMANT": "Dormant",
  "promotions.weekday.MON": "Mon",
  "promotions.weekday.TUE": "Tue",
  "promotions.weekday.WED": "Wed",
  "promotions.weekday.THU": "Thu",
  "promotions.weekday.FRI": "Fri",
  "promotions.weekday.SAT": "Sat",
  "promotions.weekday.SUN": "Sun",

  "promotions.detail.tabs.label": "Promotion detail sections",
  "promotions.detail.tabs.definition": "Definition",
  "promotions.detail.tabs.codes": "Coupon codes",
  "promotions.detail.tabs.applications": "Applications",
  "promotions.detail.tabs.funding": "Funding report",
  "promotions.detail.redemptions": "Redemptions: {count} / {limit}",
  "promotions.detail.unlimited": "unlimited",
  "promotions.lifecycle.reason": "Reason (optional, audited)",
  "promotions.lifecycle.ACTIVE": "Activate",
  "promotions.lifecycle.ACTIVE.title": "Activate this promotion?",
  "promotions.lifecycle.PAUSED": "Pause",
  "promotions.lifecycle.PAUSED.title": "Pause this promotion?",
  "promotions.lifecycle.PAUSED.description": "It stops applying to new orders immediately.",
  "promotions.lifecycle.ENDED": "End",
  "promotions.lifecycle.ENDED.title": "End this promotion?",
  "promotions.lifecycle.ENDED.description": "Ending is permanent and cannot be re-activated.",
  "promotions.lifecycle.ARCHIVED": "Archive",
  "promotions.lifecycle.ARCHIVED.title": "Archive this promotion?",
  "promotions.lifecycle.ARCHIVED.description": "It leaves the day-to-day lists.",
  "promotions.delete": "Delete draft",
  "promotions.delete.title": "Delete this draft?",
  "promotions.delete.description": "This cannot be undone.",

  "promotions.codes.add.label": "New code",
  "promotions.codes.add.invalid": "3–40 characters: letters, digits, dash or underscore only.",
  "promotions.codes.add.submit": "Add code",
  "promotions.codes.table.label": "Coupon codes",
  "promotions.codes.table.code": "Code",
  "promotions.codes.table.state": "State",
  "promotions.codes.table.created": "Created",
  "promotions.codes.active": "Active",
  "promotions.codes.inactive": "Inactive",
  "promotions.codes.enable": "Enable",
  "promotions.codes.enable.title": "Enable this code?",
  "promotions.codes.disable": "Disable",
  "promotions.codes.disable.title": "Disable this code?",
  "promotions.codes.disable.description": "Customers can no longer enter this code.",
  "promotions.codes.empty.title": "No codes yet",
  "promotions.lifecycle.codesRequired": "Add an active coupon code before activating.",

  "promotions.applications.table.label": "Promotion applications",
  "promotions.applications.filter.status": "Filter by application status",
  "promotions.applications.table.promotion": "Promotion",
  "promotions.applications.table.order": "Order",
  "promotions.applications.table.code": "Code",
  "promotions.applications.table.status": "Status",
  "promotions.applications.table.amount": "Discount",
  "promotions.applications.table.funding": "Funding (merchant / platform)",
  "promotions.applications.table.totals": "Total before ← after",
  "promotions.applications.table.appliedAt": "Applied at",
  "promotions.applications.empty.title": "No applications yet",
  "promotions.applications.emptyFiltered.title": "No applications match",
  "promotions.application.status.APPLIED": "Applied",
  "promotions.application.status.REVERTED": "Reverted",
  "promotions.application.status.SETTLED": "Settled",
  "promotions.revert.SUPERSEDED": "Superseded by recalculation",
  "promotions.revert.WINDOW_RECALC_REMOVED": "Removed in modification window",
  "promotions.revert.ORDER_CANCELLED": "Order cancelled",
  "promotions.revert.ADMIN_VOID": "Voided by admin",

  "promotions.funding.table.label": "Funding reports",
  "promotions.funding.table.promotion": "Promotion",
  "promotions.funding.table.applications": "Applications",
  "promotions.funding.table.reverted": "Reverted",
  "promotions.funding.table.totalDiscount": "Total discount",
  "promotions.funding.table.platformFunded": "Platform-funded",
  "promotions.funding.table.merchantFunded": "Merchant-funded",
  "promotions.funding.table.driverCompensation": "Driver compensation",
  "promotions.funding.empty.title": "No funding data",
  "promotions.funding.window": "Window: {from} → {to}",
  "promotions.funding.includePending": "Include pending (applied, not yet settled)",
  "promotions.funding.pending.title": "Pending (not yet settled)",
  "promotions.funding.pending.none": "Nothing pending in this window.",
  "promotions.funding.byMerchant.title": "By merchant",
  "promotions.funding.revertedCount": "{count} reverted (excluded from totals)",

  "promotions.evaluate.merchant": "Merchant",
  "promotions.evaluate.zone": "Recipient zone",
  "promotions.evaluate.category": "Order category",
  "promotions.evaluate.orderValue": "Order value",
  "promotions.evaluate.deliveryFee": "Delivery fee",
  "promotions.evaluate.coupon": "Coupon code",
  "promotions.evaluate.at": "Evaluate at",
  "promotions.evaluate.firstOrder": "Treat as the customer's first order",
  "promotions.evaluate.pick": "Choose…",
  "promotions.evaluate.none": "None",
  "promotions.evaluate.run": "Evaluate",
  "promotions.evaluate.result.title": "Result",
  "promotions.evaluate.noneApplied.title": "No discount applies",
  "promotions.evaluate.split": "Funding: merchant {merchant} / platform {platform}",
  "promotions.evaluate.totalBefore": "Total before",
  "promotions.evaluate.totalAfter": "Total after",
  "promotions.evaluate.candidates.title": "Considered promotions",
  "promotions.evaluate.denied.title": "Preview unavailable",
  "promotions.verdict.APPLIED": "Applied",
  "promotions.verdict.CONDITIONS_NOT_MET": "Conditions not met",
  "promotions.verdict.USAGE_LIMIT_REACHED": "Usage limit reached",
  "promotions.verdict.CUSTOMER_LIMIT_REACHED": "Customer limit reached",
  "promotions.verdict.MERCHANT_COFUNDING_NOT_ALLOWED": "Merchant not enabled for co-funding",
  "promotions.verdict.LOST_STACKING": "Lost stacking",
  "promotions.verdict.NOT_STARTED": "Not started",
  "promotions.verdict.EXPIRED": "Expired",
  "promotions.couponEcho.MATCHED": "Matched",
  "promotions.couponEcho.NOT_FOUND": "Not found",
  "promotions.couponEcho.INACTIVE": "Inactive",
  "promotions.couponEcho.NOT_ELIGIBLE": "Not eligible",
  "promotions.couponEcho.USAGE_LIMIT_REACHED": "Usage limit reached",
  "promotions.couponEcho.CUSTOMER_LIMIT_REACHED": "Customer limit reached",
  "promotions.couponEcho.NOT_STARTED": "Not started yet",
  "promotions.couponEcho.EXPIRED": "Expired",
  "promotions.couponEcho.LOST_STACKING": "Lost stacking",

  /* M7-106 — Ads (Wave 14, marketing.yaml; §14.4, §14.7) */
  /* Store categories — §14.4 admin-managed merchant categories (M7-148). */
  "storeCategories.title": "Store categories",
  "storeCategories.create": "New category",
  "storeCategories.denied": "You do not have permission to view store categories.",
  "storeCategories.table.label": "Store categories",
  "storeCategories.table.image": "Image",
  "storeCategories.table.name": "Name",
  "storeCategories.table.icon": "Icon key",
  "storeCategories.table.stores": "Stores",
  "storeCategories.table.status": "Status",
  "storeCategories.table.order": "Order",
  "storeCategories.status.active": "Visible",
  "storeCategories.status.hidden": "Hidden",
  "storeCategories.action.show": "Show",
  "storeCategories.action.hide": "Hide",
  "storeCategories.hide.title": "Hide this category?",
  "storeCategories.action.delete": "Delete category",
  "storeCategories.reorder.up": "Move up",
  "storeCategories.reorder.down": "Move down",
  "storeCategories.reorder.failed": "The new order was not saved.",
  "storeCategories.empty.title": "No categories yet",
  "storeCategories.toast.created": "Category created.",
  "storeCategories.toast.saved": "Category saved.",
  "storeCategories.toast.shown": "Category is visible again.",
  "storeCategories.toast.hidden": "Category hidden.",
  "storeCategories.toast.deleted": "Category deleted.",
  "storeCategories.delete.title": "Delete this category?",
  "storeCategories.delete.description": "This cannot be undone.",
  "storeCategories.delete.inUse": "This category is in use — hide it instead",
  "storeCategories.dialog.createTitle": "New category",
  "storeCategories.dialog.editTitle": "Edit category",
  "storeCategories.form.nameAr": "Name (Arabic)",
  "storeCategories.form.nameEn": "Name (English)",
  "storeCategories.form.icon": "Icon",
  "storeCategories.form.tooLong": "Use {max} characters or fewer.",
  "storeCategories.form.image": "Category image",
  "storeCategories.form.imagePreview": "Category image preview",
  "storeCategories.form.defaultOrderCategory": "Default order category",
  "storeCategories.form.defaultOrderCategoryNone": "No default",
  "storeCategories.form.unknownOrderCategory": "That order category no longer exists or is disabled — pick another.",
  "storeCategories.form.visibility": "Visibility",
  "storeCategories.hideWarning": "{count} stores stay filed under this category and keep listing under “All”.",
  "storeCategories.image.upload": "Upload image",
  "storeCategories.image.replace": "Replace image",
  "storeCategories.image.remove": "Remove image",

  // M7-150 — أنواع المركبات (vehicle types), owner ruling 2026-09-15 #1.
  "vehicleTypes.title": "Vehicle types",
  "vehicleTypes.create": "New vehicle type",
  "vehicleTypes.denied": "You do not have permission to view vehicle types.",
  "vehicleTypes.table.label": "Vehicle types",
  "vehicleTypes.table.code": "Code",
  "vehicleTypes.table.name": "Name",
  "vehicleTypes.table.drivers": "Drivers",
  "vehicleTypes.table.merchants": "Stores",
  "vehicleTypes.table.status": "Status",
  "vehicleTypes.table.order": "Order",
  "vehicleTypes.status.active": "Available",
  "vehicleTypes.status.hidden": "Hidden",
  "vehicleTypes.option.hidden": "(hidden)",
  "vehicleTypes.action.show": "Show",
  "vehicleTypes.action.hide": "Hide",
  "vehicleTypes.hide.title": "Hide this vehicle type?",
  "vehicleTypes.action.delete": "Delete vehicle type",
  "vehicleTypes.reorder.up": "Move up",
  "vehicleTypes.reorder.down": "Move down",
  "vehicleTypes.reorder.failed": "The new order was not saved.",
  "vehicleTypes.empty.title": "No vehicle types yet",
  "vehicleTypes.toast.created": "Vehicle type created.",
  "vehicleTypes.toast.saved": "Vehicle type saved.",
  "vehicleTypes.toast.shown": "Vehicle type is available again.",
  "vehicleTypes.toast.hidden": "Vehicle type hidden.",
  "vehicleTypes.toast.deleted": "Vehicle type deleted.",
  "vehicleTypes.delete.title": "Delete this vehicle type?",
  "vehicleTypes.delete.description": "This cannot be undone.",
  "vehicleTypes.delete.inUse": "This vehicle type is in use — hide it instead",
  "vehicleTypes.dialog.createTitle": "New vehicle type",
  "vehicleTypes.dialog.editTitle": "Edit vehicle type",
  "vehicleTypes.form.code": "Code",
  "vehicleTypes.form.codeImmutable": "The code cannot be changed after the vehicle type is created.",
  "vehicleTypes.form.codeInvalid": "Use capital letters, digits and underscores, starting with a letter.",
  "vehicleTypes.form.nameAr": "Name (Arabic)",
  "vehicleTypes.form.nameEn": "Name (English)",
  "vehicleTypes.form.tooLong": "Use {max} characters or fewer.",
  "vehicleTypes.form.visibility": "Availability",
  "vehicleTypes.hideWarning": "{drivers} drivers and {stores} stores keep this type and only new assignments stop.",

  "ads.title": "Ads",
  "ads.create": "New ad",
  "ads.create.title": "New ad",
  "ads.denied": "You do not have permission to view ads.",
  "ads.manage.denied": "You do not have permission to manage ads.",
  "ads.table.label": "Ads",
  "ads.table.image": "Image",
  "ads.table.title": "Title",
  "ads.table.kind": "Tap action",
  "ads.table.target": "Target store",
  "ads.table.status": "Status",
  "ads.table.schedule": "Schedule",
  "ads.table.order": "Order",
  "ads.schedule.always": "Always",
  "ads.schedule.from": "From {date}",
  "ads.schedule.until": "Until {date}",
  "ads.empty.title": "No ads yet",
  "ads.kind.none": "Image only",
  "ads.kind.store": "Opens a store",
  "ads.kind.product": "Opens a product",
  "ads.status.served": "Live",
  "ads.status.inactive": "Inactive",
  "ads.status.scheduled": "Scheduled",
  "ads.status.expired": "Expired",
  "ads.status.target_hidden": "Store hidden",
  "ads.reorder.up": "Move up",
  "ads.reorder.down": "Move down",
  "ads.reorder.failed": "Reordering failed — the list was refreshed from the server.",
  "ads.form.title": "Title",
  "ads.form.title.required": "Enter a title.",
  "ads.form.title.tooLong": "Keep the title within 120 characters.",
  "ads.form.image": "Image",
  "ads.form.image.required": "Choose an image.",
  "ads.form.image.type": "Only JPEG, PNG or WebP images are accepted.",
  "ads.form.image.size": "The image must be 2 MB or smaller.",
  "ads.form.image.tooNarrow": "The image must be at least 600 pixels wide.",
  "ads.form.image.ratioWarning": "This image is {ratio} — it will be cropped to fit the 2:1 frame.",
  "ads.form.image.preview": "Preview",
  "ads.form.image.empty": "No image chosen",
  "ads.form.kind": "Tap action",
  "ads.form.store": "Store",
  "ads.form.store.required": "Choose the store this ad opens.",
  "ads.form.active": "Active",
  "ads.form.startsAt": "Starts at",
  "ads.form.endsAt": "Ends at",
  "ads.form.schedule.order": "The end must be after the start.",
  "ads.form.submit.create": "Create ad",
  "ads.form.submit.save": "Save changes",
  "ads.form.created": "Ad created",
  "ads.form.saved": "Ad saved",
  "ads.delete": "Delete",
  "ads.delete.title": "Delete this ad?",
  "ads.delete.description": "This cannot be undone.",
  "ads.delete.done": "Ad deleted",
  "ads.detail.tabs.label": "Ad sections",
  "ads.detail.tabs.definition": "Definition",

  // M7-182 — customer home sections (marketing.yaml v1.2.0, §14.4 / §14.7).
  "homeSections.title": "Home sections",
  "homeSections.denied": "You do not have permission to view home sections.",
  "homeSections.empty.title": "No home sections",
  "homeSections.section.free_delivery": "Free delivery",
  "homeSections.section.discounts": "Discounts",
  "homeSections.section.request_driver": "Request a driver",
  "homeSections.section.offers_banner": "Offers banner",
  "homeSections.status.shown": "Shown",
  "homeSections.status.hidden": "Hidden",
  "homeSections.switch": "Shown on the home screen",
  "homeSections.form.titleAr": "Section screen title (Arabic)",
  "homeSections.form.titleEn": "Section screen title (English)",
  "homeSections.form.tooLong": "Use {max} characters or fewer.",
  "homeSections.toast.saved": "Titles saved.",
  "homeSections.toast.shown": "Section shown.",
  "homeSections.toast.hidden": "Section hidden.",
  "homeSections.toast.toggleFailed": "Visibility was not changed.",
  "homeSections.image": "Artwork",
  "homeSections.image.preview": "Artwork preview",
  "homeSections.image.empty": "No artwork",
  "homeSections.image.upload": "Upload artwork",
  "homeSections.image.replace": "Replace artwork",
  "homeSections.image.remove": "Remove artwork",
  "homeSections.image.remove.title": "Remove the artwork?",
  "homeSections.image.remove.description": "The app goes back to the plain header for this section.",
  "homeSections.image.remove.description.request_driver": "The entry disappears from the app's home screen.",
  "homeSections.image.remove.description.offers_banner": "The panel disappears from the top of the Offers tab.",
  "homeSections.meta.updated": "Updated {date}",
  "homeSections.meta.by": "by {name}",

  // M7-253 — store collections («المجموعات»), marketing.yaml v1.4.0.
  "collections.title": "Collections",
  "collections.denied": "You do not have permission to view collections.",
  "collections.create": "New collection",
  "collections.empty.title": "No collections",
  "collections.table.label": "Collections",
  "collections.table.image": "Artwork",
  "collections.table.title": "Title",
  "collections.table.stores": "Stores",
  "collections.table.shown": "Shown",
  "collections.table.order": "Order",
  "collections.status.shown": "Shown",
  "collections.status.hidden": "Hidden",
  "collections.switch": "Shown on the home screen",
  "collections.reorder.up": "Move up",
  "collections.reorder.down": "Move down",
  "collections.reorder.failed": "Reordering failed — the list was refreshed.",
  "collections.toast.shown": "Collection shown.",
  "collections.toast.hidden": "Collection hidden.",
  "collections.toast.toggleFailed": "Visibility was not changed.",
  "collections.toast.created": "Collection created.",
  "collections.toast.saved": "Collection saved.",
  "collections.toast.deleted": "Collection deleted.",
  "collections.toast.headingSaved": "Heading saved.",
  "collections.heading.title": "Row heading",
  "collections.heading.ar": "Heading (Arabic)",
  "collections.heading.en": "Heading (English)",
  "collections.form.titleAr": "Title (Arabic)",
  "collections.form.titleEn": "Title (English)",
  "collections.form.visibility": "Visibility",
  "collections.form.tooLong": "Use {max} characters or fewer.",
  "collections.form.stores": "Stores",
  "collections.form.stores.count": "{count} of {max} stores",
  "collections.form.stores.tooMany": "Choose {max} stores or fewer.",
  "collections.form.stores.empty": "No stores chosen",
  "collections.form.stores.search": "Search stores by name",
  "collections.form.stores.results": "Matching stores",
  "collections.form.stores.noResults": "No matching stores",
  "collections.form.stores.add": "Add {name}",
  "collections.form.stores.remove": "Remove {name}",
  "collections.form.stores.moveUp": "Move {name} up",
  "collections.form.stores.moveDown": "Move {name} down",
  "collections.form.stores.failed": "Stores could not be loaded.",
  "collections.form.store.notVisible": "Not shown in the customer app",
  "collections.form.store.missing": "Store not found",
  "collections.form.store.unknown": "Unknown store",
  "collections.image": "Artwork",
  "collections.image.preview": "Artwork preview",
  "collections.image.empty": "No artwork",
  "collections.image.upload": "Upload artwork",
  "collections.image.replace": "Replace artwork",
  "collections.dialog.createTitle": "New collection",
  "collections.dialog.editTitle": "Edit collection",
  "collections.action.delete": "Delete collection",
  "collections.delete.title": "Delete the collection?",
  "collections.delete.description": "The collection disappears from the app's home screen.",

  "categoryLayout.title": "Category layout",
  "categoryLayout.denied": "You do not have permission to view category layouts.",
  "categoryLayout.category": "Category",
  "categoryLayout.category.none": "No store categories",
  "categoryLayout.category.failed": "Store categories could not be loaded.",
  "categoryLayout.labelMode": "Tile label",
  "categoryLayout.labelMode.image_only": "Image only",
  "categoryLayout.labelMode.image_with_name": "Image with name",
  "categoryLayout.toast.labelModeSaved": "Tile label saved.",
  "categoryLayout.toast.labelModeFailed": "Tile label was not changed.",
  "categoryLayout.table.image": "Image",
  "categoryLayout.table.title": "Title",
  "categoryLayout.table.stores": "Stores",
  "categoryLayout.table.shown": "Shown",
  "categoryLayout.table.order": "Order",
  "categoryLayout.status.shown": "Shown",
  "categoryLayout.status.hidden": "Hidden",
  "categoryLayout.reorder.up": "Move up",
  "categoryLayout.reorder.down": "Move down",
  "categoryLayout.form.titleAr": "Title (Arabic)",
  "categoryLayout.form.titleEn": "Title (English)",
  "categoryLayout.form.visibility": "Visibility",
  "categoryLayout.form.tooLong": "Use {max} characters or fewer.",
  "categoryLayout.form.stores": "Stores",
  "categoryLayout.form.stores.tooMany": "Choose {max} stores or fewer.",
  "categoryLayout.form.position": "Position",
  "categoryLayout.form.position.integer": "Enter a whole number.",
  "categoryLayout.form.position.max": "Use {max} or less.",
  "categoryLayout.subcategories.title": "Subcategories",
  "categoryLayout.subcategories.create": "New subcategory",
  "categoryLayout.subcategories.empty.title": "No subcategories",
  "categoryLayout.subcategories.table.label": "Subcategories",
  "categoryLayout.subcategories.switch": "Shown in the category",
  "categoryLayout.subcategories.reorder.failed": "Reordering failed — the list was refreshed.",
  "categoryLayout.subcategories.toast.shown": "Subcategory shown.",
  "categoryLayout.subcategories.toast.hidden": "Subcategory hidden.",
  "categoryLayout.subcategories.toast.toggleFailed": "Visibility was not changed.",
  "categoryLayout.subcategories.toast.created": "Subcategory created.",
  "categoryLayout.subcategories.toast.saved": "Subcategory saved.",
  "categoryLayout.subcategories.toast.deleted": "Subcategory deleted.",
  "categoryLayout.subcategories.dialog.createTitle": "New subcategory",
  "categoryLayout.subcategories.dialog.editTitle": "Edit subcategory",
  "categoryLayout.subcategories.action.delete": "Delete subcategory",
  "categoryLayout.subcategories.delete.title": "Delete the subcategory?",
  "categoryLayout.subcategories.delete.description": "The tile disappears from the category screen in the app.",
  "categoryLayout.subcategories.image": "Tile image",
  "categoryLayout.subcategories.image.preview": "Tile image preview",
  "categoryLayout.subcategories.image.empty": "No image",
  "categoryLayout.subcategories.image.upload": "Upload image",
  "categoryLayout.subcategories.image.replace": "Replace image",
  "categoryLayout.slots.title": "Sections & banners",
  "categoryLayout.slots.create": "New slot",
  "categoryLayout.slots.empty.title": "No sections or banners",
  "categoryLayout.slots.table.label": "Sections and banners",
  "categoryLayout.slots.table.kind": "Kind",
  "categoryLayout.slots.table.position": "Position",
  "categoryLayout.slots.table.content": "Content",
  "categoryLayout.slots.table.stores": "Stores",
  "categoryLayout.slots.table.target": "Target store",
  "categoryLayout.slots.kind.section": "Section",
  "categoryLayout.slots.kind.banner": "Banner",
  "categoryLayout.slots.position.start": "Before the list",
  "categoryLayout.slots.position.after": "After {count} stores",
  "categoryLayout.slots.switch": "Shown in the category",
  "categoryLayout.slots.reorder.failed": "Reordering failed — the list was refreshed.",
  "categoryLayout.slots.toast.shown": "Slot shown.",
  "categoryLayout.slots.toast.hidden": "Slot hidden.",
  "categoryLayout.slots.toast.toggleFailed": "Visibility was not changed.",
  "categoryLayout.slots.toast.created": "Slot created.",
  "categoryLayout.slots.toast.saved": "Slot saved.",
  "categoryLayout.slots.toast.deleted": "Slot deleted.",
  "categoryLayout.slots.dialog.createTitle": "New slot",
  "categoryLayout.slots.dialog.editTitle.section": "Edit section",
  "categoryLayout.slots.dialog.editTitle.banner": "Edit banner",
  "categoryLayout.slots.action.delete": "Delete slot",
  "categoryLayout.slots.delete.title": "Delete the slot?",
  "categoryLayout.slots.delete.description": "The section or banner disappears from the category screen in the app.",
  "categoryLayout.slots.form.kind": "Kind",
  "categoryLayout.slots.form.target": "Target store",
  "categoryLayout.slots.form.target.none": "No target store",
  "categoryLayout.slots.image": "Banner image",
  "categoryLayout.slots.image.preview": "Banner image preview",
  "categoryLayout.slots.image.empty": "No image",
  "categoryLayout.slots.image.upload": "Upload image",
  "categoryLayout.slots.image.replace": "Replace image",

  /* M7-269 — home layout (§14.4 / §14.7, owner ruling 2026-10-05) */
  "homeLayout.title": "Home layout",
  "homeLayout.denied": "You do not have permission to view the home layout.",
  "homeLayout.table.label": "Home items",
  "homeLayout.table.order": "Order",
  "homeLayout.table.kind": "Kind",
  "homeLayout.table.item": "Item",
  "homeLayout.table.content": "Content",
  "homeLayout.table.shown": "Shown",
  "homeLayout.empty.title": "No home items",
  "homeLayout.reorder.up": "Move up",
  "homeLayout.reorder.down": "Move down",
  "homeLayout.reorder.failed": "Reordering failed — the list was refreshed.",
  "homeLayout.builtin.free_delivery": "Free delivery",
  "homeLayout.builtin.discounts": "Discounts",
  "homeLayout.builtin.collections": "Collections",
  "homeLayout.builtin.open": "Open",
  "homeLayout.kind.builtin": "Built-in",
  "homeLayout.kind.image_section": "Image section",
  "homeLayout.kind.title_section": "Title section",
  "homeLayout.kind.banner_group": "Banner group",
  "homeLayout.stores": "{count} stores",
  "homeLayout.stores.one": "{count} store",
  "homeLayout.banners.count": "{count} banners",
  "homeLayout.banners.count.one": "{count} banner",
  "homeLayout.rotate": "{count} s",
  "homeLayout.status.shown": "Shown",
  "homeLayout.status.hidden": "Hidden",
  "homeLayout.switch": "Shown on the home screen",
  "homeLayout.toast.shown": "Item shown.",
  "homeLayout.toast.hidden": "Item hidden.",
  "homeLayout.toast.toggleFailed": "Visibility was not changed.",
  "homeLayout.toast.created": "Item created.",
  "homeLayout.toast.saved": "Item saved.",
  "homeLayout.toast.deleted": "Item deleted.",
  "homeLayout.add": "Add",
  "homeLayout.add.menu": "Add a home item",
  "homeLayout.add.image_section": "Image section",
  "homeLayout.add.title_section": "Title section",
  "homeLayout.add.banner_group": "Banner group",
  "homeLayout.action.delete": "Delete item",
  "homeLayout.delete.title": "Delete the item?",
  "homeLayout.delete.description": "The item disappears from the home screen in the app.",
  "homeLayout.dialog.createTitle.image_section": "New image section",
  "homeLayout.dialog.createTitle.title_section": "New title section",
  "homeLayout.dialog.createTitle.banner_group": "New banner group",
  "homeLayout.dialog.editTitle.image_section": "Edit image section",
  "homeLayout.dialog.editTitle.title_section": "Edit title section",
  "homeLayout.dialog.editTitle.banner_group": "Edit banner group",
  "homeLayout.form.kind": "Kind",
  "homeLayout.form.titleAr": "Name (Arabic)",
  "homeLayout.form.titleEn": "Name (English)",
  "homeLayout.form.visibility": "Visibility",
  "homeLayout.form.tooLong": "Use {max} characters or fewer.",
  "homeLayout.form.stores": "Stores",
  "homeLayout.form.stores.tooMany": "Choose {max} stores or fewer.",
  "homeLayout.form.rotate": "Rotation (seconds)",
  "homeLayout.form.rotate.integer": "Enter a whole number.",
  "homeLayout.form.rotate.range": "Use {min} to {max}.",
  "homeLayout.image": "Section image",
  "homeLayout.image.preview": "Section image preview",
  "homeLayout.image.empty": "No image",
  "homeLayout.image.upload": "Upload image",
  "homeLayout.image.replace": "Replace image",
  "homeLayout.banners.title": "Banners",
  "homeLayout.banners.add": "Add banner",
  "homeLayout.banners.empty": "No banners",
  "homeLayout.banners.target.none": "No target store",
  "homeLayout.banners.switch": "Shown in the rotation",
  "homeLayout.banners.moveUp": "Move banner {index} up",
  "homeLayout.banners.moveDown": "Move banner {index} down",
  "homeLayout.banners.edit": "Edit banner {index}",
  "homeLayout.banners.delete": "Delete banner {index}",
  "homeLayout.banners.reorder.failed": "Reordering failed — the list was refreshed.",
  "homeLayout.banners.toast.shown": "Banner shown.",
  "homeLayout.banners.toast.hidden": "Banner hidden.",
  "homeLayout.banners.toast.toggleFailed": "Visibility was not changed.",
  "homeLayout.banners.toast.created": "Banner added.",
  "homeLayout.banners.toast.saved": "Banner saved.",
  "homeLayout.banners.toast.deleted": "Banner deleted.",
  "homeLayout.banners.dialog.createTitle": "New banner",
  "homeLayout.banners.dialog.editTitle": "Edit banner",
  "homeLayout.banners.delete.title": "Delete the banner?",
  "homeLayout.banners.delete.description": "The banner leaves the rotation on the home screen in the app.",
  "homeLayout.banners.form.image": "Banner image",
  "homeLayout.banners.form.image.preview": "Banner image preview",
  "homeLayout.banners.form.image.empty": "No image",
  "homeLayout.banners.form.image.upload": "Upload image",
  "homeLayout.banners.form.image.replace": "Replace image",
  "homeLayout.banners.form.image.required": "Choose an image.",
  "homeLayout.banners.form.target": "Target store",
  "homeLayout.banners.form.visibility": "Visibility",

  /* M7-309 — privacy policy (admin legal surface, §14.4 / §14.5) */
  "legal.title": "Privacy policy",
  "legal.denied": "You do not have permission to manage the privacy policy.",
  "legal.updated": "Last updated",
  "legal.tabs": "Language",
  "legal.tab.ar": "العربية",
  "legal.tab.en": "English",
  "legal.tab.invalid": "Has an error",
  "legal.body.ar": "Arabic text (Markdown)",
  "legal.body.en": "English text (Markdown)",
  "legal.preview": "Preview",
  "legal.error.tooLong": "At most {max} characters.",
  "legal.reset": "Discard changes",
  "legal.save": "Save",
  "legal.toast.saved": "Privacy policy saved.",
  "legal.link.label": "Public link",
  "legal.link.copy": "Copy link",
  "legal.link.copied": "Link copied.",
  "legal.link.copyFailed": "The link could not be copied.",

  /* M7-301 — pop-up ads (marketing.yaml v1.7.0, §14.4 / §14.7) */
  "popups.title": "Pop-up ads",
  "popups.denied": "You do not have permission to manage pop-up ads.",
  "popups.table.label": "Pop-up ads",
  "popups.table.order": "Order",
  "popups.table.popup": "Pop-up",
  "popups.table.enabled": "Enabled",
  "popups.table.frequency": "Per day",
  "popups.table.target": "Target store",
  "popups.table.schedule": "Schedule",
  "popups.table.servable": "Served",
  "popups.empty.title": "No pop-up ads",
  "popups.add": "Add pop-up",
  "popups.reorder.up": "Move up",
  "popups.reorder.down": "Move down",
  "popups.reorder.failed": "Reordering failed — the list was refreshed.",
  "popups.switch": "Enabled",
  "popups.status.enabled": "Enabled",
  "popups.status.disabled": "Disabled",
  "popups.servable.yes": "Served",
  "popups.servable.no": "Not served",
  "popups.reason.disabled": "Disabled",
  "popups.reason.no_image": "No image",
  "popups.reason.scheduled": "Scheduled",
  "popups.reason.expired": "Expired",
  "popups.frequency.unlimited": "Unlimited",
  "popups.frequency.one": "Once a day",
  "popups.frequency.two": "Twice a day",
  "popups.frequency.few": "{count} times a day",
  "popups.frequency.many": "{count} times a day",
  "popups.frequency.unit": "/ day",
  "popups.target.none": "No target store",
  "popups.target.hidden": "Store hidden",
  "popups.schedule.always": "Always",
  "popups.schedule.from": "From {date}",
  "popups.schedule.until": "Until {date}",
  "popups.toast.enabled": "Pop-up enabled.",
  "popups.toast.disabled": "Pop-up disabled.",
  "popups.toast.toggleFailed": "Status was not changed.",
  "popups.toast.created": "Pop-up created.",
  "popups.toast.saved": "Pop-up saved.",
  "popups.toast.deleted": "Pop-up deleted.",
  "popups.action.delete": "Delete pop-up",
  "popups.delete.title": "Delete the pop-up?",
  "popups.delete.description": "The pop-up stops showing in the app.",
  "popups.dialog.createTitle": "New pop-up",
  "popups.dialog.editTitle": "Edit pop-up",
  "popups.form.title": "Title",
  "popups.form.tooLong": "Use {max} characters or fewer.",
  "popups.form.enabled": "Status",
  "popups.form.frequency": "Shows per day",
  "popups.form.frequency.integer": "Enter a whole number.",
  "popups.form.frequency.range": "Use {min} to {max}.",
  "popups.form.target": "Target store",
  "popups.form.startsAt": "Starts",
  "popups.form.endsAt": "Ends",
  "popups.form.schedule.order": "The end must be after the start.",
  "popups.image": "Pop-up image",
  "popups.image.preview": "Pop-up image preview",
  "popups.image.empty": "No image",
  "popups.image.upload": "Upload image",
  "popups.image.replace": "Replace image",

  "merchantPicker.search.label": "Search stores by name or number",
  "merchantPicker.search.placeholder": "Store name or number",
  "merchantPicker.results": "Matching stores",
  "merchantPicker.count": "{count} stores",
  "merchantPicker.count.one": "{count} store",
  "merchantPicker.truncated": "Showing {shown} of {total} — narrow the search to see the rest",
  "merchantPicker.empty.title": "No matching stores",
  "merchantPicker.failed": "Could not load the store directory.",
  "merchantPicker.change": "Change",

  /* M6-07 — feature flags (§14.4, §13, R10) */
  "flags.title": "Feature Flags",
  "flags.table.label": "Feature flags",
  "flags.table.flag": "Flag",
  "flags.table.scope": "Scope",
  "flags.table.state": "State",
  "flags.table.overrides": "Overrides",
  "flags.table.nonDefault": "differs from default",
  "flags.scope.SYSTEM": "System",
  "flags.scope.MERCHANT": "Per-merchant",
  "flags.state.on": "On",
  "flags.state.off": "Off",
  "flags.action.enable": "Enable",
  "flags.action.disable": "Disable",
  "flags.action.overrides": "Overrides",
  "flags.packageFeature.badge": "Package feature — read-only",
  "flags.confirm.enable.title": "Enable this feature?",
  "flags.confirm.disable.title": "Disable this feature?",
  "flags.confirm.disable.description": "“{flag}” turns off platform-wide immediately.",
  "flags.overrides.title": "Per-merchant overrides — {flag}",
  "flags.overrides.merchant": "Merchant",
  "flags.overrides.pick": "Choose…",
  "flags.overrides.state": "Override state",
  "flags.overrides.reason": "Reason (audited)",
  "flags.overrides.save": "Save override",
  "flags.overrides.remove": "Remove override",
  "flags.overrides.saved": "Override saved.",
  "flags.overrides.removed": "Override removed — the system state applies again.",
  "flags.empty.title": "No flags",

  /* M6-07 — system settings (§13, §14.4) */
  "settings.title": "System Settings",
  "settings.search": "Search",
  "settings.search.placeholder": "Key or description",
  "settings.loading": "Loading settings…",
  "settings.empty.title": "No settings match",
  "settings.masked": "Sensitive — masked",
  "settings.window.badge": "Modification window",
  "settings.edit.title": "Edit setting",
  "settings.edit.value": "Value",
  "settings.edit.reset": "Reset to default",
  "settings.edit.default": "Seeded default: {value}",
  "settings.edit.invalidInt": "Enter a whole number.",
  "settings.edit.invalidDecimal": "Enter a decimal number.",
  "settings.edit.invalidJson": "Enter a valid JSON object.",
  "settings.edit.notAnOption": "Pick one of the allowed options.",
  "settings.edit.outOfRange": "The value is outside the allowed range.",
  "settings.edit.tooLong": "At most {max} characters.",
  "settings.confirm.title": "Apply this setting?",
  "settings.confirm.description": "{key}: {from} → {to} takes effect immediately, platform-wide.",
  "settings.reset.title": "Reset to default?",
  "settings.reset.description": "{key} returns to its seeded default ({value}) immediately.",
  "settings.providersEntry.open": "Manage providers",

  /* M6-07 — payment providers (§5.2, §14.4) */
  "providers.title": "Payment Providers",
  "providers.table.label": "Payment providers",
  "providers.table.provider": "Provider",
  "providers.table.kind": "Kind",
  "providers.table.state": "State",
  "providers.table.refunds": "Refunds",
  "providers.table.config": "Credentials",
  "providers.kind.INTERNAL": "Internal",
  "providers.kind.ELECTRONIC": "Electronic",
  "providers.enabled": "Enabled",
  "providers.disabled": "Disabled",
  "providers.refunds.yes": "Supported",
  "providers.config.set": "Configured",
  "providers.config.none": "Not configured",
  "providers.create": "Register provider",
  "providers.action.enable": "Enable",
  "providers.action.disable": "Disable",
  "providers.confirm.enable.title": "Enable this provider?",
  "providers.confirm.disable.title": "Disable this provider?",
  "providers.confirm.disable.description": "“{provider}” stops accepting new transactions immediately.",
  "providers.form.createTitle": "Register provider",
  "providers.form.editTitle": "Edit provider",
  "providers.form.key": "Provider key",
  "providers.form.keyInvalid": "Lowercase letters, digits and underscore only (max 48).",
  "providers.form.kind": "Kind",
  "providers.form.displayAr": "Display name (Arabic)",
  "providers.form.displayEn": "Display name (English)",
  "providers.form.config": "Credentials (write-only)",
  "providers.form.config.configured": "Credentials are configured. They are never displayed — entering new values replaces them; leaving the fields blank keeps them.",
  "providers.form.apiKey": "API key",
  "providers.form.apiSecret": "API secret",
  "providers.empty.title": "No providers",

  "apiError.VALIDATION_FAILED": "The submitted data is invalid",
  "apiError.AUTH_INVALID_CREDENTIALS": "Invalid credentials",
  "apiError.AUTH_TOKEN_INVALID": "Invalid session, please sign in again",
  "apiError.AUTH_TOKEN_EXPIRED": "Session expired",
  "apiError.AUTH_ACCOUNT_SUSPENDED": "This account is suspended",
  "apiError.REFRESH_INVALID": "Invalid session",
  "apiError.REFRESH_EXPIRED": "Session expired",
  "apiError.REFRESH_REUSE_DETECTED": "Session terminated for security reasons",
  "apiError.PERMISSION_DENIED": "You do not have permission for this action",
  "apiError.RESOURCE_NOT_FOUND": "Resource not found",
  "apiError.RATE_LIMITED": "Too many requests, try again later",
  // geo.yaml + merchants.yaml x-error-codes
  "apiError.ZONE_NAME_TAKEN": "Zone name already in use",
  "apiError.ZONE_ACTIVE": "Disable the zone before deleting it",
  "apiError.ZONE_DISABLED": "This zone is currently disabled",
  "apiError.BRANCH_LOCATION_MISSING": "The store location must be pinned on the map first",
  "apiError.OWNER_ALREADY_HAS_MERCHANT": "This user already owns a registered merchant",
  "apiError.PHONE_TAKEN": "This phone number already belongs to an existing account — link that user instead of creating a new one",
  "apiError.APPLICATION_ALREADY_DECIDED": "This application has already been decided",
  "apiError.MULTI_BRANCH_DISABLED": "The platform is single-branch — a second branch cannot be added",
  "apiError.FEATURE_DISABLED": "This feature is switched off for the platform",
  "apiError.BRANCH_ZONE_INVALID": "The selected zone is not available",
  "apiError.STAFF_USER_NOT_FOUND": "No registered user with this phone number",
  "apiError.STAFF_ALREADY_EMPLOYED": "This user is already staff of this merchant",
  "apiError.NO_OWNER_ACCOUNT": "No owner account",
  "apiError.MERCHANT_NOT_READY_FOR_ORDERS": "Activate the store and pin its location on the map first",
  // theme.yaml
  "apiError.THEME_VERSION_CONFLICT": "The theme was changed elsewhere — reload and reapply your edits",
  // orders.yaml (M6-04)
  "apiError.INVALID_TRANSITION": "The order cannot move to that status from its current state",
  "apiError.CANCELLATION_NOT_ALLOWED": "The order cannot be cancelled at this stage",
  "apiError.ORDER_NOT_EDITABLE": "The order cannot be edited after pickup",
  "apiError.DRIVER_BUSY": "The driver already has an active order",
  "apiError.DRIVER_NOT_ELIGIBLE": "The driver is not eligible for assignment",
  "apiError.DISPATCH_IN_PROGRESS": "This order is already being offered to drivers",
  // promotions.yaml + settings.yaml + payments.yaml (M6-07)
  "apiError.PROMOTION_NOT_EDITABLE": "Promotion economics are frozen after activation",
  "apiError.PROMOTION_NOT_DELETABLE": "Cannot delete a promotion past draft or with applications",
  "apiError.PROMOTION_STATUS_INVALID": "Status transition not in the whitelist",
  "apiError.PROMOTION_KIND_MISMATCH": "Coupon codes exist only on coupon promotions",
  "apiError.FUNDING_SPLIT_INVALID": "Funding split percentages are invalid for the funder",
  "apiError.COUPON_CODE_DUPLICATE": "Coupon code already exists",
  "apiError.SETTING_VALUE_INVALID": "Invalid setting value",
  "apiError.FLAG_NOT_MERCHANT_SCOPED": "This flag does not support per-merchant overrides",
  "apiError.PROVIDER_KEY_TAKEN": "Provider key already in use",
  "apiError.ROLE_KEY_TAKEN": "Role key already in use",
  "apiError.ROLE_SYSTEM_IMMUTABLE": "System roles cannot be modified",
  "apiError.ROLE_IN_USE": "This role is currently assigned to users",
  "apiError.DRIVER_USER_NOT_FOUND": "No registered account with this phone number",
  "apiError.DRIVER_ALREADY_EXISTS": "This account already has a driver profile",
  "apiError.DRIVER_BLOCK_REASON_RESERVED": "That block reason is reserved for the wallet system",
  "apiError.DOCUMENT_ALREADY_REVIEWED": "This document has already been reviewed",
  "apiError.DRIVER_PLAN_INVALID": "Invalid driver plan configuration",
  "apiError.BANNER_KIND_NOT_AVAILABLE": "Product banners are not available yet (phase 2)",
  "apiError.BANNER_TARGET_NOT_FOUND": "The target store does not exist",
  "apiError.BANNER_ORDER_INCOMPLETE": "The order list must contain every banner exactly once",
  "apiError.COLLECTION_ORDER_INCOMPLETE": "The order list must contain every collection exactly once",
  "apiError.SUBCATEGORY_ORDER_INCOMPLETE": "The order list must contain every subcategory exactly once",
  "apiError.SLOT_ORDER_INCOMPLETE": "The order list must contain every slot exactly once",
  "apiError.SLOT_KIND_MISMATCH": "This action applies to banner slots only",
  "apiError.HOME_ORDER_INCOMPLETE": "The order list must contain every home item exactly once",
  "apiError.POPUP_ORDER_INCOMPLETE": "The order list must contain every pop-up exactly once",
  "apiError.HOME_BANNER_ORDER_INCOMPLETE": "The order list must contain every banner of the group exactly once",
  "apiError.HOME_ENTRY_KIND_MISMATCH": "This action does not apply to this entry kind",
  "apiError.MENU_ORDER_INCOMPLETE": "The order list must contain every menu image exactly once",
  "apiError.CATEGORY_IN_USE": "This category cannot be deleted while stores are assigned to it — hide it instead",
  "apiError.CATEGORY_ORDER_INCOMPLETE": "The new order must list every current category exactly once",
  "apiError.VEHICLE_TYPE_IN_USE": "Drivers or stores still use this vehicle type — hide it instead of deleting it",
  "apiError.VEHICLE_TYPE_CODE_TAKEN": "This vehicle type code is already in use",
  "apiError.VEHICLE_TYPE_ORDER_INCOMPLETE": "The order must list every vehicle type exactly once",
  "apiError.UNKNOWN": "Something went wrong — please try again.",

  // ----------------------------------------------------------------
  // M6-19 — §6.6 override queue, notifications admin, audit browse,  
  // geofence radii, merchant directory visibility                    
  // ----------------------------------------------------------------

  "common.refresh": "Refresh",
  "common.loading": "Loading…",
  "common.view": "View",
  "common.save": "Save",

  "nav.geofenceOverrides": "Geofence Overrides",
  "screen.geofenceOverrides.title": "Geofence Overrides",

  // §6.6 override approval queue
  "overrides.title": "Geofence Overrides",
  "overrides.pendingCount": "{count} request(s) waiting for a decision.",
  "overrides.refreshedAt": "Updated {time}",
  "overrides.refreshFailed": "Could not refresh the queue — showing the last successful load.",
  "overrides.denied.title": "You cannot review override requests",
  "overrides.filter.status": "Request state",
  "overrides.filter.driver": "Driver",
  "overrides.status.PENDING": "Waiting",
  "overrides.status.APPROVED": "Approved",
  "overrides.status.DENIED": "Denied",
  "overrides.status.CONSUMED": "Used",
  "overrides.status.EXPIRED": "Expired",
  "overrides.transition.PICKUP": "Pickup",
  "overrides.transition.DELIVERY": "Delivery",
  "overrides.age.minutes": "{minutes} min",
  "overrides.age.hours": "{hours} h {minutes} min",
  "overrides.table.label": "Geofence override requests",
  "overrides.table.waiting": "Waiting",
  "overrides.table.order": "Order",
  "overrides.table.driver": "Driver",
  "overrides.table.transition": "Blocked step",
  "overrides.table.distance": "Distance vs allowed",
  "overrides.table.reason": "Driver's reason",
  "overrides.table.status": "State",
  "overrides.distance.value": "{distance} m of {allowed} m",
  "overrides.action.approve": "Approve",
  "overrides.action.deny": "Deny",
  "overrides.empty.title": "No override requests",
  "overrides.empty.pending.title": "No driver is waiting",
  "overrides.export.distanceM": "Distance (m)",
  "overrides.export.allowedM": "Allowed (m)",
  "overrides.export.requestedAt": "Requested at",
  "overrides.export.decidedAt": "Decided at",
  "overrides.export.decisionNote": "Decision note",
  "overrides.toast.approved": "Exception approved for order {order}.",
  "overrides.toast.denied": "Exception denied for order {order}.",
  "overrides.toast.alreadyDecided": "This request was already decided — the queue has been refreshed.",
  "overrides.decide.approve.title": "Approve this geofence exception?",
  "overrides.decide.deny.title": "Deny this geofence exception?",
  "overrides.decide.subject": "Order {order} — blocked at {transition}.",
  "overrides.decide.reasonLabel": "Decision reason",
  "overrides.decide.reasonRequired": "A decision reason is required.",

  // Notification templates (§14.8)
  "notifications.title": "Notification Templates",
  "notifications.count": "{count} template variant(s)",
  "notifications.denied.title": "You cannot view notification templates",
  "notifications.filter.channel": "Channel",
  "notifications.search.label": "Search templates",
  "notifications.search.placeholder": "Template key or message text",
  "notifications.channel.PUSH": "Push",
  "notifications.channel.SMS": "SMS",
  "notifications.channel.EMAIL": "Email",
  "notifications.channel.IN_APP": "In-app",
  "notifications.table.label": "Notification template variants",
  "notifications.table.key": "Template",
  "notifications.table.channel": "Channel",
  "notifications.table.body": "Message",
  "notifications.table.class": "Class",
  "notifications.table.updated": "Last edited",
  "notifications.badge.critical": "Critical",
  "notifications.badge.inactive": "Inactive",
  "notifications.empty.title": "No templates match",
  "notifications.action.edit": "Edit content",
  "notifications.action.test": "Test send",
  "notifications.toast.saved": "Template {key} updated.",
  "notifications.edit.title": "Edit content —",
  "notifications.edit.bodyRequired": "The message text is required.",
  "notifications.edit.unknownPlaceholder": "Unknown placeholder: {tokens}",
  "notifications.field.titleAr": "Title (Arabic)",
  "notifications.field.titleEn": "Title (English)",
  "notifications.field.bodyAr": "Message (Arabic)",
  "notifications.field.bodyEn": "Message (English)",
  "notifications.field.critical": "Notification class",
  "notifications.field.deepLink": "Deep link",
  "notifications.field.active": "Template is active",
  "notifications.critical.yes": "Critical",
  "notifications.critical.no": "Standard",
  "notifications.test.title": "Test send —",
  "notifications.test.send": "Send test",
  "notifications.test.recipient": "Recipient",
  "notifications.test.recipientRequired": "A recipient is required.",
  "notifications.test.queued": "Queued for delivery — notification ID:",

  // Global audit browse (audit.yaml v1.0.0)
  "auditBrowse.title": "Audit Log",
  "auditBrowse.count": "{count} entry(ies)",
  "auditBrowse.list.label": "Audit entries",
  "auditBrowse.filter.subjectType": "Entity type",
  "auditBrowse.filter.subjectId": "Entity ID",
  "auditBrowse.filter.actionPrefix": "Action starts with",
  "auditBrowse.filter.actorType": "Actor type",
  "auditBrowse.filter.actorUser": "Actor user ID",
  "auditBrowse.filter.from": "From",
  "auditBrowse.filter.to": "To",
  "auditBrowse.actorType.USER": "Person",
  "auditBrowse.actorType.SYSTEM": "System",
  "auditBrowse.actorType.API_CLIENT": "API client",
  "auditBrowse.rangeInvalid": "The end date is before the start date — the date filter is not applied.",
  "auditBrowse.emptyFiltered.title": "No entries match these filters",

  // §6.6 geofence radii per scope (geo.yaml v1.2.0)
  "geofence.title": "Geofence Radii",
  "geofence.backToZones": "Back to zones",
  "geofence.denied.title": "You cannot view geofence radii",
  "geofence.table.label": "Geofence radius overrides",
  "geofence.table.scope": "Scope",
  "geofence.table.scopeType": "Type",
  "geofence.table.pickup": "Pickup radius",
  "geofence.table.delivery": "Delivery radius",
  "geofence.scope.ZONE": "Zone",
  "geofence.scope.MERCHANT": "Merchant",
  "geofence.inherited": "Inherits global",
  "geofence.metres": "{value} m",
  "geofence.unit.metres": "m",
  "geofence.filter.scopeType": "Scope type",
  "geofence.add.scopeType": "Add override for",
  "geofence.add.scope": "Scope",
  "geofence.add.pick": "Select…",
  "geofence.add.action": "Set radii",
  "geofence.empty.title": "No overrides yet",
  "geofence.editor.title": "Geofence radii — {scope}",
  "geofence.field.pickup": "Pickup radius",
  "geofence.field.delivery": "Delivery radius",
  "geofence.field.rangeError": "Enter a whole number between 1 and 100000 metres.",
  "geofence.field.buffer": "GPS tolerance",
  "geofence.field.toggles": "Checks enabled",
  "geofence.toggles.value": "Pickup: {pickup} · Delivery: {delivery}",
  "geofence.toggle.on": "on",
  "geofence.toggle.off": "off",
  "geofence.action.remove": "Remove override",
  "geofence.confirm.remove.title": "Remove this override?",
  "geofence.confirm.remove.description": "{scope} returns to the global geofence defaults immediately.",
  "geofence.toast.saved": "Geofence radii saved for {scope}.",
  "geofence.toast.removed": "Override removed — {scope} now inherits the global defaults.",

  // §3.1/§3.2 order list filters (orders.yaml v1.2.0)
  "orders.filter.source": "Source",
  "orders.filter.type": "Type",

  // §7/§7.1 customer-app directory visibility (merchants.yaml v1.2.0)
  "merchants.table.visibility": "Customer app",
  "merchants.filter.visibility": "Customer app",
  "merchants.visibility.label": "Customer app directory",
  "merchants.visibility.visible": "Visible",
  "merchants.visibility.hidden": "Hidden",

  /* ---------------- M7-07 admin screens tail ---------------- */

  // User picker over identity.yaml v1.2.0 GET /admin/users (M6-19 A4 closed)
  "userPicker.search.label": "Search users by name or phone",
  "userPicker.search.placeholder": "Name or phone number",
  "userPicker.role": "Role",
  "userPicker.role.admin": "Admin",
  "userPicker.role.merchant": "Merchant",
  "userPicker.role.driver": "Driver",
  "userPicker.role.customer": "Customer",
  "userPicker.results": "Matching users",
  "userPicker.count": "{count} users",
  "userPicker.count.one": "{count} user",
  "userPicker.truncated": "Showing {shown} of {total} — narrow the search to see the rest",
  "userPicker.empty.title": "No matching users",
  "userPicker.failed": "Could not load the user directory.",
  "userPicker.change": "Change",

  // Orders dispatch could not place (M7-74 — orders.yaml PENDING page joined
  // with the dispatch.order.unassigned audit trail)
  "unassigned.title": "Orders Without a Driver",
  "unassigned.count": "{count} waiting",
  "unassigned.table.label": "Orders waiting for a driver",
  "unassigned.table.waiting": "Waiting",
  "unassigned.table.order": "Order",
  "unassigned.table.vehicle": "Vehicle needed",
  "unassigned.table.reason": "Why",
  "unassigned.reason.NO_ELIGIBLE_ONLINE_DRIVERS": "No eligible driver online",
  "unassigned.reason.NO_VEHICLE_TYPE_MATCH": "No driver with the required vehicle",
  "unassigned.reason.MAX_WAVES_EXHAUSTED": "Offer rounds ran out",
  "unassigned.reason.wave": "Round {wave}",
  "unassigned.reason.none": "Still being offered",
  "unassigned.reason.noAccess": "Needs audit access",
  "unassigned.action.assign": "Assign a driver",
  "unassigned.action.openOrder": "Open order",
  "unassigned.empty.title": "Every order has a driver",
  "unassigned.denied.title": "You cannot view orders",
  // M7-82 — the contracted retry (dispatch.yaml v1.3.0)
  "unassigned.reason.AUTOMATIC_DISPATCH_DISABLED": "Automatic dispatch is off",
  "unassigned.reason.NO_VEHICLE_TYPE_MATCH.named": "No {vehicle} driver is online",
  "unassigned.reason.offered": "Offered to {count} drivers",
  "unassigned.reason.offered.one": "Offered to {count} driver",
  "unassigned.reason.justNow": "Just now",
  "unassigned.action.redispatch": "Re-dispatch",
  "unassigned.redispatch.confirmTitle": "Re-dispatch this order?",
  "unassigned.redispatch.offered": "Order {orderNo} was offered to {count} drivers",
  "unassigned.redispatch.offered.one": "Order {orderNo} was offered to {count} driver",
  "unassigned.redispatch.stuck": "Order {orderNo} was re-dispatched, but no driver was offered it",
  "unassigned.redispatch.inProgress": "Order {orderNo} is already being offered to drivers — the queue was refreshed",
  "unassigned.redispatch.driverBound": "A driver has already taken order {orderNo} — the queue was refreshed",
  "unassigned.redispatch.statusChanged": "Order {orderNo} is no longer waiting for a driver ({status}) — the queue was refreshed",
  "unassigned.redispatch.noLongerWaiting": "Order {orderNo} is no longer waiting for a driver — the queue was refreshed",
  "unassigned.redispatch.blocked.driverBound": "A driver is already bound to this order",
  "unassigned.redispatch.blocked.status": "This order is no longer waiting for a driver",

  // Dispatch assignments browse (dispatch.yaml GET /admin/dispatch/assignments)
  "assignments.title": "Assignments",
  "assignments.count": "{count} assignments",
  "assignments.count.one": "{count} assignment",
  "assignments.table.label": "Driver assignments",
  "assignments.table.order": "Order",
  "assignments.table.driver": "Driver",
  "assignments.table.via": "Assigned via",
  "assignments.table.status": "Assignment",
  "assignments.table.orderStatus": "Order status",
  "assignments.table.assignedAt": "Assigned",
  "assignments.table.released": "Released",
  "assignments.filter.status": "Assignment status",
  "assignments.filter.driver": "Driver",
  "assignments.filter.order": "Order ID",
  "assignments.filter.orderPlaceholder": "Paste an order ULID",
  "assignments.filter.orderInvalid": "Not filtering yet — an order ID is 26 characters.",
  "assignments.status.ACTIVE": "Active",
  "assignments.status.COMPLETED": "Completed",
  "assignments.status.RELEASED": "Released",
  "assignments.via.OFFER_ACCEPT": "Driver accepted the offer",
  "assignments.via.ADMIN_ASSIGN": "Assigned by an admin",
  "assignments.release.ADMIN_REASSIGN": "Reassigned by an admin",
  "assignments.release.ORDER_CANCELLED": "Order cancelled",
  "assignments.empty.title": "No assignments yet",
  "assignments.emptyFiltered.title": "No assignments match these filters",
  "assignments.action.openOrder": "Open order",
  "assignments.action.openDriver": "Open driver",
  "assignments.denied.title": "You cannot view assignments",

  // §13 dispatch rules (dispatch.yaml GET/PATCH /admin/dispatch/rules)
  "dispatchRules.title": "Dispatch Rules",
  "dispatchRules.active": "Active",
  "dispatchRules.inactive": "Inactive",
  "dispatchRules.activate": "Make active",
  "dispatchRules.edit": "Edit parameters",
  "dispatchRules.updated": "Updated {when}",
  "dispatchRules.noConfig": "This strategy takes no parameters.",
  "dispatchRules.confirm.activate.title": "Make “{rule}” the active dispatch rule?",
  "dispatchRules.confirm.activate.confirm": "Make active",
  "dispatchRules.dialog.title": "Parameters — {rule}",
  "dispatchRules.field.radius_km": "Broadcast radius (km)",
  "dispatchRules.field.max_drivers": "Maximum drivers per wave",
  "dispatchRules.field.wave_count": "Number of waves",
  "dispatchRules.field.wave_interval_seconds": "Seconds between waves",
  "dispatchRules.field.exclusive": "One active order per driver",
  "dispatchRules.field.max_waves": "Maximum offer waves",
  "dispatchRules.field.eligible_zone_scope": "Eligible driver zones",
  "dispatchRules.value.yes": "Yes",
  "dispatchRules.value.no": "No",
  "dispatchRules.value.zoneScope.ALL": "All zones",
  "dispatchRules.field.invalid": "Enter a whole number of 0 or more.",
  "dispatchRules.toast.saved": "Parameters saved for {rule}.",
  "dispatchRules.toast.activated": "{rule} is now the active dispatch rule.",
  "dispatchRules.empty.title": "No dispatch rules registered",
  "dispatchRules.denied.title": "You cannot manage dispatch rules",

  // Delivery log (notifications.yaml v1.1.0 GET /admin/notifications/deliveries)
  "notifications.tabs.label": "Notification views",
  "notifications.tabs.templates": "Templates",
  "notifications.tabs.deliveries": "Delivery log",
  "notifications.tabs.send": "Send message",

  // ---- Custom send (M7-37d ← M7-36a addendum, owner-decisions #6) ----
  "customSend.audience.legend": "Audience",
  "customSend.audience.group": "A group",
  "customSend.audience.user": "One person",
  "customSend.field.group": "Group",
  "customSend.group.ALL": "Everyone",
  "customSend.group.CUSTOMERS": "All customers",
  "customSend.group.MERCHANTS": "All merchants",
  "customSend.group.DRIVERS": "All drivers",
  "customSend.field.recipient": "Recipient",
  "customSend.submit": "Send message",
  "customSend.confirm.title": "Send this message?",
  "customSend.confirm.description": "This goes to {audience} for real and cannot be recalled.",
  "customSend.confirm.send": "Send",
  "customSend.sent.title": "Message queued",
  "customSend.sent.details": "{count} recipient(s).",
  "customSend.sent.groupDetails": "The group is being resolved and sent in the background.",

  "deliveries.count": "{count} attempts",
  "deliveries.count.one": "{count} attempt",
  "deliveries.table.label": "Delivery attempts",
  "deliveries.table.template": "Template",
  "deliveries.table.recipient": "Recipient",
  "deliveries.table.channel": "Channel",
  "deliveries.table.status": "Status",
  "deliveries.table.attempt": "Attempt",
  "deliveries.table.provider": "Provider",
  "deliveries.table.created": "Attempted",
  "deliveries.attempt.value": "{attempt} of {max}",
  "deliveries.status.QUEUED": "Queued",
  "deliveries.status.SENDING": "Sending",
  "deliveries.status.SENT": "Sent",
  "deliveries.status.DELIVERED": "Delivered",
  "deliveries.status.FAILED": "Failed",
  "deliveries.status.SKIPPED": "Skipped",
  "deliveries.skip.PREFERENCE_DISABLED": "Recipient disabled this channel",
  "deliveries.skip.CHANNEL_UNCONFIGURED": "Channel not configured",
  "deliveries.skip.NO_PUSH_TOKEN": "No push token registered",
  "deliveries.skip.PROVIDER_UNSUPPORTED": "Not supported by the provider",
  "deliveries.retryAt": "Retry at {time}",
  "deliveries.filter.status": "Status",
  "deliveries.filter.channel": "Channel",
  "deliveries.filter.template": "Template",
  "deliveries.filter.recipient": "Recipient",
  "deliveries.filter.from": "From",
  "deliveries.filter.to": "To",
  "deliveries.filter.recipientAll": "Any recipient",
  "deliveries.empty.title": "No delivery attempts yet",
  "deliveries.emptyFiltered.title": "No attempts match these filters",
  "deliveries.denied.title": "You cannot view the delivery log",

  // ---- §7.2 engagement reports (M7-38) ----
  "engagement.title": "Engagement Reports",
  "engagement.denied.title": "You cannot view engagement reports",
  "engagement.groupBy.label": "Group by",
  "engagement.groupBy.merchant": "Stores",
  "engagement.groupBy.category": "Categories",
  "engagement.groupBy.zone": "Zones",
  "engagement.window.label": "Window",
  "engagement.window.day": "Today",
  "engagement.window.week": "Last 7 days",
  "engagement.window.month": "Last 30 days",
  "engagement.window.custom": "Custom",
  "engagement.rangeInvalid": "The end date is before the start date — pick a valid range.",
  "engagement.event.filter": "Event",
  "engagement.event.all": "All events",
  "engagement.event.STORE_PROFILE_VIEW": "Store visits",
  "engagement.event.CALL_TAP": "Call taps",
  "engagement.event.WHATSAPP_TAP": "WhatsApp taps",
  "engagement.table.label": "Engagement by group",
  "engagement.table.group.merchant": "Store",
  "engagement.table.group.category": "Category",
  "engagement.table.group.zone": "Zone",
  "engagement.table.total": "Total",
  "engagement.table.share": "Share",
  "engagement.table.trend": "Trend",
  "engagement.trend.new": "New",
  "engagement.trend.flat": "—",
  "engagement.trend.sr": "{pct} versus the previous window",
  "engagement.trend.upSr": "up",
  "engagement.trend.downSr": "down",
  "engagement.empty.title": "No engagement in this window",
  "engagement.empty.widen": "Show last 30 days",

  // Proof of delivery (orders.yaml v1.3.0 — admin read)
  "orders.pod.title": "Proof of delivery",
  "orders.pod.requirement": "Required proof",
  "orders.pod.mode.NONE": "None",
  "orders.pod.mode.PHOTO": "Photo",
  "orders.pod.mode.SIGNATURE": "Signature",
  "orders.pod.mode.OTP": "Customer code (OTP)",
  "orders.pod.none": "This order needs no delivery proof.",
  "orders.pod.pending": "No proof submitted yet.",
  "orders.pod.submittedBy": "Submitted by",
  "orders.pod.submittedAt": "Submitted",
  "orders.pod.otpVerified": "Customer code verified",
  "orders.pod.otpVerifiedAt": "Verified {time}",
  "orders.pod.otpNotVerified": "Code issued but not verified",
  "orders.pod.viewFile": "Open proof image",
  "orders.pod.failed": "Could not load the proof record.",

  /* ---------------------------------------------------------------- */
  /* M7-23 — driver management, RBAC, grouped settings, machine ids    */
  /* ---------------------------------------------------------------- */

  "nav.roles": "Roles & Permissions",

  "drivers.new": "New driver",
  "drivers.create.title": "New driver",
  "drivers.create.section.account": "Driver account",
  "drivers.create.name": "Driver name",
  "drivers.create.nameRequired": "The driver's name is required.",
  "drivers.create.phone": "Driver phone",
  "drivers.create.linkToggle": "Link an existing account",
  "drivers.create.phoneTakenTitle": "Phone already registered",
  "drivers.create.phoneTakenAction": "Link the existing account",
  "drivers.create.success.title": "Driver created",
  "drivers.create.success.open": "Open driver profile",
  "drivers.create.section.profile": "Profile",
  "drivers.create.section.vehicle": "Vehicle",
  "drivers.create.submit": "Create driver",
  "drivers.form.phone": "Account phone",
  "drivers.form.nationalId": "National ID number",
  "drivers.form.birthDate": "Birth date",
  "drivers.form.notes": "Internal notes",
  "drivers.form.vehicleType": "Vehicle type",
  "drivers.form.plateNo": "Plate number",
  "drivers.form.make": "Make",
  "drivers.form.model": "Model",
  "drivers.form.modelYear": "Year",
  "drivers.form.color": "Colour",
  "drivers.toast.created": "Driver profile created",
  "drivers.toast.updated": "Driver updated",
  "drivers.edit.open": "Edit driver",
  "drivers.action.openWallet": "Wallet",
  "drivers.edit.title": "Edit driver",
  "drivers.edit.status": "Lifecycle status",
  "drivers.edit.blockReason": "Block",
  "drivers.edit.blockNone": "Not blocked",
  "drivers.edit.reservedReason": "This block reason is reserved for the wallet system.",
  "drivers.tabs.documents": "Documents",
  "drivers.tabs.vehicles": "Vehicle",
  "drivers.tabs.plan": "Plan & wallet",
  "drivers.detail.openWallet": "Open driver wallet",
  "drivers.docType.NATIONAL_ID": "National ID",
  "drivers.docType.RESIDENCE_CARD": "Residence card",
  "drivers.docType.DRIVING_LICENSE": "Driving licence",
  "drivers.docType.VEHICLE_REGISTRATION": "Vehicle registration",
  "drivers.docType.INSURANCE": "Insurance",
  "drivers.docType.OTHER": "Other document",
  "drivers.review.PENDING": "Pending review",
  "drivers.review.APPROVED": "Approved",
  "drivers.review.REJECTED": "Rejected",
  "drivers.documents.table.label": "Driver documents",
  "drivers.documents.table.type": "Document",
  "drivers.documents.table.expires": "Expires",
  "drivers.documents.table.status": "Review",
  "drivers.documents.table.reviewed": "Reviewed",
  "drivers.documents.filter.status": "Review status",
  "drivers.documents.view": "Open file",
  "drivers.documents.noFile": "No file",
  "drivers.documents.approve": "Approve",
  "drivers.documents.reject": "Reject",
  "drivers.documents.review.title": "Review document",
  "drivers.documents.review.decision": "Decision",
  "drivers.documents.review.reason": "Rejection reason",
  "drivers.documents.review.reasonRequired": "A reason is required when rejecting.",
  "drivers.documents.toast.approved": "Document approved",
  "drivers.documents.toast.rejected": "Document rejected",
  "drivers.documents.empty.title": "No documents yet",
  "drivers.documents.denied.title": "You cannot view driver documents",
  "drivers.vehicle.register": "Register vehicle",
  "drivers.vehicle.replace": "Replace vehicle",
  "drivers.vehicle.details": "Details",
  "drivers.vehicle.noDetails": "No details recorded",
  "drivers.vehicle.toast.saved": "Vehicle saved",
  "drivers.vehicle.empty.title": "No vehicle registered",
  "drivers.plan.title": "Driver plan",
  "drivers.plan.type": "Plan",
  "drivers.plan.dailyFee": "Daily fee",
  "drivers.plan.commissionPercent": "Commission",
  "drivers.plan.effectiveFrom": "Effective from",
  "drivers.plan.none": "No plan set — this driver is not being charged.",
  "drivers.plan.change": "Change plan",
  "drivers.plan.set": "Set plan",
  "drivers.plan.modalTitle": "Driver plan",
  "drivers.plan.feeRequired": "Enter the daily fee as an amount, e.g. 5000.00.",
  "drivers.plan.percentRequired": "Enter the commission as a percentage with two decimals, e.g. 15.00.",
  "drivers.plan.toast.saved": "Driver plan updated",
  "drivers.plan.denied": "Changing the plan needs the driver-plan manage permission.",
  "drivers.plan.loadFailed": "Could not load the driver plan.",

  "wallets.plan.manageOnDriver": "Change plan on the driver page",

  "roles.title": "Roles & Permissions",
  "roles.tabs.label": "RBAC sections",
  "roles.tabs.roles": "Roles",
  "roles.tabs.users": "User roles",
  "roles.count": "{count} roles",
  "roles.count.one": "{count} role",
  "roles.search.label": "Search roles",
  "roles.search.placeholder": "Search by key or name…",
  "roles.table.label": "Roles",
  "roles.table.role": "Role",
  "roles.table.type": "Type",
  "roles.table.permissions": "Permissions",
  "roles.table.created": "Created",
  "roles.type.system": "System",
  "roles.type.custom": "Custom",
  "roles.new": "New role",
  "roles.create.title": "New role",
  "roles.edit.title": "Edit role",
  "roles.form.key": "Role key",
  "roles.form.keyInvalid": "Use lowercase letters and underscores only (2–64 characters).",
  "roles.form.nameAr": "Name (Arabic)",
  "roles.form.nameEn": "Name (English)",
  "roles.form.description": "Description",
  "roles.delete": "Delete role",
  "roles.delete.title": "Delete this role?",
  "roles.delete.description": "This cannot be undone.",
  "roles.toast.created": "Role created",
  "roles.toast.updated": "Role saved",
  "roles.toast.deleted": "Role deleted",
  "roles.empty.title": "No roles match",
  "roles.denied.title": "You cannot manage roles",
  "roles.detail.tabs.label": "Role sections",
  "roles.detail.tabs.details": "Details",
  "roles.detail.tabs.permissions": "Permissions",
  "roles.permissions.selected": "{count} of {total} selected",
  "roles.permissions.save": "Save permissions",
  "roles.permissions.search": "Filter permissions",
  "roles.permissions.searchPlaceholder": "Search by key or description…",
  "roles.permissions.selectDomain": "Select every permission in this domain",
  "roles.permissions.clearDomain": "Clear every permission in this domain",
  "roles.permissions.empty": "No permission matches your filter.",
  "roles.permissions.confirmTitle": "Replace this role's permissions?",
  "roles.permissions.confirmDescription": "{count} permissions become the role's complete set immediately.",
  "roles.permissions.toast.saved": "Permissions saved",
  "roles.permissions.denied": "Editing permissions needs the identity role manage permission.",

  "userRoles.title": "User Roles",
  "userRoles.pick": "User",
  "userRoles.current": "Assigned roles",
  "userRoles.none": "This user holds no roles.",
  "userRoles.noUser.title": "Pick a user",
  "userRoles.add": "Add role",
  "userRoles.remove": "Remove",
  "userRoles.role": "Role",
  "userRoles.scope": "Scope",
  "userRoles.scopeType.GLOBAL": "Whole platform",
  "userRoles.scopeType.MERCHANT": "One merchant",
  "userRoles.scopeType.BRANCH": "One branch",
  "userRoles.scopeId": "Scope id",
  "userRoles.scopeIdRequired": "A scope id is required for a scoped role.",
  "userRoles.duplicate": "That role and scope is already assigned.",
  "userRoles.save": "Save assignments",
  "userRoles.table.label": "Role assignments",
  "userRoles.toast.saved": "Role assignments saved",
  "userRoles.denied": "Assigning roles needs the identity user assign-roles permission.",
  "userRoles.selfConfirm.title": "Remove a role from your own account?",
  "userRoles.selfConfirm.description": "You may lose access to the control panel.",

  "settings.tabs.label": "Setting groups",
  "settings.group.api": "API access",
  "settings.group.auth": "Authentication",
  "settings.group.authz": "Authorization",
  "settings.group.customer_requests": "Customer requests",
  "settings.group.dispatch": "Dispatch",
  "settings.group.driver_plans": "Driver plans",
  "settings.group.drivers": "Drivers",
  "settings.group.engagement": "Engagement",
  "settings.group.flags": "Feature flags",
  "settings.group.geo": "Geo & distance",
  "settings.group.geofence": "Geofence",
  "settings.group.marketing": "Marketing",
  "settings.group.customer_app": "Customer app",
  "settings.group.notifications": "Notifications",
  "settings.group.orders": "Orders & window",
  "settings.group.otp": "OTP",
  "settings.group.platform": "Platform",
  "settings.group.pricing": "Delivery pricing",
  "settings.group.promotions": "Promotions",
  "settings.group.settlement": "Settlement",
  "settings.group.support": "Support",
  "settings.group.tracking": "Tracking",
  "settings.group.wallet": "Wallets",
  "settings.groupCount": "{count} settings",
  "settings.groupCount.one": "{count} setting",
  "settings.emptyGroup": "No setting in this group matches your search.",

  "audit.verb.generic": "Recorded",
  "audit.verb.assigned": "Assigned",
  "audit.verb.approved": "Approved",
  "audit.verb.rejected": "Rejected",
  "customers.edit.open": "Edit customer",
  "customers.edit.title": "Edit customer",
  "customers.edit.saved": "Customer updated",
  "customers.form.name": "Name",

  "drivers.earnings.title": "Earnings",
  "drivers.earnings.today": "Today",
  "drivers.earnings.week": "Last 7 days",
  "drivers.earnings.delivered": "Delivered",
  "drivers.earnings.feesKept": "Fees kept",
  "drivers.earnings.cofund": "Discount compensation",
  "drivers.earnings.deductions": "Deductions",
  "drivers.earnings.net": "Net",

} as const;

export type MessageKey = keyof typeof en;
export type MessageCatalog = Record<MessageKey, string>;

export const ar: MessageCatalog = {
  "app.name": "لوحة التحكم",
  "app.skipToContent": "الانتقال إلى المحتوى الرئيسي",

  "nav.group.overview": "نظرة عامة",
  "nav.group.operations": "العمليات",
  "nav.group.directory": "الدليل",
  "nav.group.finance": "المالية",
  "nav.group.growth": "النمو",
  "nav.group.system": "النظام",

  "nav.dashboard": "الرئيسية",
  "nav.orders": "الطلبات",
  "nav.dispatchMap": "خريطة التوزيع",
  "nav.unassignedOrders": "بلا سائق",
  "nav.assignments": "الإسنادات",
  "nav.dispatchRules": "قواعد التوزيع",
  "nav.merchants": "التجار",
  "nav.merchantApplications": "طلبات الانضمام",
  "nav.storeCategories": "فئات المتاجر",
  "nav.drivers": "السائقون",
  "nav.vehicleTypes": "أنواع المركبات",
  "nav.customers": "العملاء",
  "nav.zones": "المناطق",
  "nav.wallets": "المحافظ",
  "nav.settlement": "التسويات",
  "nav.promotions": "العروض",
  "nav.subscriptions": "الاشتراكات",
  "nav.notifications": "الإشعارات",
  "nav.settings": "الإعدادات",
  "nav.featureFlags": "مفاتيح الميزات",
  "nav.audit": "سجل التدقيق",
  "nav.engagement": "التفاعل",
  "nav.ads": "الإعلانات",
  "nav.homeSections": "أقسام الرئيسية",
  "nav.collections": "المجموعات",
  "nav.categoryLayout": "تخطيط الأقسام",
  "nav.homeLayout": "تخطيط الرئيسية",
  "nav.popups": "الإعلانات المنبثقة",
  "nav.privacyPolicy": "سياسة الخصوصية",

  "nav.collapse": "طيّ القائمة",
  "nav.expand": "توسيع القائمة",

  "breadcrumbs.label": "مسار التنقل",

  "topbar.language": "English",
  "topbar.languageSwitchTo": "تغيير اللغة إلى الإنجليزية",
  "topbar.theme.light": "الوضع الفاتح",
  "topbar.theme.dark": "الوضع الداكن",
  "topbar.theme.system": "اتباع وضع النظام",
  "topbar.userMenu": "قائمة الحساب",
  "topbar.signedInAs": "مسجّل الدخول باسم",
  "topbar.logout": "تسجيل الخروج",
  "topbar.loggingOut": "جارٍ تسجيل الخروج…",

  "login.title": "تسجيل الدخول",
  "login.phone.label": "رقم الهاتف",
  "login.phone.invalid": "أدخل رقم هاتف دولي صالح (+ ثم 8–15 رقماً).",
  "login.password.label": "كلمة المرور",
  "login.password.tooShort": "كلمة المرور يجب ألا تقل عن 8 أحرف.",
  "login.password.show": "إظهار كلمة المرور",
  "login.password.hide": "إخفاء كلمة المرور",
  "login.submit": "تسجيل الدخول",
  "login.submitting": "جارٍ تسجيل الدخول…",
  "login.lockout": "محاولات كثيرة — تم قفل تسجيل الدخول مؤقتاً. حاول مجدداً بعد {seconds} ثانية.",

  "session.restoring": "جارٍ استعادة الجلسة…",
  "session.expired": "انتهت صلاحية الجلسة — الرجاء تسجيل الدخول مجدداً.",

  "screen.dashboard.title": "الرئيسية",
  "screen.orders.title": "الطلبات",
  "screen.dispatchMap.title": "خريطة التوزيع",
  "screen.merchants.title": "التجار",
  "screen.storeCategories.title": "فئات المتاجر",
  "screen.drivers.title": "السائقون",
  "screen.vehicleTypes.title": "أنواع المركبات",
  "screen.customers.title": "العملاء",
  "screen.zones.title": "المناطق",
  "screen.wallets.title": "المحافظ",
  "screen.settlement.title": "التسويات",
  "screen.notifications.title": "الإشعارات",
  "screen.promotions.title": "العروض",
  "screen.ads.title": "الإعلانات",
  "screen.homeSections.title": "أقسام الرئيسية",
  "screen.collections.title": "المجموعات",
  "screen.categoryLayout.title": "تخطيط الأقسام",
  "screen.homeLayout.title": "تخطيط الرئيسية",
  "screen.popups.title": "الإعلانات المنبثقة",
  "screen.privacyPolicy.title": "سياسة الخصوصية",
  "screen.subscriptions.title": "الاشتراكات",
  "screen.settings.title": "الإعدادات",
  "screen.featureFlags.title": "مفاتيح الميزات",
  "screen.audit.title": "سجل التدقيق",

  "notFound.title": "الصفحة غير موجودة",
  "notFound.backHome": "العودة إلى الرئيسية",

  "error.offline": "تعذّر الوصول إلى الخادم — تحقق من الاتصال وحاول مجدداً.",
  "common.retry": "إعادة المحاولة",
  "common.cancel": "إلغاء",
  "common.close": "إغلاق",
  "common.edit": "تعديل",
  "common.actions": "إجراءات",
  "common.saveChanges": "حفظ التغييرات",
  "common.saveFailed": "فشل الحفظ — حاول مرة أخرى.",
  "common.backToList": "العودة إلى القائمة",
  "common.clearFilters": "مسح الفلاتر",
  "common.filterAll": "الكل",

  "form.required": "هذا الحقل مطلوب.",
  "form.wholeMoneyOnly": "المبالغ بالدينار الصحيح — بلا كسور.",
  "merchants.form.vehicleType": "نوع مركبة التوصيل",
  "merchants.form.vehicleTypePlaceholder": "اختر نوع المركبة",
  "merchants.table.vehicleType": "المركبة",
  "orders.table.vehicleType": "المركبة",
  "orders.detail.vehicleType": "نوع المركبة المطلوب",
  "merchants.detail.vehicleType": "نوع مركبة التوصيل",
  "applications.review.vehicleType": "نوع مركبة التوصيل",
  "form.invalidPhone": "أدخل رقم هاتف دولي صالح (+ ثم 8–15 رقماً).",

  "pager.range": "{from}–{to} من {total}",
  "pager.perPage": "صفوف لكل صفحة",
  "pager.previous": "الصفحة السابقة",
  "pager.next": "الصفحة التالية",
  "pager.pageOf": "{page} / {pages}",

  "unsaved.title": "تجاهل التغييرات غير المحفوظة؟",
  "unsaved.description": "ستفقد التعديلات غير المحفوظة.",
  "unsaved.leave": "تجاهل وغادِر",
  "unsaved.stay": "متابعة التعديل",

  "week.sat": "السبت",
  "week.sun": "الأحد",
  "week.mon": "الاثنين",
  "week.tue": "الثلاثاء",
  "week.wed": "الأربعاء",
  "week.thu": "الخميس",
  "week.fri": "الجمعة",

  // ---- المناطق (M2-14) ----
  "zones.title": "المناطق",
  "zones.count": "{count} منطقة",
  "zones.count.one": "{count} منطقة",
  "zones.new": "منطقة جديدة",
  "zones.search.label": "البحث في المناطق",
  "zones.search.placeholder": "ابحث بالاسم العربي أو الإنجليزي…",
  "zones.filter.status": "الحالة",
  "zones.filter.all": "الكل",
  "zones.status.active": "مفعّلة",
  "zones.status.disabled": "معطّلة",
  "zones.table.label": "المناطق",
  "zones.table.name": "المنطقة",
  "zones.table.center": "نقطة المركز",
  "zones.table.pricing": "تسعير التوصيل",
  "zones.table.status": "الحالة",
  "zones.table.updated": "آخر تحديث",
  "zones.pricing.custom": "تسعير خاص",
  "zones.pricing.global": "التسعير العام",
  "zones.empty.title": "لا توجد مناطق بعد",
  "zones.emptyFiltered.title": "لا نتائج مطابقة",
  "zones.editor.createTitle": "منطقة جديدة",
  "zones.editor.editTitle": "تعديل المنطقة — {name}",
  "zones.editor.detailsTitle": "بيانات المنطقة",
  "zones.editor.createSubmit": "إنشاء المنطقة",
  "zones.form.nameAr": "الاسم (بالعربية)",
  "zones.form.nameEn": "الاسم (بالإنجليزية)",
  "zones.form.nameRequired": "أدخل اسماً من حرفين على الأقل.",
  "zones.form.active": "المنطقة مفعّلة",
  "zones.form.pinRequired": "أسقط دبوس مركز المنطقة على الخريطة قبل الحفظ.",
  "zones.pin.title": "نقطة المركز",
  "zones.pin.mapLabel": "خريطة مركز المنطقة",
  "zones.pin.instructions": "انقر على الخريطة لإسقاط دبوس المركز، واسحبه للتعديل، أو استخدم مفاتيح الأسهم للضبط الدقيق. اضغط \u2066Shift\u2069 لخطوات أكبر.",
  "zones.pricing.title": "تجاوزات تسعير التوصيل",
  "zones.pricing.baseCost": "التكلفة الأساسية",
  "zones.pricing.costPerKm": "التكلفة لكل كم",
  "zones.pricing.minFee": "الحد الأدنى لأجرة التوصيل",
  "zones.pricing.maxFee": "الحد الأعلى لأجرة التوصيل",
  "zones.pricing.amount": "قيمة {label}",
  "zones.pricing.inherits": "يرث الإعداد العام.",
  "zones.pricing.inheritsValue": "يرث الإعداد العام — حالياً {value}.",
  "zones.pricing.invalidAmount": "أدخل قيمة صالحة (مثال: 2500 أو 2500.50).",
  "zones.pricing.minAboveMax": "لا يمكن أن يتجاوز الحد الأدنى الحدَّ الأعلى.",
  "zones.toast.created": "تم إنشاء المنطقة.",
  "zones.toast.updated": "تم حفظ المنطقة.",
  "zones.toast.deleted": "تم حذف المنطقة.",
  "zones.toast.createdPricingFailed": "أُنشئت المنطقة لكن حفظ تجاوز التسعير فشل — أعد المحاولة أدناه.",
  "zones.disable.title": "تعطيل هذه المنطقة؟",
  "zones.disable.description": "يختفي النطاق فوراً من نماذج الطلب ومن تعيين مواقع المتاجر.",
  "zones.disable.confirm": "تعطيل المنطقة",
  "zones.delete.title": "منطقة الخطر",
  "zones.delete.action": "حذف المنطقة",
  "zones.delete.confirmTitle": "حذف هذه المنطقة؟",
  "zones.delete.confirmDescription": "لا يمكن التراجع عن هذا الإجراء.",
  "zones.export.nameAr": "الاسم (عربي)",
  "zones.export.nameEn": "الاسم (إنجليزي)",
  "zones.export.lat": "خط عرض المركز",
  "zones.export.lng": "خط طول المركز",

  // ---- Dispatch live map (M6-05, §14.4, §6.5) ----
  "dispatch.title": "خريطة التوزيع",
  "dispatch.counts": "{drivers} سائق متصل ({onOrder} على طلب) · {orders} طلب نشط",
  "dispatch.updatedAt": "آخر تحديث {time}",
  "dispatch.layers.label": "طبقات الخريطة",
  "dispatch.layers.drivers": "السائقون",
  "dispatch.truncated": "تُعرض أول 100 طلب لكل حالة — استخدم قائمة الطلبات للتصفية الدقيقة.",
  "dispatch.unplotted": "{count} طلبات ليست على الخريطة بعد (موقع الاستلام قيد التحميل).",
  "dispatch.unplotted.one": "{count} طلب ليس على الخريطة بعد (موقع الاستلام قيد التحميل).",
  "dispatch.map.label": "خريطة التوزيع الحيّة",
  "dispatch.empty.title": "لا حركة حالياً",
  "dispatch.denied.title": "لا صلاحية لعرض الخريطة الحيّة",
  "dispatch.marker.order": "الطلب {orderNo} — {status}",
  "dispatch.marker.driver": "{name} — {status}",
  "dispatch.panel.orderLabel": "تفاصيل الطلب {orderNo}",
  "dispatch.panel.driverLabel": "تفاصيل السائق {name}",
  "dispatch.panel.route": "المسار",
  "dispatch.panel.routeValue": "{from} ← {to}",
  "dispatch.panel.noDropoff": "لا وجهة تسليم بعد",
  "dispatch.panel.driver": "السائق",
  "dispatch.panel.noDriver": "لم يُسند بعد",
  "dispatch.panel.created": "أُنشئ",
  "dispatch.panel.viewOrder": "فتح الطلب",
  "dispatch.panel.viewDriver": "فتح السائق",
  "dispatch.panel.currentOrder": "الطلب الحالي",
  "dispatch.panel.noCurrentOrder": "لا يوجد طلب نشط",
  "dispatch.panel.lastSeen": "الموقع غير محدّث — آخر ظهور {time}.",
  "dispatch.assign.action": "إسناد سائق",
  "dispatch.reassign.action": "إعادة إسناد السائق",
  "dispatch.assign.title": "إسناد سائق للطلب {orderNo}",
  "dispatch.reassign.title": "إعادة إسناد الطلب {orderNo} لسائق آخر",
  "dispatch.assign.driver": "السائق",
  "dispatch.assign.driverPlaceholder": "اختر سائقاً…",
  "dispatch.assign.driverRequired": "اختر سائقاً أولاً.",
  "dispatch.assign.driverBusyOption": "{name} — على طلب",
  "dispatch.assign.driverOfflineOption": "{name} — غير متصل",
  "dispatch.reassign.reason": "السبب",
  "dispatch.assign.reasonRequired": "السبب مطلوب (3 أحرف على الأقل).",
  "dispatch.assign.force": "الإسناد رغم ذلك (تجاوز حاجز السائق المشغول)",
  "dispatch.assign.submit": "إسناد السائق",
  "dispatch.reassign.submit": "إعادة الإسناد",
  "dispatch.assign.done": "تم الإسناد — الطلب {orderNo} مؤكَّد الآن.",
  "dispatch.reassign.done": "تمت إعادة إسناد {orderNo}.",
  "dispatch.assign.confirmTitle": "إسناد هذا السائق؟",
  "dispatch.reassign.confirmTitle": "إعادة إسناد هذا الطلب؟",
  "dispatch.assign.driverVehicleOption": "{name} · {vehicle}",
  "dispatch.assign.mismatchNotice": "هذا الطلب يحتاج {required}، والسائق الذي اخترته يقود {driverVehicle}.",
  "dispatch.assign.mismatchConfirmTitle": "إسناد سائق بمركبة مخالفة؟",

  // ---- التجار (M2-15) ----
  "merchants.title": "التجار",
  "merchants.count": "{count} تاجر",
  "merchants.count.one": "{count} تاجر",
  "merchants.new": "تاجر جديد",
  "merchants.search.label": "البحث في التجار",
  "merchants.search.placeholder": "ابحث بالاسم أو الرقم أو الهاتف…",
  "merchants.filter.status": "الحالة",
  "merchants.filter.zone": "المنطقة",
  "merchants.table.label": "التجار",
  "merchants.table.name": "التاجر",
  "merchants.table.category": "التصنيف",
  "merchants.table.phone": "الهاتف",
  "merchants.table.branches": "الفروع",
  "merchants.table.status": "الحالة",
  "merchants.table.readiness": "الطلبات",
  "merchants.status.DRAFT": "مسودة",
  "merchants.status.ACTIVE": "مفعّل",
  "merchants.status.SUSPENDED": "موقوف",
  "merchants.status.CLOSED": "مغلق",
  "merchants.readiness.ready": "جاهز",
  "merchants.readiness.notReady": "غير جاهز",
  "merchants.readiness.readyLong": "يستطيع هذا التاجر إنشاء الطلبات — الحساب مفعّل وله فرع نشط بموقع مثبّت.",
  "merchants.readiness.notReadyLong": "لا يستطيع هذا التاجر إنشاء الطلبات بعد:",
  "merchants.readiness.gap.notActive": "حساب التاجر غير مفعّل.",
  "merchants.readiness.gap.noBranch": "لا يوجد فرع نشط بموقع مثبّت على الخريطة.",
  "merchants.empty.title": "لا يوجد تجار بعد",
  "merchants.emptyFiltered.title": "لا نتائج مطابقة",
  "merchants.create.title": "تاجر جديد",
  "merchants.create.ownerTitle": "حساب المالك",
  "merchants.create.profileTitle": "ملف المتجر",
  "merchants.create.branchTitle": "الفرع الأول",
  "merchants.create.branchToggle": "إنشاء الفرع الأول الآن",
  "merchants.create.ownerConflictTitle": "المالك يملك متجراً بالفعل",
  "merchants.create.phoneTakenTitle": "رقم الهاتف مسجل مسبقاً",
  "merchants.create.phoneTakenAction": "ربط الحساب الموجود",
  "merchants.create.submit": "إنشاء التاجر",
  // M7-43a — إنشاء حساب المالك ضمن النموذج (الموجة 8).
  "merchants.owner.linkToggle": "ربط حساب موجود",
  "merchants.owner.name": "اسم المالك",
  "merchants.owner.phone": "هاتف المالك",
  "merchants.owner.password": "كلمة المرور",
  "merchants.owner.generate": "توليد",
  "merchants.owner.copy": "نسخ",
  "merchants.owner.passwordCopied": "تم نسخ كلمة المرور",
  "merchants.owner.passwordCopyFailed": "تعذر نسخ كلمة المرور",
  "merchants.owner.nameRequired": "اسم المالك مطلوب.",
  "merchants.owner.phoneInvalid": "أدخل رقم هاتف دولي صالح (+ ثم 8–15 رقماً).",
  "merchants.owner.passwordTooShort": "كلمة المرور 8 أحرف على الأقل.",
  "merchants.owner.linkRequired": "ابحث واختر حساب المالك.",
  "merchants.owner.searchLabel": "البحث في دليل المستخدمين",
  "merchants.owner.passwordTooLong": "كلمة المرور 128 حرفاً على الأكثر.",
  "merchants.owner.title": "حساب المالك",
  "merchants.owner.resetPassword": "إعادة تعيين كلمة المرور",
  "merchants.owner.resetTitle": "إعادة تعيين كلمة مرور المالك",
  "merchants.owner.resetDescription": "سيتم تسجيل خروج المالك من جميع الأجهزة.",
  "merchants.owner.newPassword": "كلمة المرور الجديدة",
  "merchants.owner.regenerate": "توليد من جديد",
  "merchants.owner.resetConfirm": "تعيين",
  "merchants.owner.resetDone": "تم تعيين كلمة المرور",
  "merchants.form.owner": "حساب المالك",
  "merchants.form.ownerInvalid": "أدخل معرّف مستخدم صالحاً من 26 خانة.",
  "merchants.form.nameAr": "الاسم (بالعربية)",
  "merchants.form.nameEn": "الاسم (بالإنجليزية)",
  "merchants.form.category": "التصنيف",
  "merchants.form.categoryPlaceholder": "اختر تصنيفاً…",
  "merchants.form.categoryInactive": "(غير مفعّل)",
  "merchants.form.contactPhone": "هاتف التواصل",
  "merchants.form.whatsappPhone": "رقم واتساب (اختياري)",
  "merchants.form.descriptionAr": "الوصف (بالعربية)",
  "merchants.form.descriptionEn": "الوصف (بالإنجليزية)",
  "merchants.form.status": "الحالة",
  "merchants.form.suspendedReason": "سبب الإيقاف",
  "merchants.form.reasonRequired": "سبب الإيقاف مطلوب.",
  "merchants.social.title": "روابط التواصل",
  "merchants.social.instagram": "إنستغرام",
  "merchants.social.facebook": "فيسبوك",
  "merchants.social.show": "إظهار في التطبيق",
  "merchants.social.invalid.instagram": "أدخل رابط \u2066https\u2069 على \u2066instagram.com\u2069.",
  "merchants.social.invalid.facebook": "أدخل رابط \u2066https\u2069 على \u2066facebook.com\u2069 أو \u2066fb.com\u2069.",
  "merchants.social.urlRequired": "أدخل الرابط لإظهاره في التطبيق.",
  "merchants.prep.title": "وقت التحضير",
  "merchants.prep.from": "من",
  "merchants.prep.to": "إلى",
  "merchants.prep.unit": "د",
  "merchants.prep.range": "أدخل رقماً صحيحاً من \u2066{min}\u2069 إلى \u2066{max}\u2069.",
  "merchants.prep.lte": "يجب ألا يتجاوز «إلى».",
  "merchants.prep.value": "\u2066{min}–{max}\u2069 د",
  "merchants.statusConfirm.suspendTitle": "إيقاف هذا التاجر؟",
  "merchants.statusConfirm.suspendDescription": "يفقد التاجر الوصول ولا يمكنه إنشاء طلبات حتى إعادة التفعيل.",
  "merchants.statusConfirm.closeTitle": "إغلاق هذا التاجر؟",
  "merchants.statusConfirm.closeDescription": "الإغلاق نهائي ولا يمكن التراجع عنه.",
  "merchants.statusConfirm.confirm": "تأكيد التغيير",
  "merchants.toast.created": "تم إنشاء التاجر.",
  "merchants.toast.updated": "تم حفظ التاجر.",
  "merchants.action.openWallet": "المحفظة",
  "merchants.tabs.label": "أقسام التاجر",
  "merchants.tabs.profile": "الملف",
  "merchants.tabs.branches": "الفروع",
  "merchants.tabs.staff": "الموظفون",
  "merchants.tabs.media": "الوسائط",

  // ---- موقع المتجر (الموجة 16 — موقع واحد لكل متجر) ----
  "storeLocation.title": "موقع المتجر",
  "storeLocation.empty.title": "لم يُحدَّد موقع المتجر بعد",
  "storeLocation.pinNow": "تثبيت موقع المتجر",
  "storeLocation.save": "حفظ الموقع",
  "storeLocation.toast.pinned": "تم تثبيت موقع المتجر.",
  "storeLocation.toast.saved": "تم حفظ موقع المتجر.",
  "storeLocation.form.pinRequired": "يجب تثبيت موقع المتجر على الخريطة قبل الحفظ.",
  "storeLocation.form.phone": "هاتف المتجر (اختياري)",
  "storeLocation.form.active": "الموقع نشط",
  "merchants.create.locationToggle": "تثبيت موقع المتجر الآن",
  "merchants.readiness.readyLongSingle": "يستطيع هذا التاجر إنشاء الطلبات — الحساب مفعّل وموقع المتجر مثبّت.",
  "merchants.readiness.gap.noLocation": "لم يُثبَّت موقع المتجر على الخريطة.",

  // ---- وسائط المتجر (الموجة 16) ----
  "media.logo.title": "الشعار",
  "media.cover.title": "صورة الغلاف",
  "media.logo.preview": "معاينة الشعار",
  "media.cover.preview": "معاينة الغلاف",
  "media.empty": "لا توجد صورة",
  "media.choose": "اختيار صورة",
  "media.replace": "استبدال الصورة",
  "media.upload": "رفع",
  "media.remove": "إزالة",
  "media.remove.logoTitle": "إزالة الشعار؟",
  "media.remove.coverTitle": "إزالة صورة الغلاف؟",
  "media.remove.description": "تختفي الصورة فوراً من تطبيق العميل.",
  "media.toast.saved": "تم حفظ الصورة.",
  "media.toast.removed": "تمت إزالة الصورة.",
  "media.error.type": "تُقبل صور \u2066JPEG\u2069 أو \u2066PNG\u2069 أو \u2066WebP\u2069 فقط.",
  "media.error.size": "يجب ألا يتجاوز حجم الصورة {mb} ميغابايت.",
  "media.error.tooSmall": "يجب ألا يقل الضلع الأقصر في الصورة عن {px} بكسل.",
  "media.error.tooNarrow": "يجب ألا يقل عرض الصورة عن {px} بكسل.",
  "media.ratio.logo": "نسبة هذه الصورة {ratio} — سيتم قصّها إلى مربع.",
  "media.ratio.cover": "نسبة هذه الصورة {ratio} — سيتم قصّها لتناسب إطار 2:1.",
  "media.menu.title": "صور القائمة",
  "media.menu.counter": "{count} صورة",
  "media.menu.add": "إضافة صور",
  "media.menu.uploading": "جارٍ رفع {done} من {total}…",
  "media.menu.uploaded": "تمت إضافة {count} صورة.",
  "media.menu.empty.title": "لا توجد صور للقائمة بعد",
  "media.menu.image": "صورة القائمة {index}",
  "media.menu.moveEarlier": "تقديم الصورة {index}",
  "media.menu.moveLater": "تأخير الصورة {index}",
  "media.menu.delete": "حذف الصورة {index}",
  "media.menu.deleteConfirm.title": "حذف صورة القائمة هذه؟",
  "media.menu.deleteConfirm.description": "لا يمكن التراجع عن هذا الإجراء.",
  "media.menu.deleteConfirm.confirm": "حذف الصورة",
  "media.menu.deleted": "تم حذف صورة القائمة.",
  "media.menu.reorderFailed": "فشلت إعادة الترتيب — تم تحديث القائمة من الخادم.",

  // ---- الفروع (§6.2) ----
  "branches.new": "إضافة فرع",
  "branches.active": "نشط",
  "branches.inactive": "غير نشط",
  "branches.inZone": "المنطقة: {zone}",
  "branches.zoneUnknown": "منطقة غير معروفة",
  "branches.empty.title": "لا توجد فروع بعد",
  "branches.modal.createTitle": "إضافة فرع",
  "branches.modal.editTitle": "تعديل الفرع",
  "branches.modal.createSubmit": "إضافة الفرع",
  "branches.form.nameAr": "اسم الفرع (بالعربية)",
  "branches.form.nameEn": "اسم الفرع (بالإنجليزية)",
  "branches.form.zone": "المنطقة",
  "branches.form.zonePlaceholder": "اختر منطقة…",
  // M7-43d — حالات دليل المناطق تُقال صراحة (بلاغ المالك).
  "branches.form.zonesErrorTitle": "تعذر تحميل المناطق",
  "branches.form.zonesErrorBody": "لم يتم تحميل دليل المناطق المفعّلة، لذا لا يمكن إسناد منطقة حالياً.",
  "branches.form.zonesEmptyTitle": "لا توجد مناطق مفعّلة",
  "branches.form.zonesEmptyAction": "فتح المناطق",
  "branches.form.pin": "موقع المتجر",
  "branches.form.pinMapLabel": "خريطة موقع المتجر",
  "branches.form.pinInstructions": "انقر على الخريطة لتثبيت موقع المتجر بدقة، واسحب الدبوس للتعديل، أو استخدم مفاتيح الأسهم. اضغط \u2066Shift\u2069 لخطوات أكبر.",
  "branches.form.pinRequired": "يجب تثبيت موقع المتجر على الخريطة — لا يمكن حفظ الفرع بدونه.",
  "branches.form.phone": "هاتف الفرع (اختياري)",
  "branches.form.landmark": "علامة مميزة (اختياري)",
  "branches.form.address": "العنوان (اختياري)",
  "branches.form.hours": "ساعات العمل",
  "branches.form.hoursInvalid": "حدد وقتي الفتح والإغلاق لكل يوم عمل.",
  "branches.form.opensAt": "وقت فتح {day}",
  "branches.form.closesAt": "وقت إغلاق {day}",
  "branches.form.closed": "مغلق",
  "branches.form.active": "الفرع نشط",
  "branches.deactivate": "تعطيل",
  "branches.deactivateConfirm.title": "تعطيل هذا الفرع؟",
  "branches.deactivateConfirm.description": "إذا كان هذا آخر فرع نشط فلن يتمكن التاجر من إنشاء الطلبات.",
  "branches.toast.created": "تمت إضافة الفرع.",
  "branches.toast.updated": "تم حفظ الفرع.",
  "branches.toast.deactivated": "تم تعطيل الفرع.",
  "branches.map.label": "خريطة مواقع الفروع",
  "branches.map.moved": "تم تحديث موقع {branch}.",
  "branches.map.confirmTitle": "نقل {branch}؟",
  "branches.map.confirmAction": "نقل دبوس المتجر",
  "branches.map.saving": "جارٍ حفظ الموقع الجديد…",

  // ---- طلبات الانضمام كتاجر (§14.5 — M7-43b) ----
  "applications.title": "طلبات انضمام التجار",
  "applications.pendingCount": "{count} طلباً بانتظار المراجعة",
  "applications.pendingCount.one": "{count} طلب بانتظار المراجعة",
  "applications.filter.status": "الحالة",
  "applications.status.PENDING": "قيد المراجعة",
  "applications.status.APPROVED": "معتمد",
  "applications.status.REJECTED": "مرفوض",
  "applications.table.label": "طلبات انضمام التجار",
  "applications.table.store": "المتجر",
  "applications.table.applicant": "مقدم الطلب",
  "applications.table.category": "التصنيف",
  "applications.table.zone": "المنطقة",
  "applications.table.submitted": "تاريخ التقديم",
  "applications.table.status": "الحالة",
  "applications.empty.title": "لا توجد طلبات قيد المراجعة",
  "applications.emptyFiltered.title": "لا توجد طلبات مطابقة",
  "applications.review.title": "طلب — {store}",
  "applications.review.applicant": "مقدم الطلب",
  "applications.review.store": "معلومات المتجر",
  "applications.review.decision": "القرار",
  "applications.review.rejectionReason": "سبب الرفض: {reason}",
  "applications.review.decidedAt": "تم البت في {date}",
  "applications.action.approve": "اعتماد",
  "applications.action.reject": "رفض…",
  "applications.approveConfirm.title": "اعتماد «{store}»؟",
  "applications.reject.title": "رفض الطلب",
  "applications.reject.reason": "سبب الرفض",
  "applications.reject.reasonRequired": "سبب الرفض مطلوب.",
  "applications.reject.confirm": "رفض «{store}»",
  "applications.toast.approved": "تم اعتماد «{store}» — حساب التاجر فعّال الآن.",
  "applications.toast.rejected": "تم رفض «{store}».",

  // ---- الموظفون ----
  "staff.add": "إضافة موظف",
  "staff.table.label": "الموظفون",
  "staff.table.user": "المستخدم",
  "staff.table.scope": "النطاق",
  "staff.table.title": "المسمى الوظيفي",
  "staff.table.status": "الحالة",
  "staff.table.since": "أُضيف في",
  "staff.scope.merchant": "كامل المتجر",
  "staff.scope.branch": "الفرع: {branch}",
  "staff.scope.branchUnknown": "فرع (محذوف)",
  "staff.status.active": "نشط",
  "staff.status.revoked": "مسحوب",
  "staff.empty.title": "لا يوجد موظفون بعد",
  "staff.modal.title": "إضافة موظف",
  "staff.modal.submit": "إضافة الموظف",
  "staff.form.phone": "هاتف المستخدم",
  "staff.form.scope": "نطاق الوصول",
  "staff.form.title": "المسمى الوظيفي (اختياري)",
  "staff.revoke": "سحب الوصول",
  "staff.revokeConfirm.title": "سحب وصول هذا الموظف؟",
  "staff.revokeConfirm.description": "يُلغى وصولهم إلى المتجر فوراً.",
  "staff.toast.added": "تمت إضافة الموظف.",
  "staff.toast.revoked": "تم سحب وصول الموظف.",

  // ---- لوحة المؤشرات (M6-03) ----
  "dashboard.refreshedAt": "آخر تحديث {time}",
  "dashboard.kpi.ordersToday": "طلبات اليوم",
  "dashboard.kpi.activeDrivers": "السائقون النشطون",
  "dashboard.kpi.revenue": "إيرادات اليوم",
  "dashboard.kpi.cancellations": "إلغاءات اليوم",
  "dashboard.delta.up": "ارتفاع بنسبة {percent} بالمئة مقارنة بالأمس",
  "dashboard.delta.down": "انخفاض بنسبة {percent} بالمئة مقارنة بالأمس",
  "dashboard.delta.flat": "دون تغيير مقارنة بالأمس",
  "dashboard.chart.byHour.title": "الطلبات حسب الساعة",
  "dashboard.chart.byStatus.title": "توزيع الحالات",
  "dashboard.chart.hourColumn": "الساعة",
  "dashboard.chart.ordersColumn": "الطلبات",
  "dashboard.chart.statusColumn": "الحالة",
  "dashboard.chart.unknownStatus": "\u2066{status}\u2069 (غير معروفة في هذا الإصدار)",
  "dashboard.empty.title": "لا توجد طلبات اليوم بعد",

  // ---- مدخل الإعدادات (M6-03) ----
  "settings.themeEntry.open": "إدارة السمة",

  // ---- إدارة السمة (M6-03، §14.1.1 / ADR-006) ----
  "themeScreen.title": "السمة والهوية",
  "themeScreen.version": "الإصدار {version} — آخر تحديث {time}",
  "themeScreen.brandSection": "الهوية",
  "themeScreen.appNameAr": "اسم التطبيق (عربي)",
  "themeScreen.appNameEn": "اسم التطبيق (إنجليزي)",
  "themeScreen.logoLight": "الشعار — الخلفيات الفاتحة",
  "themeScreen.logoDark": "الشعار — الخلفيات الداكنة",
  "themeScreen.logoMark": "الأيقونة (مربعة)",
  "themeScreen.tabs.label": "وضع السمة",
  "themeScreen.tab.light": "الوضع الفاتح",
  "themeScreen.tab.dark": "الوضع الداكن",
  "themeScreen.group.core": "ألوان الهوية",
  "themeScreen.group.promo": "لون العروض",
  "themeScreen.group.surfaces": "الأسطح والحدود",
  "themeScreen.group.text": "النصوص والتركيز",
  "themeScreen.group.feedback": "ألوان الحالات",
  "themeScreen.colorInvalid": "أدخل لوناً سداسياً بأحرف كبيرة بصيغة \u200E#RRGGBB.",
  "themeScreen.nameRequired": "اسم التطبيق مطلوب باللغتين.",
  "themeScreen.logoRequired": "رابط الشعار مطلوب.",
  "themeScreen.preview.title": "المعاينة",
  "themeScreen.preview.description": "تُعرض من مسودتك قبل الحفظ — بدّل تبويب الوضع لمعاينة الفاتح/الداكن.",
  "themeScreen.preview.body": "نص أساسي على لون السطح، مع حبر ثانوي للتسميات.",
  "themeScreen.preview.primaryAction": "إجراء رئيسي",
  "themeScreen.preview.secondaryAction": "ثانوي",
  "themeScreen.preview.dangerAction": "إجراء خطر",
  "themeScreen.preview.badgeSuccess": "نجاح",
  "themeScreen.preview.badgeWarning": "تحذير",
  "themeScreen.preview.badgeDanger": "خطر",
  "themeScreen.preview.badgeInfo": "معلومة",
  "themeScreen.preview.badgePromo": "عرض",
  "themeScreen.save": "حفظ وتطبيق…",
  "themeScreen.reset": "تجاهل التغييرات",
  "themeScreen.confirm.title": "تطبيق هذه السمة على المنصة كاملة؟",
  "themeScreen.confirm.apply": "تطبيق السمة",
  "themeScreen.toast.saved": "تم حفظ السمة — يعاد تلوين العملاء عند أول إعادة تحقق.",
  "themeScreen.conflict.title": "قام شخص آخر بحفظ سمة أحدث",
  "themeScreen.conflict.description": "تعديلاتك مبنية على إصدار قديم — أعد تحميل السمة الحالية ثم أعد تطبيقها.",
  "themeScreen.conflict.reload": "إعادة تحميل السمة الحالية",
  "themeScreen.denied.title": "لا تملك صلاحية السمة",

  // ---- منصة الجداول (M6-04) ----
  "table.columns": "الأعمدة",
  "table.export.csv": "تصدير CSV",
  "table.export.excel": "تصدير Excel",
  "table.savedFilters.label": "فلاتر محفوظة",
  "table.savedFilters.placeholder": "الفلاتر المحفوظة…",
  "table.savedFilters.save": "حفظ الفلاتر",
  "table.savedFilters.modalTitle": "حفظ الفلاتر الحالية",
  "table.savedFilters.nameLabel": "اسم مجموعة الفلاتر",
  "table.savedFilters.nameRequired": "أدخل اسماً لمجموعة الفلاتر.",
  "table.savedFilters.saveConfirm": "حفظ",
  "table.savedFilters.delete": "حذف الفلتر المحفوظ",
  "table.savedFilters.deleteTitle": "حذف هذا الفلتر المحفوظ؟",
  "table.savedFilters.deleteConfirm": "حذف",
  "table.bulk.label": "إجراءات جماعية",
  "table.bulk.selected": "{count} محدد",
  "table.bulk.clear": "إلغاء التحديد",
  "table.selectAll": "تحديد كل صفوف هذه الصفحة",
  "table.selectRow": "تحديد {name}",

  // ---- الطلبات (M6-04) ----
  "orders.title": "الطلبات",
  "orders.count": "{count} طلب",
  "orders.count.one": "{count} طلب",
  "orders.search.label": "البحث برقم الطلب",
  "orders.search.placeholder": "رقم الطلب الكامل، مثال: \u2066ORD-000042\u2069…",
  "orders.filter.status": "الحالة",
  "orders.filter.merchant": "التاجر",
  "orders.filter.driver": "السائق",
  "orders.filter.zone": "المنطقة",
  "orders.filter.category": "التصنيف",
  "orders.filter.from": "من تاريخ",
  "orders.filter.to": "إلى تاريخ",
  "orders.table.label": "الطلبات",
  "orders.table.orderNo": "الطلب",
  "orders.table.created": "أُنشئ",
  "orders.table.status": "الحالة",
  "orders.table.merchant": "المتجر / المُرسِل",
  "orders.table.driver": "السائق",
  "orders.table.zone": "المنطقة",
  "orders.table.category": "التصنيف",
  "orders.table.source": "المصدر",
  "orders.table.type": "النوع",
  "orders.table.total": "الإجمالي",
  "orders.export.currency": "العملة",
  "orders.empty.title": "لا توجد طلبات بعد",
  "orders.emptyFiltered.title": "لا نتائج مطابقة",
  "orders.source.MERCHANT_CREATED": "التاجر",
  "orders.source.CUSTOMER_CREATED": "تطبيق العميل",
  "orders.source.ADMIN_CREATED": "الإدارة",
  "orders.source.API_CREATED": "واجهة برمجية",
  "orders.type.DISPATCH": "توصيل",
  "orders.type.MARKETPLACE": "سوق",
  "orders.type.PICKUP": "استلام ذاتي",
  "orders.type.SCHEDULED": "مجدول",
  "orders.bulkCancel.action": "إلغاء {count} طلب",
  "orders.bulkCancel.action.one": "إلغاء {count} طلب",
  "orders.bulkCancel.title": "إلغاء {count} طلب؟",
  "orders.bulkCancel.title.one": "إلغاء {count} طلب؟",
  "orders.bulkCancel.description": "لا يمكن التراجع عن هذا الإجراء.",
  "orders.bulkCancel.confirm": "إلغاء الطلبات",
  "orders.bulkCancel.done": "تم إلغاء {count} طلب.",
  "orders.bulkCancel.done.one": "تم إلغاء {count} طلب.",
  "orders.bulkCancel.partial": "أُلغي {done} وفشل {failed} — تم تحديث القائمة.",
  "orders.transition.reasonLabel": "السبب",
  "orders.transition.reasonRequired": "هذا الإجراء يتطلب ذكر السبب.",
  "orders.detail.title": "الطلب {orderNo}",
  "orders.tabs.label": "أقسام الطلب",
  "orders.tabs.details": "التفاصيل",
  "orders.tabs.history": "سجل التغييرات",
  "orders.detail.parties": "الأطراف والسياق",
  "orders.detail.merchant": "التاجر",
  "orders.detail.driver": "السائق",
  "orders.detail.noDriver": "لم يُسند بعد",
  "orders.detail.customer": "العميل",
  "orders.detail.customerPhone": "هاتف العميل",
  "orders.detail.customerLink": "ربط الحساب",
  "orders.detail.registered": "حساب مسجل",
  "orders.detail.guest": "سجل ضيف",
  "orders.detail.modWindow": "تنتهي مهلة التعديل",
  "orders.detail.completedAt": "اكتمل",
  "orders.detail.landmark": "نقطة دالة",
  "orders.detail.dropoff": "دبوس التسليم",
  "orders.detail.breakdown": "تفصيل السعر",
  "orders.detail.noQuote": "لا يوجد عرض توصيل بعد — طلب بنقطة دالة فقط، يُستكمل قبل الاستلام.",
  "orders.detail.orderValue": "قيمة الطلب",
  "orders.detail.deliveryFee": "أجرة التوصيل",
  "orders.detail.feeFormula": "الأساس {base} + {perKm}/كم × {km} كم",
  "orders.detail.capMin": "طُبّق الحد الأدنى",
  "orders.detail.capMax": "طُبّق الحد الأعلى",
  "orders.detail.exactPin": "قيست المسافة إلى الدبوس الدقيق",
  "orders.detail.zoneCenter": "قيست المسافة إلى مركز المنطقة",
  "orders.detail.discount": "الخصم",
  "orders.detail.total": "المبلغ النهائي",
  "orders.funding.PLATFORM": "بتمويل المنصة",
  "orders.funding.MERCHANT": "بتمويل التاجر",
  "orders.funding.SHARED": "تمويل مشترك",
  "orders.funding.line": "{funder} — التاجر {merchant} · المنصة {platform} · تعويض السائق {driver}",
  "orders.detail.money": "الدفع والتسوية",
  "orders.detail.paymentMethod": "طريقة الدفع",
  "orders.detail.cashOnDelivery": "الدفع نقداً عند التسليم",
  "orders.detail.toCollect": "يحصّل السائق",
  "orders.detail.settlementLink": "سجلات التسوية",
  "orders.detail.walletsLink": "المحافظ",
  "orders.timeline.created": "أنشأه {actor}",
  "orders.timeline.transition": "بواسطة {actor} — من {from}",
  "orders.timeline.reason": "السبب: {reason}",
  "orders.actor.MERCHANT": "التاجر",
  "orders.actor.DRIVER": "السائق",
  "orders.actor.ADMIN": "الإدارة",
  "orders.actor.CUSTOMER": "العميل",
  "orders.actor.SYSTEM": "النظام",
  "orders.actor.API": "واجهة برمجية",
  "orders.geofence.PASSED": "السياج الجغرافي: مطابق",
  "orders.geofence.SKIPPED": "السياج الجغرافي: مُتجاوز تلقائياً",
  "orders.geofence.OVERRIDDEN": "السياج الجغرافي: استثناء معتمد",
  "orders.geofence.distance": "{distance} م عن الهدف (المسموح {allowed} م)",
  "orders.actions.cancel": "إلغاء الطلب…",
  "orders.actions.return": "تسجيل كمرتجع…",
  "orders.cancel.title": "إلغاء الطلب {orderNo}؟",
  "orders.cancel.description": "لا يمكن التراجع عن هذا الإجراء.",
  "orders.cancel.confirm": "إلغاء الطلب",
  "orders.return.title": "تسجيل الطلب {orderNo} كمرتجع؟",
  "orders.return.description": "ينتهي التوصيل كإرجاع ولا يمكن التراجع عنه.",
  "orders.return.confirm": "تسجيل كمرتجع",
  "orders.toast.cancelled": "تم إلغاء الطلب.",
  "orders.toast.returned": "تم تسجيل الطلب كمرتجع.",

  // ---- Customer delivery requests (Wave 21, §3.1/§14.4 — owner ruling 2026-09-15) ----
  "orders.customerRequest.badge": "طلب عميل",
  "orders.customerRequest.noStore": "بدون متجر",
  "orders.detail.pickup": "الاستلام",
  "orders.detail.pickupAddress": "عنوان الاستلام",
  "orders.detail.pickupLandmark": "نقطة دالة للاستلام",
  "orders.detail.pickupZone": "منطقة الاستلام",
  "orders.detail.pickupPin": "دبوس الاستلام",
  "orders.detail.pickupMap": "موقع الاستلام على الخريطة",
  "orders.detail.pickupNoPin": "بدون دبوس — القياس من مركز المنطقة",
  "orders.detail.recipientCard": "المستلِم والغرض",
  "orders.detail.recipientName": "المستلِم",
  "orders.detail.recipientPhone": "هاتف المستلِم",
  "orders.detail.parcel": "الغرض",
  "orders.detail.payer": "دافع الأجرة",
  "orders.detail.sender": "المُرسِل",
  "orders.detail.senderPhone": "هاتف المُرسِل",
  "orders.detail.senderNote": "ملاحظة للسائق",
  "orders.payer.SENDER": "المُرسِل",
  "orders.payer.RECIPIENT": "المستلِم",

  // ---- السائقون (M6-04) ----
  "drivers.title": "السائقون",
  "drivers.count": "{count} سائق",
  "drivers.count.one": "{count} سائق",
  "drivers.search.label": "البحث في السائقين",
  "drivers.search.placeholder": "ابحث بالرقم أو الاسم أو الهاتف…",
  "drivers.filter.status": "الحالة",
  "drivers.filter.availability": "التوفر",
  "drivers.filter.blocked": "المحظورون فقط",
  "drivers.filter.vehicle": "المركبة",
  "drivers.table.label": "السائقون",
  "drivers.table.driver": "السائق",
  "drivers.table.phone": "الهاتف",
  "drivers.table.vehicle": "المركبة",
  "drivers.vehicle.none": "بلا مركبة",
  "drivers.table.status": "الحالة",
  "drivers.table.availability": "التوفر",
  "drivers.table.blocked": "الحظر",
  "drivers.table.created": "انضم",
  "drivers.export.driverNo": "رقم السائق",
  "drivers.status.PENDING_APPROVAL": "بانتظار الاعتماد",
  "drivers.status.ACTIVE": "مفعّل",
  "drivers.status.SUSPENDED": "موقوف",
  "drivers.status.TERMINATED": "منتهي",
  "drivers.availability.ONLINE": "متصل",
  "drivers.availability.OFFLINE": "غير متصل",
  "drivers.availability.ON_ORDER": "في مهمة",
  "drivers.blocked.WALLET_BELOW_THRESHOLD": "الرصيد دون الحد",
  "drivers.blocked.DOCUMENTS_EXPIRED": "وثائق منتهية",
  "drivers.blocked.ADMIN": "حظر إداري",
  "drivers.empty.title": "لا يوجد سائقون بعد",
  "drivers.emptyFiltered.title": "لا نتائج مطابقة",
  "drivers.tabs.label": "أقسام السائق",
  "drivers.tabs.overview": "نظرة عامة",
  "drivers.detail.notBlocked": "غير محظور",
  "drivers.detail.nationalId": "الرقم الوطني",
  "drivers.detail.birthDate": "تاريخ الميلاد",
  "drivers.detail.notes": "ملاحظات",

  // ---- العملاء (M6-04) ----
  "customers.title": "العملاء",
  "customers.count": "{count} عميل",
  "customers.count.one": "{count} عميل",
  "customers.search.label": "البحث في العملاء",
  "customers.search.placeholder": "ابحث بالهاتف أو الاسم…",
  "customers.filter.origin": "المنشأ",
  "customers.filter.claimed": "ربط الحساب",
  "customers.table.label": "العملاء",
  "customers.table.customer": "العميل",
  "customers.table.origin": "المنشأ",
  "customers.table.linked": "الحساب",
  "customers.table.locale": "اللغة",
  "customers.table.created": "أول ظهور",
  "customers.export.phone": "الهاتف",
  "customers.origin.REGISTERED": "تسجيل مباشر",
  "customers.origin.GUEST_MERCHANT_ORDER": "ضيف عبر طلب تاجر",
  "customers.origin.GUEST_ADMIN": "ضيف عبر الإدارة",
  "customers.linked.registered": "مسجل",
  "customers.linked.guest": "ضيف",
  "customers.locale.ar": "العربية",
  "customers.locale.en": "الإنجليزية",
  "customers.unnamed": "عميل دون اسم",
  "customers.empty.title": "لا يوجد عملاء بعد",
  "customers.emptyFiltered.title": "لا نتائج مطابقة",
  "customers.tabs.label": "أقسام العميل",
  "customers.tabs.overview": "نظرة عامة",
  "customers.detail.claimedAt": "رُبط الحساب",
  "customers.detail.addresses": "العناوين المحفوظة",
  "customers.addresses.default": "افتراضي",
  "customers.addresses.empty.title": "لا توجد عناوين محفوظة",

  // ---- سجل التغييرات (audit.yaml v1.0.0 — M6-18، مطابقة M6-06) ----
  "audit.tab": "سجل التغييرات",
  "audit.verb.created": "إنشاء",
  "audit.verb.updated": "تعديل",
  "audit.verb.statusChanged": "تغيير حالة",
  "audit.verb.deleted": "حذف",
  "audit.by": "بواسطة {actor}",
  "audit.actor.system": "النظام",
  "audit.subjectLabel": "الكيان",
  "audit.traceLabel": "التتبّع",
  "audit.diff.was": "كان",
  "audit.diff.now": "أصبح",
  "audit.emptyValue": "(فارغ)",
  "audit.empty.title": "لا توجد تغييرات مسجلة",
  "audit.denied.title": "سجل التغييرات غير متاح",

  // ---- إدارة المحافظ (M6-06 §14.4، §8.1/§8.2) ----
  "wallets.title": "المحافظ",
  "wallets.count": "{count} محفظة",
  "wallets.count.one": "{count} محفظة",
  "wallets.tabs.label": "مجموعات المحافظ",
  "wallets.tabs.merchants": "محافظ التجار",
  "wallets.tabs.drivers": "محافظ السائقين",
  "wallets.table.label": "المحافظ",
  "wallets.table.owner": "المالك",
  "wallets.table.balance": "الرصيد",
  "wallets.table.creditLimit": "حد الائتمان",
  "wallets.table.available": "المتاح",
  "wallets.table.status": "الحالة",
  "wallets.table.blocked": "إيقاف الإسناد",
  "wallets.table.actions": "إجراءات",
  "wallets.badge.negative": "رصيد سالب",
  "wallets.badge.frozen": "موقوفة",
  "wallets.badge.active": "فعّالة",
  "wallets.badge.blocked": "موقوف",
  "wallets.badge.notBlocked": "سليم",
  "wallets.filter.negativeOnly": "الأرصدة السالبة فقط",
  "wallets.filter.blockedOnly": "السائقون الموقوفون فقط",
  "wallets.action.fund": "تمويل المحفظة",
  "wallets.action.topup": "شحن رصيد",
  "wallets.action.adjust": "تسوية",
  "wallets.action.setCreditLimit": "تعديل حد الائتمان",
  "wallets.export.ownerNo": "رقم المالك",
  "wallets.empty.title": "لا توجد محافظ بعد",
  "wallets.openOwner.action": "فتح محفظة مالك",
  "wallets.openOwner.title": "فتح محفظة مالك",
  "wallets.openOwner.searchMerchant": "بحث في التجار",
  "wallets.openOwner.searchDriver": "بحث في السائقين",
  "wallets.openOwner.searchPlaceholder": "الاسم أو الرقم أو الهاتف",
  "wallets.openOwner.results": "الملاك المطابقون",
  "wallets.openOwner.failed": "تعذّر تحميل الدليل.",
  "wallets.openOwner.empty.title": "لا يوجد مالك مطابق",
  "wallets.emptyFiltered.title": "لا توجد محافظ مطابقة",
  "wallets.owner.MERCHANT": "تاجر",
  "wallets.owner.DRIVER": "سائق",
  "wallets.owner.CUSTOMER": "عميل",
  "wallets.detail.title": "المحفظة",
  "wallets.detail.balanceTitle": "الرصيد",
  "wallets.blockedBanner.title": "الإسناد موقوف",
  "wallets.blockedBanner.since": "موقوف منذ {date}.",
  "wallets.plan.title": "خطة السائق",
  "wallets.plan.type": "نوع الخطة",
  "wallets.plan.DAILY_SUBSCRIPTION": "اشتراك يومي",
  "wallets.plan.COMMISSION": "عمولة",
  "wallets.plan.dailyFee": "الرسم اليومي",
  "wallets.plan.commissionPercent": "نسبة العمولة من أجرة التوصيل",
  "wallets.plan.effectiveFrom": "سارية منذ",
  "wallets.plan.none": "لا توجد خطة فعّالة.",
  "wallets.ledger.title": "سجل الحركات",
  "wallets.ledger.filterLabel": "تصفية حسب نوع الحركة",
  "wallets.ledger.tableLabel": "حركات المحفظة",
  "wallets.ledger.date": "التاريخ",
  "wallets.ledger.type": "النوع",
  "wallets.ledger.amount": "المبلغ",
  "wallets.ledger.balanceAfter": "الرصيد بعدها",
  "wallets.ledger.related": "المرجع",
  "wallets.ledger.memo": "ملاحظة",
  "wallets.ledger.promotionRef": "عرض ترويجي",
  "wallets.ledger.empty.title": "لا توجد حركات",
  "wallets.entry.TOPUP": "شحن رصيد",
  "wallets.entry.WITHDRAWAL": "سحب",
  "wallets.entry.COMMISSION": "عمولة",
  "wallets.entry.DAILY_FEE": "رسم يومي",
  "wallets.entry.SUBSCRIPTION_FEE": "رسم اشتراك",
  "wallets.entry.COFUND_DEDUCTION": "خصم تمويل مشترك",
  "wallets.entry.COFUND_COMPENSATION": "تعويض تمويل مشترك",
  "wallets.entry.SETTLEMENT": "تسوية طلب",
  "wallets.entry.ADJUSTMENT": "تسوية إدارية",
  "wallets.entry.REFUND": "استرداد",
  "wallets.entry.REVERSAL": "عكس قيد",
  "wallets.direction.CREDIT": "إيداع (إضافة رصيد)",
  "wallets.direction.DEBIT": "خصم (سحب رصيد)",
  "wallets.dialog.fundTitle": "تمويل محفظة التاجر",
  "wallets.dialog.topupTitle": "شحن محفظة السائق",
  "wallets.dialog.adjustTitle": "قيد تسوية",
  "wallets.dialog.confirm": "ترحيل القيد",
  "wallets.dialog.amount": "المبلغ",
  "wallets.dialog.amountInvalid": "أدخل مبلغاً صالحاً (مثال: 25000.00).",
  "wallets.dialog.memo": "ملاحظة مرجعية",
  "wallets.dialog.direction": "الاتجاه",
  "wallets.dialog.override": "السماح بتجاوز حد الائتمان",
  "wallets.dialog.limitNoOverride": "هذا الخصم يتجاوز حد ائتمان المحفظة، ولا تملك صلاحية التجاوز.",
  "wallets.dialog.balanceContext": "الرصيد الحالي: {balance}.",
  "wallets.dialog.fundConsequence": "يضيف هذا إيداعاً نقدياً إلى محفظة التاجر فوراً ويظهر في السجل.",
  "wallets.dialog.topupConsequence": "يضيف هذا شحناً نقدياً إلى محفظة السائق فوراً؛ إذا كان السائق موقوفاً فإن الشحن الكافي يرفع الإيقاف.",
  "wallets.dialog.adjustDebitConsequence": "يخصم هذا من رصيد المحفظة فوراً ويظهر في السجل.",
  "wallets.dialog.adjustCreditConsequence": "يضيف هذا إلى رصيد المحفظة فوراً ويظهر في السجل.",
  "wallets.creditLimit.title": "تجاوز حد الائتمان",
  "wallets.creditLimit.label": "حد الائتمان",
  "wallets.creditLimit.confirmTitle": "تغيير حد الائتمان؟",
  "wallets.creditLimit.confirmDescription": "تُفحص عمليات الخصم القادمة وفق الحد الجديد.",
  "wallets.toast.posted": "تم ترحيل القيد.",
  "wallets.toast.limitSaved": "تم تحديث حد الائتمان.",

  // ---- التسوية والمطابقة اليومية (M6-06 §14.4، §8.3) ----
  "settlement.title": "التسوية",
  "settlement.tabs.label": "عروض التسوية",
  "settlement.tabs.records": "السجلات",
  "settlement.tabs.reconciliation": "المطابقة اليومية",
  "settlement.table.label": "سجلات التسوية",
  "settlement.table.record": "السجل",
  "settlement.table.merchant": "التاجر",
  "settlement.table.driver": "السائق",
  "settlement.table.outcome": "النتيجة",
  "settlement.table.status": "الحالة",
  "settlement.table.customerAmount": "دفع العميل",
  "settlement.table.merchantAmount": "استلم التاجر",
  "settlement.table.platformRevenue": "إيراد المنصة",
  "settlement.table.created": "التاريخ",
  "settlement.filter.merchant": "التاجر",
  "settlement.filter.driver": "السائق",
  "settlement.filter.outcome": "النتيجة",
  "settlement.filter.status": "الحالة",
  "settlement.filter.from": "من",
  "settlement.filter.to": "إلى",
  "settlement.outcome.DELIVERED": "تم التسليم",
  "settlement.outcome.RETURNED": "راجع",
  "settlement.status.DRAFT": "مسودة",
  "settlement.status.PENDING": "قيد الانتظار",
  "settlement.status.SETTLED": "مسوّى",
  "settlement.status.VOID": "ملغى",
  "settlement.status.DISPUTED": "متنازع عليه",
  "settlement.empty.title": "لا توجد سجلات تسوية",
  "settlement.emptyFiltered.title": "لا توجد سجلات مطابقة",
  "settlement.scope.DRIVER": "سائق",
  "settlement.scope.MERCHANT": "تاجر",
  "settlement.party.DRIVER": "السائق",
  "settlement.party.MERCHANT": "التاجر",
  "settlement.party.CUSTOMER": "العميل",
  "settlement.party.PLATFORM": "المنصة",
  "settlement.party.PROMOTION_FUND": "صندوق العروض",
  "settlement.flow.DRIVER_PAYS_MERCHANT": "السائق يدفع للتاجر قيمة الطلب",
  "settlement.flow.CUSTOMER_PAYS_DRIVER": "العميل يدفع للسائق عند التسليم",
  "settlement.flow.MERCHANT_REFUNDS_DRIVER": "التاجر يعيد المبلغ للسائق (إرجاع)",
  "settlement.flow.MERCHANT_PAYS_RETURN_FEE": "التاجر يدفع للسائق أجرة التوصيل (إرجاع)",
  "settlement.flow.PLATFORM_COMMISSION": "عمولة المنصة",
  "settlement.flow.DAILY_FEE": "رسم الاشتراك اليومي",
  "settlement.flow.COFUND_MERCHANT_SHARE": "حصة التاجر من الخصم الممول مشتركاً",
  "settlement.flow.COFUND_DRIVER_COMPENSATION": "تعويض السائق عن الخصم",
  "settlement.flow.REVERSAL": "عكس قيد",
  "settlement.detail.parties": "الأطراف",
  "settlement.detail.amounts": "المبالغ",
  "settlement.detail.orderValue": "قيمة الطلب",
  "settlement.detail.deliveryFee": "أجرة التوصيل",
  "settlement.detail.customerDiscount": "خصم العميل",
  "settlement.detail.merchantAmount": "استلم التاجر",
  "settlement.detail.customerAmount": "دفع العميل",
  "settlement.detail.driverIncome": "دخل السائق من التوصيل",
  "settlement.detail.platformRevenue": "إيراد المنصة",
  "settlement.detail.merchantShare": "حصة التاجر من الخصم",
  "settlement.detail.driverCompensation": "تعويض السائق عن الخصم",
  "settlement.detail.driverPlan": "خطة السائق",
  "settlement.detail.flowsTitle": "حركات التدفق النقدي",
  "settlement.detail.cofundTag": "تمويل مشترك",
  "settlement.recon.status.DRAFT": "مسودة",
  "settlement.recon.status.FINALIZED": "نهائي",
  "settlement.recon.table.label": "المطابقات اليومية",
  "settlement.recon.table.report": "التقرير",
  "settlement.recon.table.scope": "النطاق",
  "settlement.recon.table.orders": "الطلبات",
  "settlement.recon.table.ordersSplit": "{total} ({delivered} مسلّم / {returned} راجع)",
  "settlement.recon.scopeType": "نوع النطاق",
  "settlement.recon.scope": "النطاق",
  "settlement.recon.periodDate": "اليوم",
  "settlement.recon.ordersCount": "الطلبات",
  "settlement.recon.deliveredCount": "المسلّمة",
  "settlement.recon.returnedCount": "الراجعة",
  "settlement.recon.grossCollected": "إجمالي التحصيل",
  "settlement.recon.merchantPaid": "المدفوع للتجار",
  "settlement.recon.deliveryFees": "أجور التوصيل",
  "settlement.recon.platformRevenue": "إيراد المنصة",
  "settlement.recon.cofundDeductions": "خصوم التمويل المشترك",
  "settlement.recon.netPosition": "صافي الموقف",
  "settlement.recon.generatedAt": "أُنشئ {date}",
  "settlement.recon.viewLedger": "فتح دفتر المحفظة",
  "settlement.recon.empty.title": "لا توجد تقارير مطابقة",
  "settlement.recon.generate.open": "إنشاء تقرير",
  "settlement.recon.generate.title": "إنشاء مطابقة يومية",
  "settlement.recon.generate.action": "إنشاء",
  "settlement.recon.generate.scopePlaceholder": "اختر…",
  "settlement.recon.generate.scopeRequired": "اختر سائقاً أو تاجراً.",
  "settlement.recon.generate.dateRequired": "اختر اليوم المراد مطابقته.",
  "settlement.recon.generate.finalize": "اعتماد التقرير نهائياً",
  "settlement.recon.confirm.title": "إنشاء واستبدال؟",
  "settlement.recon.confirm.description": "يُستبدل أي تقرير موجود لنفس النطاق واليوم والعملة.",
  "settlement.recon.toast.generated": "تم إنشاء المطابقة.",

  // ---- إدارة الاشتراكات (M6-06 §14.4، §7) ----
  "subscriptions.title": "الاشتراكات",
  "subscriptions.tabs.label": "عروض الاشتراكات",
  "subscriptions.tabs.subscriptions": "الاشتراكات",
  "subscriptions.tabs.packages": "الباقات",
  "subscriptions.table.label": "اشتراكات التجار",
  "subscriptions.table.subscription": "الاشتراك",
  "subscriptions.table.merchant": "التاجر",
  "subscriptions.table.package": "الباقة",
  "subscriptions.table.status": "الحالة",
  "subscriptions.table.starts": "يبدأ",
  "subscriptions.table.ends": "ينتهي",
  "subscriptions.table.autoRenew": "تجديد تلقائي",
  "subscriptions.filter.status": "الحالة",
  "subscriptions.filter.merchant": "التاجر",
  "subscriptions.status.ACTIVE": "فعّال",
  "subscriptions.status.EXPIRING": "قارب الانتهاء",
  "subscriptions.status.EXPIRED": "منتهٍ",
  "subscriptions.status.RENEWED": "مجدَّد",
  "subscriptions.status.CANCELLED": "ملغى",
  "subscriptions.autoRenew.on": "تجديد تلقائي",
  "subscriptions.autoRenew.off": "معطّل",
  "subscriptions.empty.title": "لا توجد اشتراكات بعد",
  "subscriptions.emptyFiltered.title": "لا توجد اشتراكات مطابقة",
  "subscriptions.assign.open": "اشتراك جديد",
  "subscriptions.assign.title": "إسناد باقة إلى تاجر",
  "subscriptions.assign.action": "اشتراك",
  "subscriptions.assign.merchant": "التاجر",
  "subscriptions.assign.merchantPlaceholder": "اختر تاجراً…",
  "subscriptions.assign.merchantRequired": "اختر تاجراً.",
  "subscriptions.assign.package": "الباقة",
  "subscriptions.assign.packagePlaceholder": "اختر باقة…",
  "subscriptions.assign.packageRequired": "اختر باقة.",
  "subscriptions.assign.autoRenew": "تجديد تلقائي عند نهاية المدة",
  "subscriptions.assign.confirmTitle": "بدء هذا الاشتراك؟",
  "subscriptions.assign.confirmWallet": "يُخصم {amount} فوراً من محفظة التاجر.",
  "subscriptions.paidVia.label": "طريقة الدفع",
  "subscriptions.paidVia.CASH_ADMIN": "نقداً (تسجيل إداري)",
  "subscriptions.paidVia.MERCHANT_WALLET": "محفظة التاجر",
  "subscriptions.period.MONTHLY": "شهري",
  "subscriptions.period.QUARTERLY": "ربع سنوي",
  "subscriptions.period.YEARLY": "سنوي",
  "subscriptions.feature.customer_app_visibility": "الظهور في تطبيق الزبائن",
  "subscriptions.feature.featured_listing": "ظهور مميز",
  "subscriptions.feature.lower_commission": "عمولة مخفضة",
  "subscriptions.feature.promotion_cofunding": "تمويل مشترك للعروض",
  "subscriptions.feature.advanced_analytics": "تحليلات متقدمة",
  "subscriptions.package.create": "باقة جديدة",
  "subscriptions.package.createTitle": "إنشاء باقة",
  "subscriptions.package.editTitle": "تعديل الباقة",
  "subscriptions.package.active": "فعّالة",
  "subscriptions.package.inactive": "غير فعّالة",
  "subscriptions.package.table.label": "باقات الاشتراك",
  "subscriptions.package.table.name": "الباقة",
  "subscriptions.package.table.price": "السعر",
  "subscriptions.package.table.features": "المزايا",
  "subscriptions.package.table.active": "الحالة",
  "subscriptions.package.empty.title": "لا توجد باقات بعد",
  "subscriptions.package.form.required": "هذا الحقل مطلوب.",
  "subscriptions.package.form.nameAr": "الاسم (عربي)",
  "subscriptions.package.form.nameEn": "الاسم (إنجليزي)",
  "subscriptions.package.form.descriptionAr": "الوصف (عربي)",
  "subscriptions.package.form.descriptionEn": "الوصف (إنجليزي)",
  "subscriptions.package.form.price": "السعر",
  "subscriptions.package.form.period": "المدة",
  "subscriptions.package.form.sortOrder": "ترتيب العرض",
  "subscriptions.package.form.features": "مزايا الباقة",
  "subscriptions.package.form.isActive": "متاحة للاشتراكات الجديدة",
  "subscriptions.package.deactivate.title": "إيقاف عرض هذه الباقة؟",
  "subscriptions.package.deactivate.description": "لن تتمكن الاشتراكات الجديدة من استخدام هذه الباقة.",
  "subscriptions.detail.periodTitle": "فترة الاشتراك",
  "subscriptions.detail.featuresTitle": "لقطة المزايا",
  "subscriptions.detail.noFeatures": "لا مزايا في لقطة هذه الفترة.",
  "subscriptions.detail.invoicesTitle": "سجل الفواتير",
  "subscriptions.detail.renewedFrom": "مجدَّد من",
  "subscriptions.detail.renewedTo": "مجدَّد إلى",
  "subscriptions.banner.expiringTitle": "قارب الانتهاء",
  "subscriptions.banner.expiredTitle": "منتهٍ",
  "subscriptions.renew.title": "تجديد الاشتراك",
  "subscriptions.renew.action": "تجديد",
  "subscriptions.renew.confirmTitle": "تجديد هذا الاشتراك؟",
  "subscriptions.renew.toast": "تم تجديد الاشتراك.",
  "subscriptions.cancel.title": "إلغاء الاشتراك",
  "subscriptions.cancel.action": "إلغاء الاشتراك",
  "subscriptions.cancel.description": "يفقد المتجر مزايا اشتراكه، ومنها الظهور في تطبيق العميل.",
  "subscriptions.cancel.reason": "السبب",
  "subscriptions.cancel.toast": "تم إلغاء الاشتراك.",
  "subscriptions.invoice.number": "الفاتورة",
  "subscriptions.invoice.amount": "المبلغ",
  "subscriptions.invoice.status": "الحالة",
  "subscriptions.invoice.paidAt": "تاريخ الدفع",
  "subscriptions.invoice.actions": "إجراءات",
  "subscriptions.invoice.empty": "لا فواتير لهذه الفترة.",
  "subscriptions.invoice.recordPayment": "تسجيل الدفع",
  "subscriptions.invoice.toast.paid": "تم تعليم الفاتورة مدفوعة.",
  "subscriptions.invoice.payConfirmTitle": "تسجيل هذه الدفعة؟",
  "subscriptions.invoice.payConfirmWallet": "يُخصم {amount} فوراً من محفظة التاجر.",
  "subscriptions.invoice.status.PENDING": "قيد الانتظار",
  "subscriptions.invoice.status.PAID": "مدفوعة",
  "subscriptions.invoice.status.FAILED": "فاشلة",
  "subscriptions.invoice.status.VOID": "ملغاة",
  "subscriptions.invoice.status.WAIVED": "معفاة",

  // ---- تصدير التجار (M6-04) ----
  "merchants.export.merchantNo": "رقم التاجر",

  /* M6-07 — إدارة العروض (§4، §14.4) */
  "common.from": "من",
  "common.to": "إلى",

  "promotions.title": "العروض",
  "promotions.tabs.label": "أقسام العروض",
  "promotions.tabs.promotions": "العروض",
  "promotions.tabs.applications": "سجل التطبيق",
  "promotions.tabs.funding": "تقارير التمويل",
  "promotions.tabs.preview": "المعاينة",
  "promotions.create": "عرض جديد",
  "promotions.create.title": "عرض جديد",
  "promotions.create.denied": "ليست لديك صلاحية إنشاء العروض.",
  "zones.manage.denied": "ليست لديك صلاحية إدارة المناطق.",
  "merchants.create.denied": "ليست لديك صلاحية إنشاء التجار.",
  "drivers.create.denied": "ليست لديك صلاحية إنشاء السائقين.",

  "promotions.table.label": "العروض",
  "promotions.table.no": "المرجع",
  "promotions.table.name": "الاسم",
  "promotions.table.kind": "النوع",
  "promotions.table.method": "الخصم",
  "promotions.table.funder": "جهة التمويل",
  "promotions.table.status": "الحالة",
  "promotions.table.priority": "الأولوية",
  "promotions.table.exclusive": "حصري",
  "promotions.table.redemptions": "مرات الاستخدام",
  "promotions.table.starts": "يبدأ",
  "promotions.table.ends": "ينتهي",
  "promotions.listed": "معروض للعملاء",
  "promotions.filter.status": "تصفية حسب الحالة",
  "promotions.filter.kind": "النوع",
  "promotions.filter.funder": "جهة التمويل",
  "promotions.filter.target": "الهدف",
  "promotions.filter.search": "بحث",
  "promotions.filter.search.placeholder": "المرجع أو الاسم أو رمز القسيمة",
  "promotions.empty.title": "لا توجد عروض بعد",
  "promotions.emptyFiltered.title": "لا توجد عروض مطابقة",

  "promotions.status.DRAFT": "مسودة",
  "promotions.status.ACTIVE": "فعّال",
  "promotions.status.PAUSED": "موقوف مؤقتاً",
  "promotions.status.ENDED": "منتهٍ",
  "promotions.status.ARCHIVED": "مؤرشف",

  "promotions.kind.COUPON": "رمز قسيمة",
  "promotions.kind.AUTOMATIC": "تلقائي",
  "promotions.kind.CAMPAIGN": "حملة",
  "promotions.target.DELIVERY_FEE": "أجرة التوصيل",
  "promotions.target.ORDER_VALUE": "قيمة الطلب",
  "promotions.target.FREE_DELIVERY": "توصيل مجاني",
  "promotions.method.PERCENTAGE": "نسبة مئوية",
  "promotions.method.FIXED_AMOUNT": "مبلغ ثابت",
  "promotions.funder.PLATFORM": "المنصة",
  "promotions.funder.MERCHANT": "التاجر",
  "promotions.funder.SHARED": "مشترك",

  "promotions.form.nameAr": "الاسم (بالعربية)",
  "promotions.form.nameEn": "الاسم (بالإنجليزية)",
  "promotions.form.descriptionAr": "الوصف (بالعربية)",
  "promotions.form.descriptionEn": "الوصف (بالإنجليزية)",
  "promotions.form.listedInApp": "عرض في تطبيق العميل",
  "promotions.form.kind": "نوع العرض",
  "promotions.form.target": "هدف الخصم",
  "promotions.form.method": "طريقة الحساب",
  "promotions.form.valuePercent": "النسبة المئوية",
  "promotions.form.valueAmount": "المبلغ",
  "promotions.form.valueInvalid": "أدخل قيمة موجبة صحيحة (النسب حتى 100).",
  "promotions.form.maxDiscount": "الحد الأقصى للخصم",
  "promotions.form.funding": "التمويل (من يتحمل الخصم)",
  "promotions.form.funder": "جهة التمويل",
  "promotions.form.merchantShare": "حصة التاجر",
  "promotions.form.merchantShareInvalid": "في التمويل المشترك يجب أن تكون حصة التاجر بين 0 و100 حصراً.",
  "promotions.form.platformShare": "حصة المنصة (محسوبة تلقائياً): {pct}%",
  "promotions.form.sumsTo100": "مجموع النسب 100%",
  "promotions.form.merchantsRequired": "التمويل من التاجر أو المشترك يتطلب اختيار تاجر واحد على الأقل في شرط التجار.",
  "promotions.form.conditions": "الشروط",
  "promotions.form.stackingLimits": "التكديس وحدود الاستخدام",
  "promotions.form.stackable": "قابل للتكديس",
  "promotions.form.priority": "الأولوية",
  "promotions.form.usageLimitTotal": "حد الاستخدام الكلي",
  "promotions.form.usageLimitPerCustomer": "حد الاستخدام لكل زبون",
  "promotions.form.limitInvalid": "أدخل عدداً صحيحاً لا يقل عن 1.",
  "promotions.form.startsAt": "يبدأ في",
  "promotions.form.endsAt": "ينتهي في",
  "promotions.form.required": "هذا الحقل مطلوب.",
  "promotions.form.couponCode": "رمز القسيمة",
  "promotions.form.orderValueBoundsInvalid": "الحد الأدنى يجب ألا يتجاوز الحد الأعلى.",
  "promotions.form.create": "إنشاء العرض",

  "promotions.condition.first_order": "الطلب الأول",
  "promotions.condition.customer_segment": "شريحة الزبائن",
  "promotions.condition.order_value": "حدود قيمة الطلب",
  "promotions.condition.order_value.min": "الحد الأدنى",
  "promotions.condition.order_value.max": "الحد الأعلى",
  "promotions.condition.day_of_week": "أيام الأسبوع",
  "promotions.condition.time_window": "النافذة الزمنية",
  "promotions.condition.time_window.from": "من",
  "promotions.condition.time_window.to": "إلى",
  "promotions.condition.time_window.wraps": "تمتد عبر منتصف الليل",
  "promotions.condition.zones": "مناطق الاستلام",
  "promotions.condition.merchant_categories": "تصنيفات الطلب",
  "promotions.condition.merchants": "التجار",
  "promotions.segment.NEW": "جديد",
  "promotions.segment.RETURNING": "عائد",
  "promotions.segment.DORMANT": "خامل",
  "promotions.weekday.MON": "الاثنين",
  "promotions.weekday.TUE": "الثلاثاء",
  "promotions.weekday.WED": "الأربعاء",
  "promotions.weekday.THU": "الخميس",
  "promotions.weekday.FRI": "الجمعة",
  "promotions.weekday.SAT": "السبت",
  "promotions.weekday.SUN": "الأحد",

  "promotions.detail.tabs.label": "أقسام تفاصيل العرض",
  "promotions.detail.tabs.definition": "التعريف",
  "promotions.detail.tabs.codes": "رموز القسائم",
  "promotions.detail.tabs.applications": "سجل التطبيق",
  "promotions.detail.tabs.funding": "تقرير التمويل",
  "promotions.detail.redemptions": "مرات الاستخدام: {count} / {limit}",
  "promotions.detail.unlimited": "بلا حد",
  "promotions.lifecycle.reason": "السبب (اختياري، يُسجَّل في التدقيق)",
  "promotions.lifecycle.ACTIVE": "تفعيل",
  "promotions.lifecycle.ACTIVE.title": "تفعيل هذا العرض؟",
  "promotions.lifecycle.PAUSED": "إيقاف مؤقت",
  "promotions.lifecycle.PAUSED.title": "إيقاف هذا العرض مؤقتاً؟",
  "promotions.lifecycle.PAUSED.description": "يتوقف تطبيقه على الطلبات الجديدة فوراً.",
  "promotions.lifecycle.ENDED": "إنهاء",
  "promotions.lifecycle.ENDED.title": "إنهاء هذا العرض؟",
  "promotions.lifecycle.ENDED.description": "الإنهاء نهائي ولا يمكن إعادة التفعيل.",
  "promotions.lifecycle.ARCHIVED": "أرشفة",
  "promotions.lifecycle.ARCHIVED.title": "أرشفة هذا العرض؟",
  "promotions.lifecycle.ARCHIVED.description": "يُزال من القوائم اليومية.",
  "promotions.delete": "حذف المسودة",
  "promotions.delete.title": "حذف هذه المسودة؟",
  "promotions.delete.description": "لا يمكن التراجع عن هذا الإجراء.",

  "promotions.codes.add.label": "رمز جديد",
  "promotions.codes.add.invalid": "3–40 خانة: أحرف وأرقام وشرطة وشرطة سفلية فقط.",
  "promotions.codes.add.submit": "إضافة الرمز",
  "promotions.codes.table.label": "رموز القسائم",
  "promotions.codes.table.code": "الرمز",
  "promotions.codes.table.state": "الحالة",
  "promotions.codes.table.created": "أُنشئ",
  "promotions.codes.active": "فعّال",
  "promotions.codes.inactive": "معطّل",
  "promotions.codes.enable": "تفعيل",
  "promotions.codes.enable.title": "تفعيل هذا الرمز؟",
  "promotions.codes.disable": "تعطيل",
  "promotions.codes.disable.title": "تعطيل هذا الرمز؟",
  "promotions.codes.disable.description": "لن يتمكن العملاء من إدخال هذا الرمز.",
  "promotions.codes.empty.title": "لا توجد رموز بعد",
  "promotions.lifecycle.codesRequired": "أضف رمز قسيمة فعّالاً قبل التفعيل.",

  "promotions.applications.table.label": "سجل تطبيق العروض",
  "promotions.applications.filter.status": "تصفية حسب حالة التطبيق",
  "promotions.applications.table.promotion": "العرض",
  "promotions.applications.table.order": "الطلب",
  "promotions.applications.table.code": "الرمز",
  "promotions.applications.table.status": "الحالة",
  "promotions.applications.table.amount": "الخصم",
  "promotions.applications.table.funding": "التمويل (التاجر / المنصة)",
  "promotions.applications.table.totals": "الإجمالي قبل ← بعد",
  "promotions.applications.table.appliedAt": "طُبِّق في",
  "promotions.applications.empty.title": "لا يوجد سجل تطبيق بعد",
  "promotions.applications.emptyFiltered.title": "لا توجد سجلات مطابقة",
  "promotions.application.status.APPLIED": "مطبَّق",
  "promotions.application.status.REVERTED": "معكوس",
  "promotions.application.status.SETTLED": "مُسوّى",
  "promotions.revert.SUPERSEDED": "حلّت محله إعادة احتساب",
  "promotions.revert.WINDOW_RECALC_REMOVED": "أُزيل ضمن نافذة التعديل",
  "promotions.revert.ORDER_CANCELLED": "أُلغي الطلب",
  "promotions.revert.ADMIN_VOID": "أبطله المشرف",

  "promotions.funding.table.label": "تقارير التمويل",
  "promotions.funding.table.promotion": "العرض",
  "promotions.funding.table.applications": "التطبيقات",
  "promotions.funding.table.reverted": "المعكوسة",
  "promotions.funding.table.totalDiscount": "إجمالي الخصم",
  "promotions.funding.table.platformFunded": "تمويل المنصة",
  "promotions.funding.table.merchantFunded": "تمويل التاجر",
  "promotions.funding.table.driverCompensation": "تعويض السائق",
  "promotions.funding.empty.title": "لا توجد بيانات تمويل",
  "promotions.funding.window": "النافذة: {from} → {to}",
  "promotions.funding.includePending": "تضمين المعلّق (مطبَّق ولم يُسوَّ بعد)",
  "promotions.funding.pending.title": "المعلّق (لم يُسوَّ بعد)",
  "promotions.funding.pending.none": "لا شيء معلّقاً في هذه النافذة.",
  "promotions.funding.byMerchant.title": "حسب التاجر",
  "promotions.funding.revertedCount": "{count} معكوسة (مستبعدة من الإجماليات)",

  "promotions.evaluate.merchant": "التاجر",
  "promotions.evaluate.zone": "منطقة الاستلام",
  "promotions.evaluate.category": "تصنيف الطلب",
  "promotions.evaluate.orderValue": "قيمة الطلب",
  "promotions.evaluate.deliveryFee": "أجرة التوصيل",
  "promotions.evaluate.coupon": "رمز القسيمة",
  "promotions.evaluate.at": "التقييم في",
  "promotions.evaluate.firstOrder": "اعتباره الطلب الأول للزبون",
  "promotions.evaluate.pick": "اختر…",
  "promotions.evaluate.none": "بدون",
  "promotions.evaluate.run": "تقييم",
  "promotions.evaluate.result.title": "النتيجة",
  "promotions.evaluate.noneApplied.title": "لا ينطبق أي خصم",
  "promotions.evaluate.split": "التمويل: التاجر {merchant} / المنصة {platform}",
  "promotions.evaluate.totalBefore": "الإجمالي قبل",
  "promotions.evaluate.totalAfter": "الإجمالي بعد",
  "promotions.evaluate.candidates.title": "العروض المدروسة",
  "promotions.evaluate.denied.title": "المعاينة غير متاحة",
  "promotions.verdict.APPLIED": "طُبِّق",
  "promotions.verdict.CONDITIONS_NOT_MET": "الشروط غير متحققة",
  "promotions.verdict.USAGE_LIMIT_REACHED": "بلغ حد الاستخدام",
  "promotions.verdict.CUSTOMER_LIMIT_REACHED": "بلغ حد الزبون",
  "promotions.verdict.MERCHANT_COFUNDING_NOT_ALLOWED": "التاجر غير مشترك في التمويل المشترك",
  "promotions.verdict.LOST_STACKING": "خسر التكديس",
  "promotions.verdict.NOT_STARTED": "لم يبدأ",
  "promotions.verdict.EXPIRED": "منتهي الصلاحية",
  "promotions.couponEcho.MATCHED": "مطابق",
  "promotions.couponEcho.NOT_FOUND": "غير موجود",
  "promotions.couponEcho.INACTIVE": "معطّل",
  "promotions.couponEcho.NOT_ELIGIBLE": "غير مؤهل",
  "promotions.couponEcho.USAGE_LIMIT_REACHED": "بلغ حد الاستخدام",
  "promotions.couponEcho.CUSTOMER_LIMIT_REACHED": "بلغ حد الزبون",
  "promotions.couponEcho.NOT_STARTED": "لم يبدأ بعد",
  "promotions.couponEcho.EXPIRED": "منتهي الصلاحية",
  "promotions.couponEcho.LOST_STACKING": "خسر التكديس",

  /* M7-106 — الإعلانات (الموجة 14) */
  /* Store categories — §14.4 admin-managed merchant categories (M7-148). */
  "storeCategories.title": "فئات المتاجر",
  "storeCategories.create": "فئة جديدة",
  "storeCategories.denied": "ليست لديك صلاحية لعرض فئات المتاجر.",
  "storeCategories.table.label": "فئات المتاجر",
  "storeCategories.table.image": "الصورة",
  "storeCategories.table.name": "الاسم",
  "storeCategories.table.icon": "مفتاح الأيقونة",
  "storeCategories.table.stores": "المتاجر",
  "storeCategories.table.status": "الحالة",
  "storeCategories.table.order": "الترتيب",
  "storeCategories.status.active": "ظاهرة",
  "storeCategories.status.hidden": "مخفية",
  "storeCategories.action.show": "إظهار",
  "storeCategories.action.hide": "إخفاء",
  "storeCategories.hide.title": "إخفاء هذه الفئة؟",
  "storeCategories.action.delete": "حذف الفئة",
  "storeCategories.reorder.up": "تحريك لأعلى",
  "storeCategories.reorder.down": "تحريك لأسفل",
  "storeCategories.reorder.failed": "لم يتم حفظ الترتيب الجديد.",
  "storeCategories.empty.title": "لا توجد فئات بعد",
  "storeCategories.toast.created": "تم إنشاء الفئة.",
  "storeCategories.toast.saved": "تم حفظ الفئة.",
  "storeCategories.toast.shown": "أصبحت الفئة ظاهرة مجدداً.",
  "storeCategories.toast.hidden": "تم إخفاء الفئة.",
  "storeCategories.toast.deleted": "تم حذف الفئة.",
  "storeCategories.delete.title": "حذف هذه الفئة؟",
  "storeCategories.delete.description": "لا يمكن التراجع عن هذا الإجراء.",
  "storeCategories.delete.inUse": "لا يمكن حذف فئة مستخدمة — أخفِها بدلاً من ذلك",
  "storeCategories.dialog.createTitle": "فئة جديدة",
  "storeCategories.dialog.editTitle": "تعديل الفئة",
  "storeCategories.form.nameAr": "الاسم بالعربية",
  "storeCategories.form.nameEn": "الاسم بالإنجليزية",
  "storeCategories.form.icon": "الأيقونة",
  "storeCategories.form.tooLong": "استخدم {max} حرفاً أو أقل.",
  "storeCategories.form.image": "صورة الفئة",
  "storeCategories.form.imagePreview": "معاينة صورة الفئة",
  "storeCategories.form.defaultOrderCategory": "فئة الطلب الافتراضية",
  "storeCategories.form.defaultOrderCategoryNone": "بدون افتراضي",
  "storeCategories.form.unknownOrderCategory": "فئة الطلب هذه لم تعد موجودة أو أنها معطّلة — اختر غيرها.",
  "storeCategories.form.visibility": "الظهور",
  "storeCategories.hideWarning": "يبقى {count} من المتاجر على هذه الفئة ويستمر ظهورها ضمن «الكل».",
  "storeCategories.image.upload": "رفع صورة",
  "storeCategories.image.replace": "استبدال الصورة",
  "storeCategories.image.remove": "إزالة الصورة",

  // M7-150 — أنواع المركبات، قرار المالك 2026-09-15 (١).
  "vehicleTypes.title": "أنواع المركبات",
  "vehicleTypes.create": "نوع مركبة جديد",
  "vehicleTypes.denied": "ليست لديك صلاحية لعرض أنواع المركبات.",
  "vehicleTypes.table.label": "أنواع المركبات",
  "vehicleTypes.table.code": "الرمز",
  "vehicleTypes.table.name": "الاسم",
  "vehicleTypes.table.drivers": "السائقون",
  "vehicleTypes.table.merchants": "المتاجر",
  "vehicleTypes.table.status": "الحالة",
  "vehicleTypes.table.order": "الترتيب",
  "vehicleTypes.status.active": "متاح",
  "vehicleTypes.status.hidden": "مخفي",
  "vehicleTypes.option.hidden": "(مخفي)",
  "vehicleTypes.action.show": "إظهار",
  "vehicleTypes.action.hide": "إخفاء",
  "vehicleTypes.hide.title": "إخفاء نوع المركبة هذا؟",
  "vehicleTypes.action.delete": "حذف نوع المركبة",
  "vehicleTypes.reorder.up": "تحريك لأعلى",
  "vehicleTypes.reorder.down": "تحريك لأسفل",
  "vehicleTypes.reorder.failed": "لم يُحفظ الترتيب الجديد.",
  "vehicleTypes.empty.title": "لا توجد أنواع مركبات بعد",
  "vehicleTypes.toast.created": "تم إنشاء نوع المركبة.",
  "vehicleTypes.toast.saved": "تم حفظ نوع المركبة.",
  "vehicleTypes.toast.shown": "أصبح نوع المركبة متاحاً من جديد.",
  "vehicleTypes.toast.hidden": "تم إخفاء نوع المركبة.",
  "vehicleTypes.toast.deleted": "تم حذف نوع المركبة.",
  "vehicleTypes.delete.title": "حذف نوع المركبة هذا؟",
  "vehicleTypes.delete.description": "لا يمكن التراجع عن هذا الإجراء.",
  "vehicleTypes.delete.inUse": "لا يمكن حذف نوع مركبة مستخدَم — أخفِه بدلاً من ذلك",
  "vehicleTypes.dialog.createTitle": "نوع مركبة جديد",
  "vehicleTypes.dialog.editTitle": "تعديل نوع المركبة",
  "vehicleTypes.form.code": "الرمز",
  "vehicleTypes.form.codeImmutable": "لا يمكن تغيير الرمز بعد إنشاء نوع المركبة.",
  "vehicleTypes.form.codeInvalid": "استخدم حروفاً كبيرة وأرقاماً وشرطة سفلية، على أن يبدأ بحرف.",
  "vehicleTypes.form.nameAr": "الاسم بالعربية",
  "vehicleTypes.form.nameEn": "الاسم بالإنجليزية",
  "vehicleTypes.form.tooLong": "استخدم {max} حرفاً أو أقل.",
  "vehicleTypes.form.visibility": "الإتاحة",
  "vehicleTypes.hideWarning": "يحتفظ {drivers} من السائقين و{stores} من المتاجر بهذا النوع ويتوقف الإسناد الجديد فقط.",

  "ads.title": "الإعلانات",
  "ads.create": "إعلان جديد",
  "ads.create.title": "إعلان جديد",
  "ads.denied": "ليست لديك صلاحية عرض الإعلانات.",
  "ads.manage.denied": "ليست لديك صلاحية إدارة الإعلانات.",
  "ads.table.label": "الإعلانات",
  "ads.table.image": "الصورة",
  "ads.table.title": "العنوان",
  "ads.table.kind": "إجراء النقر",
  "ads.table.target": "المتجر المستهدف",
  "ads.table.status": "الحالة",
  "ads.table.schedule": "الجدولة",
  "ads.table.order": "الترتيب",
  "ads.schedule.always": "دائماً",
  "ads.schedule.from": "من {date}",
  "ads.schedule.until": "حتى {date}",
  "ads.empty.title": "لا توجد إعلانات بعد",
  "ads.kind.none": "صورة فقط",
  "ads.kind.store": "يفتح متجراً",
  "ads.kind.product": "يفتح منتجاً",
  "ads.status.served": "معروض",
  "ads.status.inactive": "موقوف",
  "ads.status.scheduled": "مجدول",
  "ads.status.expired": "منتهٍ",
  "ads.status.target_hidden": "المتجر مخفي",
  "ads.reorder.up": "نقل لأعلى",
  "ads.reorder.down": "نقل لأسفل",
  "ads.reorder.failed": "فشلت إعادة الترتيب — تم تحديث القائمة من الخادم.",
  "ads.form.title": "العنوان",
  "ads.form.title.required": "أدخل عنواناً.",
  "ads.form.title.tooLong": "يجب ألا يتجاوز العنوان 120 حرفاً.",
  "ads.form.image": "الصورة",
  "ads.form.image.required": "اختر صورة.",
  "ads.form.image.type": "تُقبل صور \u2066JPEG\u2069 أو \u2066PNG\u2069 أو \u2066WebP\u2069 فقط.",
  "ads.form.image.size": "يجب ألا يتجاوز حجم الصورة 2 ميغابايت.",
  "ads.form.image.tooNarrow": "يجب ألا يقل عرض الصورة عن 600 بكسل.",
  "ads.form.image.ratioWarning": "نسبة هذه الصورة {ratio} — سيتم قصّها لتناسب إطار 2:1.",
  "ads.form.image.preview": "معاينة",
  "ads.form.image.empty": "لم تُختر صورة",
  "ads.form.kind": "إجراء النقر",
  "ads.form.store": "المتجر",
  "ads.form.store.required": "اختر المتجر الذي يفتحه هذا الإعلان.",
  "ads.form.active": "مفعّل",
  "ads.form.startsAt": "يبدأ في",
  "ads.form.endsAt": "ينتهي في",
  "ads.form.schedule.order": "يجب أن تكون النهاية بعد البداية.",
  "ads.form.submit.create": "إنشاء الإعلان",
  "ads.form.submit.save": "حفظ التغييرات",
  "ads.form.created": "تم إنشاء الإعلان",
  "ads.form.saved": "تم حفظ الإعلان",
  "ads.delete": "حذف",
  "ads.delete.title": "حذف هذا الإعلان؟",
  "ads.delete.description": "لا يمكن التراجع عن هذا الإجراء.",
  "ads.delete.done": "تم حذف الإعلان",
  "ads.detail.tabs.label": "أقسام الإعلان",
  "ads.detail.tabs.definition": "التعريف",

  // M7-182 — أقسام الشاشة الرئيسية في تطبيق العميل.
  "homeSections.title": "أقسام الرئيسية",
  "homeSections.denied": "ليست لديك صلاحية عرض أقسام الرئيسية.",
  "homeSections.empty.title": "لا توجد أقسام",
  "homeSections.section.free_delivery": "توصيل مجاني",
  "homeSections.section.discounts": "خصومات",
  "homeSections.section.request_driver": "اطلب مندوب",
  "homeSections.section.offers_banner": "لوحة العروض",
  "homeSections.status.shown": "ظاهر",
  "homeSections.status.hidden": "مخفي",
  "homeSections.switch": "ظاهر في الشاشة الرئيسية",
  "homeSections.form.titleAr": "عنوان شاشة القسم بالعربية",
  "homeSections.form.titleEn": "عنوان شاشة القسم بالإنجليزية",
  "homeSections.form.tooLong": "استخدم {max} حرفاً أو أقل.",
  "homeSections.toast.saved": "تم حفظ العناوين.",
  "homeSections.toast.shown": "تم إظهار القسم.",
  "homeSections.toast.hidden": "تم إخفاء القسم.",
  "homeSections.toast.toggleFailed": "لم يتغير الظهور.",
  "homeSections.image": "صورة القسم",
  "homeSections.image.preview": "معاينة صورة القسم",
  "homeSections.image.empty": "لا توجد صورة",
  "homeSections.image.upload": "رفع صورة",
  "homeSections.image.replace": "استبدال الصورة",
  "homeSections.image.remove": "إزالة الصورة",
  "homeSections.image.remove.title": "إزالة صورة القسم؟",
  "homeSections.image.remove.description": "سيعود التطبيق إلى العنوان العادي لهذا القسم.",
  "homeSections.image.remove.description.request_driver": "سيختفي هذا المدخل من الشاشة الرئيسية للتطبيق.",
  "homeSections.image.remove.description.offers_banner": "ستختفي اللوحة من أعلى تبويب العروض.",
  "homeSections.meta.updated": "آخر تعديل {date}",
  "homeSections.meta.by": "بواسطة {name}",

  // M7-253 — store collections («المجموعات»), marketing.yaml v1.4.0.
  "collections.title": "المجموعات",
  "collections.denied": "ليست لديك صلاحية عرض المجموعات.",
  "collections.create": "مجموعة جديدة",
  "collections.empty.title": "لا توجد مجموعات",
  "collections.table.label": "المجموعات",
  "collections.table.image": "الصورة",
  "collections.table.title": "العنوان",
  "collections.table.stores": "المتاجر",
  "collections.table.shown": "الظهور",
  "collections.table.order": "الترتيب",
  "collections.status.shown": "ظاهرة",
  "collections.status.hidden": "مخفية",
  "collections.switch": "ظاهرة في الشاشة الرئيسية",
  "collections.reorder.up": "نقل لأعلى",
  "collections.reorder.down": "نقل لأسفل",
  "collections.reorder.failed": "فشل إعادة الترتيب — تم تحديث القائمة.",
  "collections.toast.shown": "تم إظهار المجموعة.",
  "collections.toast.hidden": "تم إخفاء المجموعة.",
  "collections.toast.toggleFailed": "لم يتغير الظهور.",
  "collections.toast.created": "تم إنشاء المجموعة.",
  "collections.toast.saved": "تم حفظ المجموعة.",
  "collections.toast.deleted": "تم حذف المجموعة.",
  "collections.toast.headingSaved": "تم حفظ العنوان.",
  "collections.heading.title": "عنوان الصف",
  "collections.heading.ar": "العنوان بالعربية",
  "collections.heading.en": "العنوان بالإنجليزية",
  "collections.form.titleAr": "عنوان المجموعة بالعربية",
  "collections.form.titleEn": "عنوان المجموعة بالإنجليزية",
  "collections.form.visibility": "الظهور",
  "collections.form.tooLong": "استخدم {max} حرفاً أو أقل.",
  "collections.form.stores": "المتاجر",
  "collections.form.stores.count": "{count} من {max} متجراً",
  "collections.form.stores.tooMany": "اختر {max} متجراً أو أقل.",
  "collections.form.stores.empty": "لم تُختَر متاجر",
  "collections.form.stores.search": "ابحث عن متجر بالاسم",
  "collections.form.stores.results": "المتاجر المطابقة",
  "collections.form.stores.noResults": "لا توجد متاجر مطابقة",
  "collections.form.stores.add": "إضافة {name}",
  "collections.form.stores.remove": "إزالة {name}",
  "collections.form.stores.moveUp": "نقل {name} لأعلى",
  "collections.form.stores.moveDown": "نقل {name} لأسفل",
  "collections.form.stores.failed": "تعذّر تحميل المتاجر.",
  "collections.form.store.notVisible": "غير ظاهر في تطبيق العملاء",
  "collections.form.store.missing": "المتجر غير موجود",
  "collections.form.store.unknown": "متجر غير معروف",
  "collections.image": "صورة المجموعة",
  "collections.image.preview": "معاينة صورة المجموعة",
  "collections.image.empty": "لا توجد صورة",
  "collections.image.upload": "رفع صورة",
  "collections.image.replace": "استبدال الصورة",
  "collections.dialog.createTitle": "مجموعة جديدة",
  "collections.dialog.editTitle": "تعديل المجموعة",
  "collections.action.delete": "حذف المجموعة",
  "collections.delete.title": "حذف المجموعة؟",
  "collections.delete.description": "ستختفي المجموعة من الشاشة الرئيسية للتطبيق.",

  "categoryLayout.title": "تخطيط الأقسام",
  "categoryLayout.denied": "ليست لديك صلاحية عرض تخطيط الأقسام.",
  "categoryLayout.category": "القسم",
  "categoryLayout.category.none": "لا توجد أقسام رئيسية",
  "categoryLayout.category.failed": "تعذّر تحميل الأقسام الرئيسية.",
  "categoryLayout.labelMode": "عرض البلاطات",
  "categoryLayout.labelMode.image_only": "الصورة فقط",
  "categoryLayout.labelMode.image_with_name": "الصورة مع الاسم",
  "categoryLayout.toast.labelModeSaved": "تم حفظ طريقة العرض.",
  "categoryLayout.toast.labelModeFailed": "لم تتغير طريقة العرض.",
  "categoryLayout.table.image": "الصورة",
  "categoryLayout.table.title": "العنوان",
  "categoryLayout.table.stores": "المتاجر",
  "categoryLayout.table.shown": "الظهور",
  "categoryLayout.table.order": "الترتيب",
  "categoryLayout.status.shown": "ظاهر",
  "categoryLayout.status.hidden": "مخفي",
  "categoryLayout.reorder.up": "نقل لأعلى",
  "categoryLayout.reorder.down": "نقل لأسفل",
  "categoryLayout.form.titleAr": "العنوان بالعربية",
  "categoryLayout.form.titleEn": "العنوان بالإنجليزية",
  "categoryLayout.form.visibility": "الظهور",
  "categoryLayout.form.tooLong": "استخدم {max} حرفاً أو أقل.",
  "categoryLayout.form.stores": "المتاجر",
  "categoryLayout.form.stores.tooMany": "اختر {max} متجراً أو أقل.",
  "categoryLayout.form.position": "الموضع",
  "categoryLayout.form.position.integer": "أدخل عدداً صحيحاً.",
  "categoryLayout.form.position.max": "استخدم {max} أو أقل.",
  "categoryLayout.subcategories.title": "الأقسام الفرعية",
  "categoryLayout.subcategories.create": "قسم فرعي جديد",
  "categoryLayout.subcategories.empty.title": "لا توجد أقسام فرعية",
  "categoryLayout.subcategories.table.label": "الأقسام الفرعية",
  "categoryLayout.subcategories.switch": "ظاهر في القسم",
  "categoryLayout.subcategories.reorder.failed": "فشل إعادة الترتيب — تم تحديث القائمة.",
  "categoryLayout.subcategories.toast.shown": "تم إظهار القسم الفرعي.",
  "categoryLayout.subcategories.toast.hidden": "تم إخفاء القسم الفرعي.",
  "categoryLayout.subcategories.toast.toggleFailed": "لم يتغير الظهور.",
  "categoryLayout.subcategories.toast.created": "تم إنشاء القسم الفرعي.",
  "categoryLayout.subcategories.toast.saved": "تم حفظ القسم الفرعي.",
  "categoryLayout.subcategories.toast.deleted": "تم حذف القسم الفرعي.",
  "categoryLayout.subcategories.dialog.createTitle": "قسم فرعي جديد",
  "categoryLayout.subcategories.dialog.editTitle": "تعديل القسم الفرعي",
  "categoryLayout.subcategories.action.delete": "حذف القسم الفرعي",
  "categoryLayout.subcategories.delete.title": "حذف القسم الفرعي؟",
  "categoryLayout.subcategories.delete.description": "ستختفي البلاطة من شاشة القسم في التطبيق.",
  "categoryLayout.subcategories.image": "صورة البلاطة",
  "categoryLayout.subcategories.image.preview": "معاينة صورة البلاطة",
  "categoryLayout.subcategories.image.empty": "لا توجد صورة",
  "categoryLayout.subcategories.image.upload": "رفع صورة",
  "categoryLayout.subcategories.image.replace": "استبدال الصورة",
  "categoryLayout.slots.title": "الأقسام الديناميكية والإعلانات",
  "categoryLayout.slots.create": "خانة جديدة",
  "categoryLayout.slots.empty.title": "لا توجد أقسام ديناميكية أو إعلانات",
  "categoryLayout.slots.table.label": "الأقسام الديناميكية والإعلانات",
  "categoryLayout.slots.table.kind": "النوع",
  "categoryLayout.slots.table.position": "الموضع",
  "categoryLayout.slots.table.content": "المحتوى",
  "categoryLayout.slots.table.stores": "المتاجر",
  "categoryLayout.slots.table.target": "المتجر المستهدف",
  "categoryLayout.slots.kind.section": "قسم ديناميكي",
  "categoryLayout.slots.kind.banner": "إعلان",
  "categoryLayout.slots.position.start": "قبل القائمة",
  "categoryLayout.slots.position.after": "بعد {count} من المتاجر",
  "categoryLayout.slots.switch": "ظاهرة في القسم",
  "categoryLayout.slots.reorder.failed": "فشل إعادة الترتيب — تم تحديث القائمة.",
  "categoryLayout.slots.toast.shown": "تم إظهار الخانة.",
  "categoryLayout.slots.toast.hidden": "تم إخفاء الخانة.",
  "categoryLayout.slots.toast.toggleFailed": "لم يتغير الظهور.",
  "categoryLayout.slots.toast.created": "تم إنشاء الخانة.",
  "categoryLayout.slots.toast.saved": "تم حفظ الخانة.",
  "categoryLayout.slots.toast.deleted": "تم حذف الخانة.",
  "categoryLayout.slots.dialog.createTitle": "خانة جديدة",
  "categoryLayout.slots.dialog.editTitle.section": "تعديل القسم الديناميكي",
  "categoryLayout.slots.dialog.editTitle.banner": "تعديل الإعلان",
  "categoryLayout.slots.action.delete": "حذف الخانة",
  "categoryLayout.slots.delete.title": "حذف الخانة؟",
  "categoryLayout.slots.delete.description": "سيختفي القسم الديناميكي أو الإعلان من شاشة القسم في التطبيق.",
  "categoryLayout.slots.form.kind": "النوع",
  "categoryLayout.slots.form.target": "المتجر المستهدف",
  "categoryLayout.slots.form.target.none": "بلا متجر مستهدف",
  "categoryLayout.slots.image": "صورة الإعلان",
  "categoryLayout.slots.image.preview": "معاينة صورة الإعلان",
  "categoryLayout.slots.image.empty": "لا توجد صورة",
  "categoryLayout.slots.image.upload": "رفع صورة",
  "categoryLayout.slots.image.replace": "استبدال الصورة",

  /* M7-269 — home layout */
  "homeLayout.title": "تخطيط الرئيسية",
  "homeLayout.denied": "ليست لديك صلاحية عرض تخطيط الرئيسية.",
  "homeLayout.table.label": "عناصر الرئيسية",
  "homeLayout.table.order": "الترتيب",
  "homeLayout.table.kind": "النوع",
  "homeLayout.table.item": "العنصر",
  "homeLayout.table.content": "المحتوى",
  "homeLayout.table.shown": "الظهور",
  "homeLayout.empty.title": "لا توجد عناصر",
  "homeLayout.reorder.up": "نقل لأعلى",
  "homeLayout.reorder.down": "نقل لأسفل",
  "homeLayout.reorder.failed": "فشل إعادة الترتيب — تم تحديث القائمة.",
  "homeLayout.builtin.free_delivery": "توصيل مجاني",
  "homeLayout.builtin.discounts": "خصومات",
  "homeLayout.builtin.collections": "المجموعات",
  "homeLayout.builtin.open": "فتح",
  "homeLayout.kind.builtin": "ثابت",
  "homeLayout.kind.image_section": "قسم بصورة",
  "homeLayout.kind.title_section": "قسم بعنوان",
  "homeLayout.kind.banner_group": "مجموعة إعلانات",
  "homeLayout.stores": "{count} متجر",
  "homeLayout.stores.one": "متجر واحد",
  "homeLayout.banners.count": "{count} إعلان",
  "homeLayout.banners.count.one": "إعلان واحد",
  "homeLayout.rotate": "{count} ث",
  "homeLayout.status.shown": "ظاهر",
  "homeLayout.status.hidden": "مخفي",
  "homeLayout.switch": "ظاهر في الرئيسية",
  "homeLayout.toast.shown": "تم إظهار العنصر.",
  "homeLayout.toast.hidden": "تم إخفاء العنصر.",
  "homeLayout.toast.toggleFailed": "لم يتغير الظهور.",
  "homeLayout.toast.created": "تم إنشاء العنصر.",
  "homeLayout.toast.saved": "تم حفظ العنصر.",
  "homeLayout.toast.deleted": "تم حذف العنصر.",
  "homeLayout.add": "إضافة",
  "homeLayout.add.menu": "إضافة عنصر إلى الرئيسية",
  "homeLayout.add.image_section": "قسم بصورة",
  "homeLayout.add.title_section": "قسم بعنوان",
  "homeLayout.add.banner_group": "مجموعة إعلانات",
  "homeLayout.action.delete": "حذف العنصر",
  "homeLayout.delete.title": "حذف العنصر؟",
  "homeLayout.delete.description": "سيختفي العنصر من الشاشة الرئيسية في التطبيق.",
  "homeLayout.dialog.createTitle.image_section": "قسم بصورة جديد",
  "homeLayout.dialog.createTitle.title_section": "قسم بعنوان جديد",
  "homeLayout.dialog.createTitle.banner_group": "مجموعة إعلانات جديدة",
  "homeLayout.dialog.editTitle.image_section": "تعديل القسم بصورة",
  "homeLayout.dialog.editTitle.title_section": "تعديل القسم بعنوان",
  "homeLayout.dialog.editTitle.banner_group": "تعديل مجموعة الإعلانات",
  "homeLayout.form.kind": "النوع",
  "homeLayout.form.titleAr": "الاسم بالعربية",
  "homeLayout.form.titleEn": "الاسم بالإنجليزية",
  "homeLayout.form.visibility": "الظهور",
  "homeLayout.form.tooLong": "استخدم {max} حرفاً أو أقل.",
  "homeLayout.form.stores": "المتاجر",
  "homeLayout.form.stores.tooMany": "اختر {max} متجراً أو أقل.",
  "homeLayout.form.rotate": "مدة التبديل (ثانية)",
  "homeLayout.form.rotate.integer": "أدخل عدداً صحيحاً.",
  "homeLayout.form.rotate.range": "استخدم من {min} إلى {max}.",
  "homeLayout.image": "صورة القسم",
  "homeLayout.image.preview": "معاينة صورة القسم",
  "homeLayout.image.empty": "لا توجد صورة",
  "homeLayout.image.upload": "رفع صورة",
  "homeLayout.image.replace": "استبدال الصورة",
  "homeLayout.banners.title": "الإعلانات",
  "homeLayout.banners.add": "إضافة إعلان",
  "homeLayout.banners.empty": "لا توجد إعلانات",
  "homeLayout.banners.target.none": "بلا متجر مستهدف",
  "homeLayout.banners.switch": "ظاهر في التبديل",
  "homeLayout.banners.moveUp": "نقل الإعلان {index} لأعلى",
  "homeLayout.banners.moveDown": "نقل الإعلان {index} لأسفل",
  "homeLayout.banners.edit": "تعديل الإعلان {index}",
  "homeLayout.banners.delete": "حذف الإعلان {index}",
  "homeLayout.banners.reorder.failed": "فشل إعادة الترتيب — تم تحديث القائمة.",
  "homeLayout.banners.toast.shown": "تم إظهار الإعلان.",
  "homeLayout.banners.toast.hidden": "تم إخفاء الإعلان.",
  "homeLayout.banners.toast.toggleFailed": "لم يتغير الظهور.",
  "homeLayout.banners.toast.created": "تمت إضافة الإعلان.",
  "homeLayout.banners.toast.saved": "تم حفظ الإعلان.",
  "homeLayout.banners.toast.deleted": "تم حذف الإعلان.",
  "homeLayout.banners.dialog.createTitle": "إعلان جديد",
  "homeLayout.banners.dialog.editTitle": "تعديل الإعلان",
  "homeLayout.banners.delete.title": "حذف الإعلان؟",
  "homeLayout.banners.delete.description": "سيخرج الإعلان من التبديل في الشاشة الرئيسية في التطبيق.",
  "homeLayout.banners.form.image": "صورة الإعلان",
  "homeLayout.banners.form.image.preview": "معاينة صورة الإعلان",
  "homeLayout.banners.form.image.empty": "لا توجد صورة",
  "homeLayout.banners.form.image.upload": "رفع صورة",
  "homeLayout.banners.form.image.replace": "استبدال الصورة",
  "homeLayout.banners.form.image.required": "اختر صورة.",
  "homeLayout.banners.form.target": "المتجر المستهدف",
  "homeLayout.banners.form.visibility": "الظهور",

  /* M7-309 — privacy policy (admin legal surface, §14.4 / §14.5) */
  "legal.title": "سياسة الخصوصية",
  "legal.denied": "ليست لديك صلاحية إدارة سياسة الخصوصية.",
  "legal.updated": "آخر تحديث",
  "legal.tabs": "اللغة",
  "legal.tab.ar": "العربية",
  "legal.tab.en": "English",
  "legal.tab.invalid": "يحتوي خطأ",
  "legal.body.ar": "النص العربي (\u2066Markdown\u2069)",
  "legal.body.en": "النص الإنجليزي (\u2066Markdown\u2069)",
  "legal.preview": "المعاينة",
  "legal.error.tooLong": "الحد الأقصى {max} حرف.",
  "legal.reset": "تجاهل التغييرات",
  "legal.save": "حفظ",
  "legal.toast.saved": "تم حفظ سياسة الخصوصية.",
  "legal.link.label": "الرابط العام",
  "legal.link.copy": "نسخ الرابط",
  "legal.link.copied": "تم نسخ الرابط.",
  "legal.link.copyFailed": "تعذّر نسخ الرابط.",

  /* M7-301 — الإعلانات المنبثقة (marketing.yaml v1.7.0, §14.4 / §14.7) */
  "popups.title": "الإعلانات المنبثقة",
  "popups.denied": "ليست لديك صلاحية إدارة الإعلانات المنبثقة.",
  "popups.table.label": "الإعلانات المنبثقة",
  "popups.table.order": "الترتيب",
  "popups.table.popup": "الإعلان",
  "popups.table.enabled": "مفعّل",
  "popups.table.frequency": "في اليوم",
  "popups.table.target": "المتجر المستهدف",
  "popups.table.schedule": "الجدولة",
  "popups.table.servable": "العرض",
  "popups.empty.title": "لا توجد إعلانات منبثقة",
  "popups.add": "إضافة إعلان",
  "popups.reorder.up": "نقل للأعلى",
  "popups.reorder.down": "نقل للأسفل",
  "popups.reorder.failed": "فشل إعادة الترتيب — تم تحديث القائمة.",
  "popups.switch": "مفعّل",
  "popups.status.enabled": "مفعّل",
  "popups.status.disabled": "موقوف",
  "popups.servable.yes": "معروض",
  "popups.servable.no": "غير معروض",
  "popups.reason.disabled": "موقوف",
  "popups.reason.no_image": "بلا صورة",
  "popups.reason.scheduled": "مجدول",
  "popups.reason.expired": "منتهٍ",
  "popups.frequency.unlimited": "بلا حد",
  "popups.frequency.one": "مرة في اليوم",
  "popups.frequency.two": "مرتان في اليوم",
  "popups.frequency.few": "{count} مرات في اليوم",
  "popups.frequency.many": "{count} مرة في اليوم",
  "popups.frequency.unit": "/ يوم",
  "popups.target.none": "بلا متجر مستهدف",
  "popups.target.hidden": "المتجر مخفي",
  "popups.schedule.always": "دائماً",
  "popups.schedule.from": "من {date}",
  "popups.schedule.until": "حتى {date}",
  "popups.toast.enabled": "تم تفعيل الإعلان.",
  "popups.toast.disabled": "تم إيقاف الإعلان.",
  "popups.toast.toggleFailed": "لم تتغير الحالة.",
  "popups.toast.created": "تم إنشاء الإعلان.",
  "popups.toast.saved": "تم حفظ الإعلان.",
  "popups.toast.deleted": "تم حذف الإعلان.",
  "popups.action.delete": "حذف الإعلان",
  "popups.delete.title": "حذف الإعلان؟",
  "popups.delete.description": "سيتوقف الإعلان عن الظهور في التطبيق.",
  "popups.dialog.createTitle": "إعلان منبثق جديد",
  "popups.dialog.editTitle": "تعديل الإعلان المنبثق",
  "popups.form.title": "العنوان",
  "popups.form.tooLong": "استخدم {max} حرفاً أو أقل.",
  "popups.form.enabled": "الحالة",
  "popups.form.frequency": "مرات الظهور في اليوم",
  "popups.form.frequency.integer": "أدخل عدداً صحيحاً.",
  "popups.form.frequency.range": "استخدم من {min} إلى {max}.",
  "popups.form.target": "المتجر المستهدف",
  "popups.form.startsAt": "يبدأ",
  "popups.form.endsAt": "ينتهي",
  "popups.form.schedule.order": "يجب أن تكون النهاية بعد البداية.",
  "popups.image": "صورة الإعلان",
  "popups.image.preview": "معاينة صورة الإعلان",
  "popups.image.empty": "لا توجد صورة",
  "popups.image.upload": "رفع صورة",
  "popups.image.replace": "استبدال الصورة",

  "merchantPicker.search.label": "ابحث عن متجر بالاسم أو الرقم",
  "merchantPicker.search.placeholder": "اسم المتجر أو رقمه",
  "merchantPicker.results": "المتاجر المطابقة",
  "merchantPicker.count": "{count} متجر",
  "merchantPicker.count.one": "{count} متجر",
  "merchantPicker.truncated": "عرض {shown} من {total} — ضيّق البحث لرؤية البقية",
  "merchantPicker.empty.title": "لا يوجد متجر مطابق",
  "merchantPicker.failed": "تعذّر تحميل دليل المتاجر.",
  "merchantPicker.change": "تغيير",

  /* M6-07 — مفاتيح الميزات (§14.4، §13، R10) */
  "flags.title": "مفاتيح الميزات",
  "flags.table.label": "مفاتيح الميزات",
  "flags.table.flag": "المفتاح",
  "flags.table.scope": "النطاق",
  "flags.table.state": "الحالة",
  "flags.table.overrides": "التخصيصات",
  "flags.table.nonDefault": "يخالف الافتراضي",
  "flags.scope.SYSTEM": "النظام",
  "flags.scope.MERCHANT": "لكل تاجر",
  "flags.state.on": "مفعّل",
  "flags.state.off": "معطّل",
  "flags.action.enable": "تفعيل",
  "flags.action.disable": "تعطيل",
  "flags.action.overrides": "التخصيصات",
  "flags.packageFeature.badge": "ميزة باقة — للقراءة فقط",
  "flags.confirm.enable.title": "تفعيل هذه الميزة؟",
  "flags.confirm.disable.title": "تعطيل هذه الميزة؟",
  "flags.confirm.disable.description": "تُعطَّل «{flag}» فوراً على مستوى المنصة.",
  "flags.overrides.title": "تخصيصات التجار — {flag}",
  "flags.overrides.merchant": "التاجر",
  "flags.overrides.pick": "اختر…",
  "flags.overrides.state": "حالة التخصيص",
  "flags.overrides.reason": "السبب (يُسجَّل في التدقيق)",
  "flags.overrides.save": "حفظ التخصيص",
  "flags.overrides.remove": "إزالة التخصيص",
  "flags.overrides.saved": "حُفظ التخصيص.",
  "flags.overrides.removed": "أُزيل التخصيص — تسري حالة النظام مجدداً.",
  "flags.empty.title": "لا توجد مفاتيح",

  /* M6-07 — إعدادات النظام (§13، §14.4) */
  "settings.title": "إعدادات النظام",
  "settings.search": "بحث",
  "settings.search.placeholder": "المفتاح أو الوصف",
  "settings.loading": "جارٍ تحميل الإعدادات…",
  "settings.empty.title": "لا توجد إعدادات مطابقة",
  "settings.masked": "حسّاس — مخفي",
  "settings.window.badge": "نافذة التعديل",
  "settings.edit.title": "تعديل الإعداد",
  "settings.edit.value": "القيمة",
  "settings.edit.reset": "إعادة إلى الافتراضي",
  "settings.edit.default": "الافتراضي المزروع: {value}",
  "settings.edit.invalidInt": "أدخل عدداً صحيحاً.",
  "settings.edit.invalidDecimal": "أدخل عدداً عشرياً.",
  "settings.edit.invalidJson": "أدخل كائن \u2066JSON\u2069 صالحاً.",
  "settings.edit.notAnOption": "اختر أحد الخيارات المسموحة.",
  "settings.edit.tooLong": "الحد الأقصى {max} حرفاً.",
  "settings.edit.outOfRange": "القيمة خارج المدى المسموح.",
  "settings.confirm.title": "تطبيق هذا الإعداد؟",
  "settings.confirm.description": "{key}: {from} ← {to} يسري فوراً على مستوى المنصة.",
  "settings.reset.title": "إعادة إلى الافتراضي؟",
  "settings.reset.description": "يعود {key} فوراً إلى افتراضيه المزروع ({value}).",
  "settings.providersEntry.open": "إدارة المزودين",

  /* M6-07 — مزودو الدفع (§5.2، §14.4) */
  "providers.title": "مزودو الدفع",
  "providers.table.label": "مزودو الدفع",
  "providers.table.provider": "المزود",
  "providers.table.kind": "النوع",
  "providers.table.state": "الحالة",
  "providers.table.refunds": "الاسترجاع",
  "providers.table.config": "بيانات الاعتماد",
  "providers.kind.INTERNAL": "داخلي",
  "providers.kind.ELECTRONIC": "إلكتروني",
  "providers.enabled": "مفعّل",
  "providers.disabled": "معطّل",
  "providers.refunds.yes": "مدعوم",
  "providers.config.set": "مضبوطة",
  "providers.config.none": "غير مضبوطة",
  "providers.create": "تسجيل مزود",
  "providers.action.enable": "تفعيل",
  "providers.action.disable": "تعطيل",
  "providers.confirm.enable.title": "تفعيل هذا المزود؟",
  "providers.confirm.disable.title": "تعطيل هذا المزود؟",
  "providers.confirm.disable.description": "يتوقف «{provider}» فوراً عن قبول المعاملات الجديدة.",
  "providers.form.createTitle": "تسجيل مزود",
  "providers.form.editTitle": "تعديل المزود",
  "providers.form.key": "معرّف المزود",
  "providers.form.keyInvalid": "أحرف صغيرة وأرقام وشرطة سفلية فقط (بحد أقصى 48).",
  "providers.form.kind": "النوع",
  "providers.form.displayAr": "الاسم المعروض (بالعربية)",
  "providers.form.displayEn": "الاسم المعروض (بالإنجليزية)",
  "providers.form.config": "بيانات الاعتماد (كتابة فقط)",
  "providers.form.config.configured": "بيانات الاعتماد مضبوطة ولا تُعرض أبداً — إدخال قيم جديدة يستبدلها، وتركها فارغة يبقيها.",
  "providers.form.apiKey": "مفتاح API",
  "providers.form.apiSecret": "سر API",
  "providers.empty.title": "لا يوجد مزودون",

  "apiError.VALIDATION_FAILED": "البيانات المدخلة غير صالحة",
  "apiError.AUTH_INVALID_CREDENTIALS": "بيانات الدخول غير صحيحة",
  "apiError.AUTH_TOKEN_INVALID": "جلسة غير صالحة، الرجاء تسجيل الدخول مجدداً",
  "apiError.AUTH_TOKEN_EXPIRED": "انتهت صلاحية الجلسة",
  "apiError.AUTH_ACCOUNT_SUSPENDED": "تم إيقاف هذا الحساب",
  "apiError.REFRESH_INVALID": "جلسة غير صالحة",
  "apiError.REFRESH_EXPIRED": "انتهت صلاحية الجلسة",
  "apiError.REFRESH_REUSE_DETECTED": "تم إنهاء الجلسة لأسباب أمنية",
  "apiError.PERMISSION_DENIED": "ليست لديك صلاحية لهذا الإجراء",
  "apiError.RESOURCE_NOT_FOUND": "العنصر غير موجود",
  "apiError.RATE_LIMITED": "طلبات كثيرة، حاول لاحقاً",
  "apiError.ZONE_NAME_TAKEN": "اسم المنطقة مستخدم مسبقاً",
  "apiError.ZONE_ACTIVE": "يجب تعطيل المنطقة قبل حذفها",
  "apiError.ZONE_DISABLED": "هذه المنطقة غير مفعّلة حالياً",
  "apiError.BRANCH_LOCATION_MISSING": "يجب تحديد موقع المتجر على الخريطة أولاً",
  "apiError.OWNER_ALREADY_HAS_MERCHANT": "هذا المستخدم يملك متجراً مسجلاً بالفعل",
  "apiError.PHONE_TAKEN": "رقم الهاتف مسجل بحساب موجود مسبقاً — اربط المستخدم الحالي بدلاً من إنشاء حساب جديد",
  "apiError.APPLICATION_ALREADY_DECIDED": "تم البت في هذا الطلب مسبقاً",
  "apiError.MULTI_BRANCH_DISABLED": "المنصة تعمل بنظام الفرع الواحد — لا يمكن إضافة فرع ثانٍ",
  "apiError.FEATURE_DISABLED": "هذه الميزة موقوفة على مستوى المنصة",
  "apiError.BRANCH_ZONE_INVALID": "المنطقة المحددة غير متاحة",
  "apiError.STAFF_USER_NOT_FOUND": "لا يوجد مستخدم مسجل بهذا الرقم",
  "apiError.STAFF_ALREADY_EMPLOYED": "هذا المستخدم مضاف مسبقاً لهذا المتجر",
  "apiError.NO_OWNER_ACCOUNT": "لا يوجد حساب مالك",
  "apiError.MERCHANT_NOT_READY_FOR_ORDERS": "أكمل تفعيل المتجر وتثبيت موقعه على الخريطة أولاً",
  "apiError.THEME_VERSION_CONFLICT": "تم تعديل السمة من جهة أخرى — أعد التحميل ثم كرر التعديل",
  "apiError.INVALID_TRANSITION": "لا يمكن تغيير حالة الطلب من وضعها الحالي",
  "apiError.CANCELLATION_NOT_ALLOWED": "لا يمكن إلغاء الطلب في هذه المرحلة",
  "apiError.ORDER_NOT_EDITABLE": "لا يمكن تعديل الطلب بعد الاستلام",
  "apiError.DRIVER_BUSY": "لدى السائق طلب نشط بالفعل",
  "apiError.DRIVER_NOT_ELIGIBLE": "السائق غير متاح للإسناد",
  "apiError.DISPATCH_IN_PROGRESS": "يتم عرض هذا الطلب على السائقين حالياً",
  // promotions.yaml + settings.yaml + payments.yaml (M6-07)
  "apiError.PROMOTION_NOT_EDITABLE": "لا يمكن تعديل عرض بعد تفعيله",
  "apiError.PROMOTION_NOT_DELETABLE": "لا يمكن حذف عرض له سجل استخدام",
  "apiError.PROMOTION_STATUS_INVALID": "انتقال حالة غير مسموح",
  "apiError.PROMOTION_KIND_MISMATCH": "أكواد القسائم متاحة لعروض الكوبون فقط",
  "apiError.FUNDING_SPLIT_INVALID": "نسب تمويل الخصم غير صحيحة",
  "apiError.COUPON_CODE_DUPLICATE": "رمز القسيمة مستخدم بالفعل",
  "apiError.SETTING_VALUE_INVALID": "قيمة الإعداد غير صالحة",
  "apiError.FLAG_NOT_MERCHANT_SCOPED": "هذه الميزة لا تقبل تخصيصاً لكل تاجر",
  "apiError.PROVIDER_KEY_TAKEN": "معرّف المزود مستخدم مسبقاً",
  "apiError.ROLE_KEY_TAKEN": "معرّف الدور مستخدم مسبقاً",
  "apiError.ROLE_SYSTEM_IMMUTABLE": "لا يمكن تعديل أدوار النظام",
  "apiError.ROLE_IN_USE": "هذا الدور مُسند لمستخدمين حالياً",
  "apiError.DRIVER_USER_NOT_FOUND": "لا يوجد حساب مسجل بهذا الرقم",
  "apiError.DRIVER_ALREADY_EXISTS": "هذا الحساب لديه ملف سائق بالفعل",
  "apiError.DRIVER_BLOCK_REASON_RESERVED": "سبب الحظر هذا محجوز لنظام المحافظ",
  "apiError.DOCUMENT_ALREADY_REVIEWED": "تمت مراجعة هذه الوثيقة مسبقاً",
  "apiError.DRIVER_PLAN_INVALID": "بيانات خطة السائق غير صالحة",
  "apiError.BANNER_KIND_NOT_AVAILABLE": "إعلانات المنتجات متاحة في المرحلة الثانية",
  "apiError.BANNER_TARGET_NOT_FOUND": "المتجر المستهدف غير موجود",
  "apiError.BANNER_ORDER_INCOMPLETE": "قائمة الترتيب يجب أن تضم جميع الإعلانات مرة واحدة",
  "apiError.COLLECTION_ORDER_INCOMPLETE": "قائمة الترتيب يجب أن تضم جميع المجموعات مرة واحدة",
  "apiError.SUBCATEGORY_ORDER_INCOMPLETE": "قائمة الترتيب يجب أن تضم جميع الأقسام الفرعية مرة واحدة",
  "apiError.SLOT_ORDER_INCOMPLETE": "قائمة الترتيب يجب أن تضم جميع الخانات مرة واحدة",
  "apiError.SLOT_KIND_MISMATCH": "هذا الإجراء متاح لخانات الإعلان فقط",
  "apiError.HOME_ORDER_INCOMPLETE": "قائمة الترتيب يجب أن تضم جميع عناصر الرئيسية مرة واحدة",
  "apiError.POPUP_ORDER_INCOMPLETE": "قائمة الترتيب يجب أن تضم جميع النوافذ الإعلانية مرة واحدة",
  "apiError.HOME_BANNER_ORDER_INCOMPLETE": "قائمة الترتيب يجب أن تضم جميع إعلانات المجموعة مرة واحدة",
  "apiError.HOME_ENTRY_KIND_MISMATCH": "هذا الإجراء لا ينطبق على نوع هذا العنصر",
  "apiError.MENU_ORDER_INCOMPLETE": "قائمة الترتيب يجب أن تضم جميع صور القائمة مرة واحدة",
  "apiError.CATEGORY_IN_USE": "لا يمكن حذف الفئة لأن هناك متاجر مرتبطة بها — أخفها بدلاً من ذلك",
  "apiError.CATEGORY_ORDER_INCOMPLETE": "يجب أن يتضمن الترتيب الجديد كل الفئات الحالية مرة واحدة بالضبط",
  "apiError.VEHICLE_TYPE_IN_USE": "لا يزال سائقون أو متاجر يستخدمون نوع المركبة هذا — أخفِه بدل حذفه",
  "apiError.VEHICLE_TYPE_CODE_TAKEN": "رمز نوع المركبة هذا مستخدم بالفعل",
  "apiError.VEHICLE_TYPE_ORDER_INCOMPLETE": "يجب أن يتضمن الترتيب كل أنواع المركبات مرة واحدة بالضبط",
  "apiError.UNKNOWN": "حدث خطأ ما — حاول مرة أخرى.",

  // M6-19 — طابور استثناءات النطاق الجغرافي، قوالب الإشعارات، سجل التدقيق،
  // أنصاف أقطار النطاق الجغرافي، ظهور المتجر في تطبيق الزبائن.
  // §14.2 bidi: أي مقطع لاتيني داخل جملة عربية (رقم طلب، مفتاح قالب، وقت)
  // ملفوف بزوج العزل U+2066…U+2069 حسب القاعدة المثبتة في رأس هذا الملف.
  "common.refresh": "تحديث",
  "common.loading": "جارٍ التحميل…",
  "common.view": "عرض",
  "common.save": "حفظ",

  "nav.geofenceOverrides": "استثناءات النطاق",
  "screen.geofenceOverrides.title": "استثناءات النطاق الجغرافي",

  "overrides.title": "استثناءات النطاق الجغرافي",
  "overrides.pendingCount": "{count} طلب بانتظار القرار.",
  "overrides.refreshedAt": "آخر تحديث \u2066{time}\u2069",
  "overrides.refreshFailed": "تعذّر تحديث الطابور — تُعرض آخر بيانات ناجحة.",
  "overrides.denied.title": "لا يمكنك مراجعة طلبات الاستثناء",
  "overrides.filter.status": "حالة الطلب",
  "overrides.filter.driver": "السائق",
  "overrides.status.PENDING": "بالانتظار",
  "overrides.status.APPROVED": "مقبول",
  "overrides.status.DENIED": "مرفوض",
  "overrides.status.CONSUMED": "مُستخدم",
  "overrides.status.EXPIRED": "منتهي",
  "overrides.transition.PICKUP": "الاستلام",
  "overrides.transition.DELIVERY": "التسليم",
  "overrides.age.minutes": "{minutes} دقيقة",
  "overrides.age.hours": "{hours} ساعة و{minutes} دقيقة",
  "overrides.table.label": "طلبات استثناء النطاق الجغرافي",
  "overrides.table.waiting": "مدة الانتظار",
  "overrides.table.order": "الطلب",
  "overrides.table.driver": "السائق",
  "overrides.table.transition": "الخطوة المتوقفة",
  "overrides.table.distance": "المسافة مقابل المسموح",
  "overrides.table.reason": "سبب السائق",
  "overrides.table.status": "الحالة",
  "overrides.distance.value": "{distance} م من أصل {allowed} م",
  "overrides.action.approve": "قبول",
  "overrides.action.deny": "رفض",
  "overrides.empty.title": "لا توجد طلبات استثناء",
  "overrides.empty.pending.title": "لا يوجد سائق بالانتظار",
  "overrides.export.distanceM": "المسافة (م)",
  "overrides.export.allowedM": "المسموح (م)",
  "overrides.export.requestedAt": "وقت الطلب",
  "overrides.export.decidedAt": "وقت القرار",
  "overrides.export.decisionNote": "ملاحظة القرار",
  "overrides.toast.approved": "تم قبول الاستثناء للطلب \u2066{order}\u2069.",
  "overrides.toast.denied": "تم رفض الاستثناء للطلب \u2066{order}\u2069.",
  "overrides.toast.alreadyDecided": "تم البت في هذا الطلب مسبقاً — جرى تحديث الطابور.",
  "overrides.decide.approve.title": "قبول استثناء النطاق الجغرافي؟",
  "overrides.decide.deny.title": "رفض استثناء النطاق الجغرافي؟",
  "overrides.decide.subject": "الطلب \u2066{order}\u2069 — متوقف عند {transition}.",
  "overrides.decide.reasonLabel": "سبب القرار",
  "overrides.decide.reasonRequired": "سبب القرار مطلوب.",

  "notifications.title": "قوالب الإشعارات",
  "notifications.count": "{count} نسخة قالب",
  "notifications.denied.title": "لا يمكنك عرض قوالب الإشعارات",
  "notifications.filter.channel": "القناة",
  "notifications.search.label": "بحث في القوالب",
  "notifications.search.placeholder": "مفتاح القالب أو نص الرسالة",
  "notifications.channel.PUSH": "إشعار فوري",
  "notifications.channel.SMS": "رسالة نصية",
  "notifications.channel.EMAIL": "بريد إلكتروني",
  "notifications.channel.IN_APP": "داخل التطبيق",
  "notifications.table.label": "نسخ قوالب الإشعارات",
  "notifications.table.key": "القالب",
  "notifications.table.channel": "القناة",
  "notifications.table.body": "الرسالة",
  "notifications.table.class": "التصنيف",
  "notifications.table.updated": "آخر تعديل",
  "notifications.badge.critical": "حرِج",
  "notifications.badge.inactive": "معطّل",
  "notifications.empty.title": "لا توجد قوالب مطابقة",
  "notifications.action.edit": "تعديل النص",
  "notifications.action.test": "إرسال تجريبي",
  "notifications.toast.saved": "تم تحديث القالب \u2066{key}\u2069.",
  "notifications.edit.title": "تعديل النص —",
  "notifications.edit.bodyRequired": "نص الرسالة مطلوب.",
  "notifications.edit.unknownPlaceholder": "رمز غير معروف: \u2066{tokens}\u2069",
  "notifications.field.titleAr": "العنوان (عربي)",
  "notifications.field.titleEn": "العنوان (إنجليزي)",
  "notifications.field.bodyAr": "الرسالة (عربي)",
  "notifications.field.bodyEn": "الرسالة (إنجليزي)",
  "notifications.field.critical": "تصنيف الإشعار",
  "notifications.field.deepLink": "الرابط العميق",
  "notifications.field.active": "القالب مفعّل",
  "notifications.critical.yes": "حرِج",
  "notifications.critical.no": "عادي",
  "notifications.test.title": "إرسال تجريبي —",
  "notifications.test.send": "إرسال تجريبي",
  "notifications.test.recipient": "المستلم",
  "notifications.test.recipientRequired": "المستلم مطلوب.",
  "notifications.test.queued": "أُدرج للإرسال — معرّف الإشعار:",

  "auditBrowse.title": "سجل التدقيق",
  "auditBrowse.count": "{count} سجل",
  "auditBrowse.list.label": "سجلات التدقيق",
  "auditBrowse.filter.subjectType": "نوع الكيان",
  "auditBrowse.filter.subjectId": "معرّف الكيان",
  "auditBrowse.filter.actionPrefix": "الإجراء يبدأ بـ",
  "auditBrowse.filter.actorType": "نوع المنفّذ",
  "auditBrowse.filter.actorUser": "معرّف المستخدم المنفّذ",
  "auditBrowse.filter.from": "من",
  "auditBrowse.filter.to": "إلى",
  "auditBrowse.actorType.USER": "مستخدم",
  "auditBrowse.actorType.SYSTEM": "النظام",
  "auditBrowse.actorType.API_CLIENT": "عميل واجهة برمجية",
  "auditBrowse.rangeInvalid": "تاريخ النهاية أسبق من تاريخ البداية — لم يُطبَّق فلتر التاريخ.",
  "auditBrowse.emptyFiltered.title": "لا توجد سجلات مطابقة",

  "geofence.title": "أنصاف أقطار النطاق الجغرافي",
  "geofence.backToZones": "العودة إلى المناطق",
  "geofence.denied.title": "لا يمكنك عرض أنصاف أقطار النطاق",
  "geofence.table.label": "تخصيصات أنصاف أقطار النطاق الجغرافي",
  "geofence.table.scope": "النطاق",
  "geofence.table.scopeType": "النوع",
  "geofence.table.pickup": "نصف قطر الاستلام",
  "geofence.table.delivery": "نصف قطر التسليم",
  "geofence.scope.ZONE": "منطقة",
  "geofence.scope.MERCHANT": "تاجر",
  "geofence.inherited": "يرث العام",
  "geofence.metres": "{value} م",
  "geofence.unit.metres": "م",
  "geofence.filter.scopeType": "نوع النطاق",
  "geofence.add.scopeType": "إضافة تخصيص لـ",
  "geofence.add.scope": "النطاق",
  "geofence.add.pick": "اختر…",
  "geofence.add.action": "ضبط الأنصاف",
  "geofence.empty.title": "لا توجد تخصيصات",
  "geofence.editor.title": "أنصاف أقطار النطاق — {scope}",
  "geofence.field.pickup": "نصف قطر الاستلام",
  "geofence.field.delivery": "نصف قطر التسليم",
  "geofence.field.rangeError": "أدخل رقماً صحيحاً بين 1 و100000 متر.",
  "geofence.field.buffer": "هامش دقة GPS",
  "geofence.field.toggles": "التحقق مفعّل",
  "geofence.toggles.value": "الاستلام: {pickup} · التسليم: {delivery}",
  "geofence.toggle.on": "مفعّل",
  "geofence.toggle.off": "معطّل",
  "geofence.action.remove": "إزالة التخصيص",
  "geofence.confirm.remove.title": "إزالة هذا التخصيص؟",
  "geofence.confirm.remove.description": "يعود {scope} فوراً إلى القيم العامة للنطاق الجغرافي.",
  "geofence.toast.saved": "تم حفظ أنصاف أقطار النطاق لـ {scope}.",
  "geofence.toast.removed": "أُزيل التخصيص — {scope} يرث الآن القيم العامة الافتراضية.",

  "orders.filter.source": "المصدر",
  "orders.filter.type": "النوع",

  "merchants.table.visibility": "تطبيق الزبائن",
  "merchants.filter.visibility": "تطبيق الزبائن",
  "merchants.visibility.label": "دليل تطبيق الزبائن",
  "merchants.visibility.visible": "ظاهر",
  "merchants.visibility.hidden": "مخفي",

  /* ---------------- M7-07 admin screens tail ---------------- */

  "userPicker.search.label": "ابحث عن مستخدم بالاسم أو رقم الهاتف",
  "userPicker.search.placeholder": "الاسم أو رقم الهاتف",
  "userPicker.role": "الدور",
  "userPicker.role.admin": "إدارة",
  "userPicker.role.merchant": "تاجر",
  "userPicker.role.driver": "سائق",
  "userPicker.role.customer": "زبون",
  "userPicker.results": "المستخدمون المطابقون",
  "userPicker.count": "{count} مستخدم",
  "userPicker.count.one": "{count} مستخدم",
  "userPicker.truncated": "عرض {shown} من {total} — ضيّق البحث لرؤية البقية",
  "userPicker.empty.title": "لا يوجد مستخدم مطابق",
  "userPicker.failed": "تعذّر تحميل دليل المستخدمين.",
  "userPicker.change": "تغيير",

  "unassigned.title": "طلبات بلا سائق",
  "unassigned.count": "{count} بالانتظار",
  "unassigned.table.label": "طلبات بانتظار سائق",
  "unassigned.table.waiting": "مدة الانتظار",
  "unassigned.table.order": "الطلب",
  "unassigned.table.vehicle": "المركبة المطلوبة",
  "unassigned.table.reason": "السبب",
  "unassigned.reason.NO_ELIGIBLE_ONLINE_DRIVERS": "لا يوجد سائق متصل ومؤهل",
  "unassigned.reason.NO_VEHICLE_TYPE_MATCH": "لا يوجد سائق بالمركبة المطلوبة",
  "unassigned.reason.MAX_WAVES_EXHAUSTED": "انتهت جولات العرض",
  "unassigned.reason.wave": "الجولة {wave}",
  "unassigned.reason.none": "ما يزال معروضاً",
  "unassigned.reason.noAccess": "يتطلب صلاحية سجل التدقيق",
  "unassigned.action.assign": "إسناد سائق",
  "unassigned.action.openOrder": "فتح الطلب",
  "unassigned.empty.title": "كل الطلبات لديها سائق",
  "unassigned.denied.title": "لا يمكنك عرض الطلبات",
  // M7-82 — the contracted retry (dispatch.yaml v1.3.0)
  "unassigned.reason.AUTOMATIC_DISPATCH_DISABLED": "الإسناد التلقائي متوقف",
  "unassigned.reason.NO_VEHICLE_TYPE_MATCH.named": "لا يوجد سائق {vehicle} متصل",
  "unassigned.reason.offered": "عُرض على {count} سائقين",
  "unassigned.reason.offered.one": "عُرض على {count} سائق",
  "unassigned.reason.justNow": "الآن",
  "unassigned.action.redispatch": "إعادة الإرسال",
  "unassigned.redispatch.confirmTitle": "إعادة إرسال الطلب؟",
  "unassigned.redispatch.offered": "عُرض الطلب {orderNo} على {count} سائقين",
  "unassigned.redispatch.offered.one": "عُرض الطلب {orderNo} على {count} سائق",
  "unassigned.redispatch.stuck": "أُعيد إرسال الطلب {orderNo} لكنه لم يُعرض على أي سائق",
  "unassigned.redispatch.inProgress": "الطلب {orderNo} معروض على السائقين حالياً — تم تحديث القائمة",
  "unassigned.redispatch.driverBound": "استلم أحد السائقين الطلب {orderNo} — تم تحديث القائمة",
  "unassigned.redispatch.statusChanged": "الطلب {orderNo} لم يعد بانتظار سائق ({status}) — تم تحديث القائمة",
  "unassigned.redispatch.noLongerWaiting": "الطلب {orderNo} لم يعد بانتظار سائق — تم تحديث القائمة",
  "unassigned.redispatch.blocked.driverBound": "يوجد سائق مرتبط بهذا الطلب",
  "unassigned.redispatch.blocked.status": "هذا الطلب لم يعد بانتظار سائق",

  "assignments.title": "الإسنادات",
  "assignments.count": "{count} إسناد",
  "assignments.count.one": "{count} إسناد",
  "assignments.table.label": "إسنادات السائقين",
  "assignments.table.order": "الطلب",
  "assignments.table.driver": "السائق",
  "assignments.table.via": "طريقة الإسناد",
  "assignments.table.status": "حالة الإسناد",
  "assignments.table.orderStatus": "حالة الطلب",
  "assignments.table.assignedAt": "تاريخ الإسناد",
  "assignments.table.released": "تاريخ الإنهاء",
  "assignments.filter.status": "حالة الإسناد",
  "assignments.filter.driver": "السائق",
  "assignments.filter.order": "معرّف الطلب",
  "assignments.filter.orderPlaceholder": "الصق معرّف الطلب (ULID)",
  "assignments.filter.orderInvalid": "لم تبدأ التصفية — معرّف الطلب من 26 خانة.",
  "assignments.status.ACTIVE": "نشط",
  "assignments.status.COMPLETED": "مكتمل",
  "assignments.status.RELEASED": "مُنهى",
  "assignments.via.OFFER_ACCEPT": "قبِل السائق العرض",
  "assignments.via.ADMIN_ASSIGN": "إسناد من الإدارة",
  "assignments.release.ADMIN_REASSIGN": "أُعيد الإسناد من الإدارة",
  "assignments.release.ORDER_CANCELLED": "أُلغي الطلب",
  "assignments.empty.title": "لا توجد إسنادات بعد",
  "assignments.emptyFiltered.title": "لا توجد إسنادات مطابقة",
  "assignments.action.openOrder": "فتح الطلب",
  "assignments.action.openDriver": "فتح السائق",
  "assignments.denied.title": "لا يمكنك عرض الإسنادات",

  "dispatchRules.title": "قواعد التوزيع",
  "dispatchRules.active": "مفعّلة",
  "dispatchRules.inactive": "غير مفعّلة",
  "dispatchRules.activate": "تفعيل",
  "dispatchRules.edit": "تعديل المعاملات",
  "dispatchRules.updated": "آخر تحديث {when}",
  "dispatchRules.noConfig": "هذه الاستراتيجية بلا معاملات.",
  "dispatchRules.confirm.activate.title": "جعل «{rule}» قاعدة التوزيع المفعّلة؟",
  "dispatchRules.confirm.activate.confirm": "تفعيل",
  "dispatchRules.dialog.title": "المعاملات — {rule}",
  "dispatchRules.field.radius_km": "نطاق البث (كم)",
  "dispatchRules.field.max_drivers": "أقصى عدد سائقين لكل موجة",
  "dispatchRules.field.wave_count": "عدد الموجات",
  "dispatchRules.field.wave_interval_seconds": "الثواني بين الموجات",
  "dispatchRules.field.exclusive": "طلب نشط واحد لكل سائق",
  "dispatchRules.field.max_waves": "أقصى عدد موجات للعرض",
  "dispatchRules.field.eligible_zone_scope": "مناطق السائقين المؤهلة",
  "dispatchRules.value.yes": "نعم",
  "dispatchRules.value.no": "لا",
  "dispatchRules.value.zoneScope.ALL": "كل المناطق",
  "dispatchRules.field.invalid": "أدخل رقماً صحيحاً 0 أو أكثر.",
  "dispatchRules.toast.saved": "حُفظت معاملات {rule}.",
  "dispatchRules.toast.activated": "{rule} هي قاعدة التوزيع المفعّلة الآن.",
  "dispatchRules.empty.title": "لا توجد قواعد توزيع مسجّلة",
  "dispatchRules.denied.title": "لا يمكنك إدارة قواعد التوزيع",

  "notifications.tabs.label": "عروض الإشعارات",
  "notifications.tabs.templates": "القوالب",
  "notifications.tabs.deliveries": "سجل الإرسال",
  "notifications.tabs.send": "إرسال رسالة",

  // ---- الإرسال المخصص (M7-37d ← ملحق M7-36a، قرارات المالك #6) ----
  "customSend.audience.legend": "الجمهور",
  "customSend.audience.group": "مجموعة",
  "customSend.audience.user": "شخص واحد",
  "customSend.field.group": "المجموعة",
  "customSend.group.ALL": "الجميع",
  "customSend.group.CUSTOMERS": "كل الزبائن",
  "customSend.group.MERCHANTS": "كل التجار",
  "customSend.group.DRIVERS": "كل السائقين",
  "customSend.field.recipient": "المستلم",
  "customSend.submit": "إرسال الرسالة",
  "customSend.confirm.title": "إرسال هذه الرسالة؟",
  "customSend.confirm.description": "تُرسل فعلياً إلى {audience} ولا يمكن استرجاعها.",
  "customSend.confirm.send": "إرسال",
  "customSend.sent.title": "الرسالة في قائمة الإرسال",
  "customSend.sent.details": "{count} مستلم/مستلمين.",
  "customSend.sent.groupDetails": "تُحلّ المجموعة وتُرسل في الخلفية.",

  "deliveries.count": "{count} محاولة",
  "deliveries.count.one": "{count} محاولة",
  "deliveries.table.label": "محاولات الإرسال",
  "deliveries.table.template": "القالب",
  "deliveries.table.recipient": "المستلم",
  "deliveries.table.channel": "القناة",
  "deliveries.table.status": "الحالة",
  "deliveries.table.attempt": "المحاولة",
  "deliveries.table.provider": "المزوّد",
  "deliveries.table.created": "وقت المحاولة",
  "deliveries.attempt.value": "{attempt} من {max}",
  "deliveries.status.QUEUED": "في الانتظار",
  "deliveries.status.SENDING": "قيد الإرسال",
  "deliveries.status.SENT": "أُرسلت",
  "deliveries.status.DELIVERED": "وصلت",
  "deliveries.status.FAILED": "فشلت",
  "deliveries.status.SKIPPED": "تم تخطيها",
  "deliveries.skip.PREFERENCE_DISABLED": "المستلم عطّل هذه القناة",
  "deliveries.skip.CHANNEL_UNCONFIGURED": "القناة غير مُهيّأة",
  "deliveries.skip.NO_PUSH_TOKEN": "لا يوجد رمز إشعارات مسجّل",
  "deliveries.skip.PROVIDER_UNSUPPORTED": "غير مدعومة لدى المزوّد",
  "deliveries.retryAt": "إعادة المحاولة {time}",
  "deliveries.filter.status": "الحالة",
  "deliveries.filter.channel": "القناة",
  "deliveries.filter.template": "القالب",
  "deliveries.filter.recipient": "المستلم",
  "deliveries.filter.from": "من",
  "deliveries.filter.to": "إلى",
  "deliveries.filter.recipientAll": "أي مستلم",
  "deliveries.empty.title": "لا توجد محاولات إرسال بعد",
  "deliveries.emptyFiltered.title": "لا توجد محاولات مطابقة",
  "deliveries.denied.title": "لا يمكنك عرض سجل الإرسال",

  // ---- تقارير التفاعل §7.2 (M7-38) ----
  "engagement.title": "تقارير التفاعل",
  "engagement.denied.title": "لا يمكنك عرض تقارير التفاعل",
  "engagement.groupBy.label": "التجميع حسب",
  "engagement.groupBy.merchant": "المتاجر",
  "engagement.groupBy.category": "التصنيفات",
  "engagement.groupBy.zone": "المناطق",
  "engagement.window.label": "المدة",
  "engagement.window.day": "اليوم",
  "engagement.window.week": "آخر 7 أيام",
  "engagement.window.month": "آخر 30 يوماً",
  "engagement.window.custom": "مخصصة",
  "engagement.rangeInvalid": "تاريخ النهاية قبل تاريخ البداية — اختر مدة صحيحة.",
  "engagement.event.filter": "الحدث",
  "engagement.event.all": "كل الأحداث",
  "engagement.event.STORE_PROFILE_VIEW": "زيارات المتجر",
  "engagement.event.CALL_TAP": "نقرات الاتصال",
  "engagement.event.WHATSAPP_TAP": "نقرات واتساب",
  "engagement.table.label": "التفاعل حسب المجموعة",
  "engagement.table.group.merchant": "المتجر",
  "engagement.table.group.category": "التصنيف",
  "engagement.table.group.zone": "المنطقة",
  "engagement.table.total": "الإجمالي",
  "engagement.table.share": "الحصة",
  "engagement.table.trend": "الاتجاه",
  "engagement.trend.new": "جديد",
  "engagement.trend.flat": "—",
  "engagement.trend.sr": "{pct} مقارنة بالمدة السابقة",
  "engagement.trend.upSr": "ارتفاع",
  "engagement.trend.downSr": "انخفاض",
  "engagement.empty.title": "لا يوجد تفاعل في هذه المدة",
  "engagement.empty.widen": "عرض آخر 30 يوماً",

  "orders.pod.title": "إثبات التسليم",
  "orders.pod.requirement": "الإثبات المطلوب",
  "orders.pod.mode.NONE": "بدون",
  "orders.pod.mode.PHOTO": "صورة",
  "orders.pod.mode.SIGNATURE": "توقيع",
  "orders.pod.mode.OTP": "رمز الزبون (OTP)",
  "orders.pod.none": "هذا الطلب لا يحتاج إثبات تسليم.",
  "orders.pod.pending": "لم يُقدَّم إثبات بعد.",
  "orders.pod.submittedBy": "قدّمه",
  "orders.pod.submittedAt": "وقت التقديم",
  "orders.pod.otpVerified": "تم التحقق من رمز الزبون",
  "orders.pod.otpVerifiedAt": "تم التحقق {time}",
  "orders.pod.otpNotVerified": "صدر الرمز ولم يُتحقق منه",
  "orders.pod.viewFile": "فتح صورة الإثبات",
  "orders.pod.failed": "تعذّر تحميل سجل الإثبات.",

  /* ---------------------------------------------------------------- */
  /* M7-23 — إدارة السائقين، الأدوار والصلاحيات، تبويب الإعدادات        */
  /* ---------------------------------------------------------------- */

  "nav.roles": "الأدوار والصلاحيات",

  "drivers.new": "سائق جديد",
  "drivers.create.title": "سائق جديد",
  "drivers.create.section.account": "حساب السائق",
  "drivers.create.name": "اسم السائق",
  "drivers.create.nameRequired": "اسم السائق مطلوب.",
  "drivers.create.phone": "هاتف السائق",
  "drivers.create.linkToggle": "ربط حساب موجود",
  "drivers.create.phoneTakenTitle": "رقم الهاتف مسجل مسبقاً",
  "drivers.create.phoneTakenAction": "ربط الحساب الموجود",
  "drivers.create.success.title": "تم إنشاء السائق",
  "drivers.create.success.open": "فتح ملف السائق",
  "drivers.create.section.profile": "الملف الشخصي",
  "drivers.create.section.vehicle": "المركبة",
  "drivers.create.submit": "إنشاء السائق",
  "drivers.form.phone": "هاتف الحساب",
  "drivers.form.nationalId": "رقم الهوية الوطنية",
  "drivers.form.birthDate": "تاريخ الميلاد",
  "drivers.form.notes": "ملاحظات داخلية",
  "drivers.form.vehicleType": "نوع المركبة",
  "drivers.form.plateNo": "رقم اللوحة",
  "drivers.form.make": "الصانع",
  "drivers.form.model": "الطراز",
  "drivers.form.modelYear": "سنة الصنع",
  "drivers.form.color": "اللون",
  "drivers.toast.created": "تم إنشاء ملف السائق",
  "drivers.toast.updated": "تم تحديث بيانات السائق",
  "drivers.edit.open": "تعديل السائق",
  "drivers.action.openWallet": "المحفظة",
  "drivers.edit.title": "تعديل السائق",
  "drivers.edit.status": "حالة الملف",
  "drivers.edit.blockReason": "الحظر",
  "drivers.edit.blockNone": "غير محظور",
  "drivers.edit.reservedReason": "سبب الحظر هذا محجوز لنظام المحافظ.",
  "drivers.tabs.documents": "الوثائق",
  "drivers.tabs.vehicles": "المركبة",
  "drivers.tabs.plan": "الخطة والمحفظة",
  "drivers.detail.openWallet": "فتح محفظة السائق",
  "drivers.docType.NATIONAL_ID": "الهوية الوطنية",
  "drivers.docType.RESIDENCE_CARD": "بطاقة الإقامة",
  "drivers.docType.DRIVING_LICENSE": "إجازة السوق",
  "drivers.docType.VEHICLE_REGISTRATION": "سنوية المركبة",
  "drivers.docType.INSURANCE": "التأمين",
  "drivers.docType.OTHER": "وثيقة أخرى",
  "drivers.review.PENDING": "بانتظار المراجعة",
  "drivers.review.APPROVED": "معتمدة",
  "drivers.review.REJECTED": "مرفوضة",
  "drivers.documents.table.label": "وثائق السائق",
  "drivers.documents.table.type": "الوثيقة",
  "drivers.documents.table.expires": "تنتهي",
  "drivers.documents.table.status": "المراجعة",
  "drivers.documents.table.reviewed": "روجعت",
  "drivers.documents.filter.status": "حالة المراجعة",
  "drivers.documents.view": "فتح الملف",
  "drivers.documents.noFile": "لا يوجد ملف",
  "drivers.documents.approve": "اعتماد",
  "drivers.documents.reject": "رفض",
  "drivers.documents.review.title": "مراجعة الوثيقة",
  "drivers.documents.review.decision": "القرار",
  "drivers.documents.review.reason": "سبب الرفض",
  "drivers.documents.review.reasonRequired": "سبب الرفض مطلوب.",
  "drivers.documents.toast.approved": "تم اعتماد الوثيقة",
  "drivers.documents.toast.rejected": "تم رفض الوثيقة",
  "drivers.documents.empty.title": "لا توجد وثائق بعد",
  "drivers.documents.denied.title": "لا يمكنك عرض وثائق السائقين",
  "drivers.vehicle.register": "تسجيل مركبة",
  "drivers.vehicle.replace": "استبدال المركبة",
  "drivers.vehicle.details": "التفاصيل",
  "drivers.vehicle.noDetails": "لا توجد تفاصيل مسجّلة",
  "drivers.vehicle.toast.saved": "تم حفظ المركبة",
  "drivers.vehicle.empty.title": "لا توجد مركبة مسجّلة",
  "drivers.plan.title": "خطة السائق",
  "drivers.plan.type": "الخطة",
  "drivers.plan.dailyFee": "الرسم اليومي",
  "drivers.plan.commissionPercent": "نسبة العمولة",
  "drivers.plan.effectiveFrom": "سارية من",
  "drivers.plan.none": "لا توجد خطة — لا يُحتسب على هذا السائق أي مبلغ.",
  "drivers.plan.change": "تغيير الخطة",
  "drivers.plan.set": "تعيين خطة",
  "drivers.plan.modalTitle": "خطة السائق",
  "drivers.plan.feeRequired": "أدخل الرسم اليومي كمبلغ، مثل ٥٠٠٠٫٠٠.",
  "drivers.plan.percentRequired": "أدخل العمولة كنسبة بخانتين عشريتين، مثل ١٥٫٠٠.",
  "drivers.plan.toast.saved": "تم تحديث خطة السائق",
  "drivers.plan.denied": "تغيير الخطة يتطلب صلاحية إدارة خطط السائقين.",
  "drivers.plan.loadFailed": "تعذّر تحميل خطة السائق.",

  "wallets.plan.manageOnDriver": "غيّر الخطة من صفحة السائق",

  "roles.title": "الأدوار والصلاحيات",
  "roles.tabs.label": "أقسام الصلاحيات",
  "roles.tabs.roles": "الأدوار",
  "roles.tabs.users": "أدوار المستخدمين",
  "roles.count": "{count} دور",
  "roles.count.one": "{count} دور",
  "roles.search.label": "البحث في الأدوار",
  "roles.search.placeholder": "ابحث بالمعرّف أو الاسم…",
  "roles.table.label": "الأدوار",
  "roles.table.role": "الدور",
  "roles.table.type": "النوع",
  "roles.table.permissions": "الصلاحيات",
  "roles.table.created": "أُنشئ",
  "roles.type.system": "نظامي",
  "roles.type.custom": "مخصّص",
  "roles.new": "دور جديد",
  "roles.create.title": "دور جديد",
  "roles.edit.title": "تعديل الدور",
  "roles.form.key": "معرّف الدور",
  "roles.form.keyInvalid": "استخدم حروفاً إنجليزية صغيرة وشرطة سفلية فقط (من حرفين إلى ٦٤).",
  "roles.form.nameAr": "الاسم (عربي)",
  "roles.form.nameEn": "الاسم (إنجليزي)",
  "roles.form.description": "الوصف",
  "roles.delete": "حذف الدور",
  "roles.delete.title": "حذف هذا الدور؟",
  "roles.delete.description": "لا يمكن التراجع عن هذا الإجراء.",
  "roles.toast.created": "تم إنشاء الدور",
  "roles.toast.updated": "تم حفظ الدور",
  "roles.toast.deleted": "تم حذف الدور",
  "roles.empty.title": "لا توجد أدوار مطابقة",
  "roles.denied.title": "لا يمكنك إدارة الأدوار",
  "roles.detail.tabs.label": "أقسام الدور",
  "roles.detail.tabs.details": "التفاصيل",
  "roles.detail.tabs.permissions": "الصلاحيات",
  "roles.permissions.selected": "{count} من {total} محدّدة",
  "roles.permissions.save": "حفظ الصلاحيات",
  "roles.permissions.search": "تصفية الصلاحيات",
  "roles.permissions.searchPlaceholder": "ابحث بالمعرّف أو الوصف…",
  "roles.permissions.selectDomain": "حدّد كل صلاحيات هذا المجال",
  "roles.permissions.clearDomain": "ألغِ كل صلاحيات هذا المجال",
  "roles.permissions.empty": "لا توجد صلاحية مطابقة للتصفية.",
  "roles.permissions.confirmTitle": "استبدال صلاحيات هذا الدور؟",
  "roles.permissions.confirmDescription": "تصبح {count} صلاحية هي مجموعة الدور الكاملة فوراً.",
  "roles.permissions.toast.saved": "تم حفظ الصلاحيات",
  "roles.permissions.denied": "تعديل الصلاحيات يتطلب صلاحية إدارة الأدوار.",

  "userRoles.title": "أدوار المستخدمين",
  "userRoles.pick": "المستخدم",
  "userRoles.current": "الأدوار المُسندة",
  "userRoles.none": "لا يحمل هذا المستخدم أي دور.",
  "userRoles.noUser.title": "اختر مستخدماً",
  "userRoles.add": "إضافة دور",
  "userRoles.remove": "إزالة",
  "userRoles.role": "الدور",
  "userRoles.scope": "النطاق",
  "userRoles.scopeType.GLOBAL": "المنصة كاملة",
  "userRoles.scopeType.MERCHANT": "متجر واحد",
  "userRoles.scopeType.BRANCH": "فرع واحد",
  "userRoles.scopeId": "معرّف النطاق",
  "userRoles.scopeIdRequired": "معرّف النطاق مطلوب للدور المُقيَّد.",
  "userRoles.duplicate": "هذا الدور بهذا النطاق مُسند مسبقاً.",
  "userRoles.save": "حفظ الإسنادات",
  "userRoles.table.label": "إسنادات الأدوار",
  "userRoles.toast.saved": "تم حفظ الإسنادات",
  "userRoles.denied": "إسناد الأدوار يتطلب صلاحية إسناد الأدوار للمستخدمين.",
  "userRoles.selfConfirm.title": "إزالة دور من حسابك أنت؟",
  "userRoles.selfConfirm.description": "قد تفقد صلاحية الوصول إلى لوحة التحكم.",

  "settings.tabs.label": "مجموعات الإعدادات",
  "settings.group.api": "الوصول البرمجي",
  "settings.group.auth": "تسجيل الدخول",
  "settings.group.authz": "الصلاحيات",
  "settings.group.customer_requests": "طلبات العملاء",
  "settings.group.dispatch": "التوزيع",
  "settings.group.driver_plans": "خطط السائقين",
  "settings.group.drivers": "السائقون",
  "settings.group.engagement": "التفاعل",
  "settings.group.flags": "مفاتيح الميزات",
  "settings.group.geo": "الجغرافيا والمسافة",
  "settings.group.geofence": "النطاق الجغرافي",
  "settings.group.marketing": "التسويق",
  "settings.group.customer_app": "تطبيق العميل",
  "settings.group.notifications": "الإشعارات",
  "settings.group.orders": "الطلبات ونافذة التعديل",
  "settings.group.otp": "رمز التحقق",
  "settings.group.platform": "المنصة",
  "settings.group.pricing": "تسعير التوصيل",
  "settings.group.promotions": "العروض",
  "settings.group.settlement": "التسوية",
  "settings.group.support": "الدعم",
  "settings.group.tracking": "التتبع",
  "settings.group.wallet": "المحافظ",
  "settings.groupCount": "{count} إعداد",
  "settings.groupCount.one": "{count} إعداد",
  "settings.emptyGroup": "لا يوجد إعداد في هذه المجموعة يطابق بحثك.",

  "audit.verb.generic": "مسجَّل",
  "audit.verb.assigned": "إسناد",
  "audit.verb.approved": "اعتماد",
  "audit.verb.rejected": "رفض",
  "customers.edit.open": "تعديل العميل",
  "customers.edit.title": "تعديل العميل",
  "customers.edit.saved": "تم تحديث العميل",
  "customers.form.name": "الاسم",

  "drivers.earnings.title": "الأرباح",
  "drivers.earnings.today": "اليوم",
  "drivers.earnings.week": "آخر ٧ أيام",
  "drivers.earnings.delivered": "المسلَّمة",
  "drivers.earnings.feesKept": "الأجور المحتفظ بها",
  "drivers.earnings.cofund": "تعويض الخصومات",
  "drivers.earnings.deductions": "الاستقطاعات",
  "drivers.earnings.net": "الصافي",

};

export const catalogs: Record<"ar" | "en", MessageCatalog> = { ar, en };
